import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormulaBuilder } from '@/components/FormulaBuilder';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  Database, Link2, GitBranch, Table2, Hash, Type, TrendingUp, BarChart3,
  Layers, Zap, ArrowRight, ArrowLeft, Plus, Trash2, Pencil, CheckCircle2,
  Save, Loader2, ChevronRight, Eye, Calendar, CalendarClock, LayoutGrid, Table,
  Search, AlertTriangle, X, Sparkles, Wand2, GripVertical, BookOpen, RefreshCw, Info, Download, Upload, FileText,
  Star, AlignVerticalSpaceBetween, Atom, ChevronDown, ChevronUp, KeyRound, GitCompare, ShieldAlert, ShieldCheck, Shield, CheckSquare, Code2, Copy, History,
} from 'lucide-react';
import { Pyramid } from 'lucide-react';
import { toast } from 'sonner';
import MeasureUnitPill from '@/components/MeasureUnitPill';
import QueryTableDialog from '@/components/semantic/QueryTableDialog';
import SaveAsModelDialog from '@/components/semantic-models/SaveAsModelDialog';
import SaveSplitButton from '@/components/SaveSplitButton';

const _TI_LABEL = { ytd: 'YTD', qtd: 'QTD', mtd: 'MTD', itd: 'ITD', yoy_pct: 'YoY%', yoy: 'YoY', prior_period: 'PriorPeriod', same_period_last_year: 'SPLY' };
// Human-readable formula for the measures list — covers formula, time-intelligence
// (which stores {op,of} not a formula string), and scoped measures.
function calcFormulaText(m) {
  if (m.formula) return m.formula;
  if (m.time_intel && m.time_intel.of) {
    const op = _TI_LABEL[m.time_intel.op] || String(m.time_intel.op || '').toUpperCase();
    return `${op}(${m.time_intel.of})`;
  }
  if (m.base_field) {
    const agg = String(m.aggregation || 'sum').toUpperCase();
    const sc = (m.scope_filters || []).map(f => `${f.field} ${f.operator || '='} ${Array.isArray(f.value) ? f.value.join(',') : f.value}`).join(' AND ');
    return sc ? `${agg}(${m.base_field}) WHERE ${sc}` : `${agg}(${m.base_field})`;
  }
  return '—';
}
import { useConfirmDialog } from '@/components/ConfirmDialog';
import { VersionHistoryButton } from '@/components/VersionHistory';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { SaveWithVersionDialog } from '@/components/SaveWithVersionDialog';
import { MetadataTableView } from '@/components/MetadataTableView';
import { LookmlSummaryDialog } from '@/components/semantic-models/LookmlSummaryDialog';
import { HelpIcon } from '@/components/HelpIcon';
import { useWorkspaceTitle, useWorkspaceDirty, useWorkspaceSaver, useWorkspace } from '@/contexts/WorkspaceContext';

import { TableauImportSummary } from '@/components/semantic-models/TableauImportSummary';
import { ImpactWarning } from '@/components/ImpactWarning';
import { CreateModelDialog } from '@/components/semantic-models/CreateModelDialog';
import { EditRelationshipDialog } from '@/components/semantic-models/EditRelationshipDialog';
import { EditCalcMeasureDialog } from '@/components/semantic-models/EditCalcMeasureDialog';
import { EntityPreviewSheet } from '@/components/semantic-models/EntityPreviewSheet';
import { AggregatePanel } from '@/components/semantic-models/AggregatePanel';
import { CalculatedMeasuresPanel } from '@/components/semantic-models/CalculatedMeasuresPanel';
import { AddRelationshipDialog } from '@/components/semantic-models/AddRelationshipDialog';
import { HierarchyEditorDialog } from '@/components/semantic-models/HierarchyEditorDialog';
import { AiWizardDialog } from '@/components/semantic-models/AiWizardDialog';
import { useAuth } from '@/contexts/AuthContext';
import { MetadataDiffDialog } from '@/components/semantic-models/MetadataDiffDialog';
import { DerivedMeasureDialog } from '@/components/semantic-models/DerivedMeasureDialog';
import { SemBrowseDialog } from '@/components/semantic-models/SemBrowseDialog';
import { AiFormulaDialog } from '@/components/semantic-models/AiFormulaDialog';
import { EditModelDialog } from '@/components/semantic-models/EditModelDialog';
import ProcessHelp from '@/components/ProcessHelp';
import { ImportModelButton, ModelCardActions } from '@/components/semantic-models/ModelPortability';
import { ModelPermissionsDialog } from '@/components/semantic-models/ModelPermissionsDialog';
import { BulkModelShareDialog } from '@/components/semantic-models/BulkModelShareDialog';
import { AddEntityDialog } from '@/components/semantic-models/AddEntityDialog';
import { AddMeasureDialog } from '@/components/semantic-models/AddMeasureDialog';
import { PyramidFormulaImportDialog } from '@/components/PyramidFormulaImportDialog';
import PyramidImportWizard from '@/components/semantic/PyramidImportWizard';
import { CalcFieldDialog } from '@/components/semantic-models/CalcFieldDialog';
import { CalcDimensionDialog } from '@/components/semantic-models/CalcDimensionDialog';
import { PiiConsistencyDialog } from '@/components/semantic-models/PiiConsistencyDialog';

const TYPE_ICONS = { integer: Hash, float: TrendingUp, string: Type, date: Calendar };

// Suggested synonyms for a date field (mirrors backend date_defaults seed) so
// modelers see sensible defaults they can edit. Cosmetic — the backend also
// infers these at read time, so detection works even if left unsaved.
const _DATE_SYNONYM_SEED = {
  book: ['booked', 'written'], written: ['written', 'booked'],
  effective: ['effective', 'inception', 'in force'], expiration: ['expiration', 'expiry'],
  load: ['loaded', 'ingested', 'etl'], accounting: ['accounting', 'posted', 'gl'],
  loss: ['date of loss', 'loss date'], transaction: ['transaction', 'txn'],
  report: ['reported', 'report date'], renewal: ['renewal', 'renewed'],
  cancel: ['cancellation', 'cancelled'], receive: ['received'], paid: ['paid', 'payment'],
};
function suggestDateSynonyms(attr) {
  const src = ((attr.name || '') + ' ' + (attr.display_name || '')).toLowerCase();
  const out = new Set();
  Object.entries(_DATE_SYNONYM_SEED).forEach(([k, vals]) => { if (src.includes(k)) vals.forEach(v => out.add(v)); });
  return Array.from(out).join(', ');
}

const ENTITY_COLORS = {
  fact: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400' },
  dimension: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400' },
};

/* ─── Entity Card on ER Canvas ─── */
function EntityCard({ entity, isSelected, onSelect, style }) {
  const colors = ENTITY_COLORS[entity.entity_type] || ENTITY_COLORS.fact;
  const measures = entity.attributes?.filter(a => a.semantic_type === 'measure') || [];
  const dimensions = entity.attributes?.filter(a => a.semantic_type === 'dimension') || [];
  return (
    <div className={`absolute glass-card rounded-2xl p-4 w-[260px] cursor-pointer transition-all duration-200 group ${isSelected ? 'ring-2 ring-primary border-primary/30 shadow-[0_0_20px_rgba(59,130,246,0.2)]' : 'hover:border-white/20 hover:shadow-lg'}`} style={style} onClick={() => onSelect(entity.entity_id)} data-testid={`entity-${entity.entity_id}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded-lg ${colors.bg} border ${colors.border}`}><Table2 className={`w-4 h-4 ${colors.text}`} strokeWidth={1.5} /></div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xs font-heading font-semibold text-foreground truncate flex items-center gap-1">{entity.name}{entity.table_type === 'sql_query' && <span className="text-[7px] px-1 py-0.5 rounded bg-sky-500/20 text-sky-300 border border-sky-500/30 font-mono flex-shrink-0" data-testid={`sql-badge-${entity.entity_id}`}>SQL</span>}</h3>
          <p className="text-[9px] text-muted-foreground truncate">{entity.table_name}</p>
        </div>
        <Badge variant="outline" className={`text-[8px] ${colors.border} ${colors.text}`}>{entity.entity_type}</Badge>
      </div>
      {dimensions.length > 0 && (
        <div className="mb-1.5">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5"><Layers className="w-2 h-2 inline mr-0.5" />Dims ({dimensions.length})</p>
          {dimensions.slice(0, 4).map((a, di) => <div key={`${a.name}-${di}`} className="text-[9px] text-muted-foreground/70 pl-2 truncate">{a.description || a.name}</div>)}
          {dimensions.length > 4 && <div className="text-[9px] text-muted-foreground/70 pl-2">+{dimensions.length - 4} more</div>}
        </div>
      )}
      {measures.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-0.5"><BarChart3 className="w-2 h-2 inline mr-0.5" />Measures ({measures.length})</p>
          {measures.slice(0, 4).map((a, mi) => (
            <div key={`${a.name}-${mi}`} className="flex items-center gap-1 text-[9px] text-foreground/70 pl-2 truncate">
              {a.description || a.name} {a.aggregation && <Badge variant="outline" className="text-[9px] h-3 px-0.5">{a.aggregation}</Badge>}{a.time_aggregation && a.time_aggregation !== 'sum' && <Badge variant="outline" className="text-[9px] h-3 px-0.5 border-amber-500/30 text-amber-400">{a.time_aggregation}</Badge>}
            </div>
          ))}
          {measures.length > 4 && <div className="text-[9px] text-muted-foreground/70 pl-2">+{measures.length - 4} more</div>}
        </div>
      )}
    </div>
  );
}

function RelLine({ rel, entities }) {
  const src = entities.find(e => e.entity_id === rel.source_entity_id);
  const tgt = entities.find(e => e.entity_id === rel.target_entity_id);
  if (!src || !tgt) return null;
  const sx = (src.position?.x || 0) + 260, sy = (src.position?.y || 0) + 40;
  const tx = tgt.position?.x || 0, ty = (tgt.position?.y || 0) + 40;
  const mx = (sx + tx) / 2;
  return (
    <g>
      <path d={`M ${sx} ${sy} C ${mx} ${sy}, ${mx} ${ty}, ${tx} ${ty}`} fill="none" stroke="rgba(59,130,246,0.25)" strokeWidth="2" strokeDasharray="6 3" />
      <circle cx={sx} cy={sy} r="3" fill="#3b82f6" opacity="0.4" />
      <circle cx={tx} cy={ty} r="3" fill="#3b82f6" opacity="0.4" />
      <rect x={mx - 30} y={(sy + ty) / 2 - 8} width="60" height="16" rx="4" fill="var(--card, hsl(240 10% 96%))" stroke="var(--border, hsl(240 6% 90%))" strokeWidth="1" />
      <text x={mx} y={(sy + ty) / 2 + 3} textAnchor="middle" fontSize="7" fill="#a1a1aa" fontFamily="JetBrains Mono">{rel.cardinality?.replace('_', ':')}</text>
    </g>
  );
}

/* Node geometry: card is ~260 wide, vertical connect center is +40 from top.
   Pick the left/right edge midpoint pair (one per node) that are closest to
   each other so the join goes from the nearest endpoint to the nearest endpoint. */
const NODE_W = 260;
const NODE_CY = 40;
function nearestEndpoints(s, t) {
  const sx0 = s.position ? s.position.x : 0;
  const sy = (s.position ? s.position.y : 0) + NODE_CY;
  const tx0 = t.position ? t.position.x : 0;
  const ty = (t.position ? t.position.y : 0) + NODE_CY;
  const sCands = [{ x: sx0, y: sy }, { x: sx0 + NODE_W, y: sy }];
  const tCands = [{ x: tx0, y: ty }, { x: tx0 + NODE_W, y: ty }];
  let best = null;
  for (const sc of sCands) {
    for (const tc of tCands) {
      const d = (sc.x - tc.x) * (sc.x - tc.x) + (sc.y - tc.y) * (sc.y - tc.y);
      if (!best || d < best.d) best = { d, sx: sc.x, sy: sc.y, tx: tc.x, ty: tc.y };
    }
  }
  return best;
}


/* ─── Main Page ─── */
// Memo boundary around the ER canvas. The Add-Measure/field dialogs keep their
// form state in this page, so every keystroke re-renders the page — this skips
// the (expensive) reconciliation of the entity/relationship canvas unless a
// canvas-relevant value actually changed. `children` is intentionally ignored
// in the comparator so text-field typing never re-reconciles the canvas.
function canvasPropsEqual(a, b) {
  return a.entities === b.entities && a.relationships === b.relationships &&
    a.canvasPan === b.canvasPan && a.canvasZoom === b.canvasZoom &&
    a.connectMode === b.connectMode && a.connectSource === b.connectSource &&
    a.connectSide === b.connectSide && a.connectHover === b.connectHover &&
    a.connectMouse === b.connectMouse && a.selectedEntity === b.selectedEntity &&
    a.draggingEntity === b.draggingEntity && a.maxX === b.maxX && a.maxY === b.maxY;
}
const CanvasMemo = React.memo(function CanvasMemo(props) { return props.children; }, canvasPropsEqual);

// Incremental windowing: caps how many rows of a very large list render at once,
// auto-loading more as the sentinel scrolls into view (keeps the DOM small & scroll snappy).
function VirtualMore({ remaining, onMore, kind }) {
  const attach = React.useCallback((node) => {
    if (!node) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onMore();
    }, { rootMargin: '250px' });
    io.observe(node);
  }, [onMore]);
  return (
    <button ref={attach} onClick={onMore}
      className="w-full my-1 py-1.5 rounded-md text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
      data-testid={`virtual-more-${kind}`}>
      Show more — {remaining} more {kind}
    </button>
  );
}

export default function SemanticModelsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCertify = ['admin', 'super_admin', 'tenant_admin', 'steward', 'data_steward', 'owner'].includes(user?.role);
  const certStatusOf = (m) => (m && m.certification && m.certification.status) || 'draft';
  const applyMeasureUpdate = (updated) => setCalcMeasures(prev => prev.map(x => x.measure_id === updated.measure_id ? { ...x, ...updated } : x));
  const certifyMeasureRow = async (m) => {
    const reason = window.prompt(`Reason for certifying "${m.name}" (recorded with this version):`, '');
    if (reason == null || !reason.trim()) return;
    try {
      const u = await api.certifyMeasure(m.measure_id, { reason: reason.trim() });
      applyMeasureUpdate(u);
      toast.success(`Certified "${m.name}"`);
    }
    catch (e) { toast.error(e.message || 'Failed to certify'); }
  };
  const [certifyingModel, setCertifyingModel] = useState(false);
  const handleCertifyModel = async () => {
    if (!activeModelId) return;
    const reason = window.prompt(`Reason for certifying model "${model?.name}" (recorded with this version):`, '');
    if (reason == null) return;
    setCertifyingModel(true);
    try {
      await api.certifyModel(activeModelId, { reason: reason.trim() });
      toast.success(`Model "${model?.name}" certified`);
      await loadModel(activeModelId);
    } catch (e) {
      toast.error(e.message || 'Could not certify — every measure on this model must be certified first');
    } finally { setCertifyingModel(false); }
  };
  const submitMeasureRow = async (m) => {
    try { const u = await api.submitMeasureCertification(m.measure_id, {}); applyMeasureUpdate(u); toast.success(`Submitted "${m.name}" for certification`); }
    catch (e) { toast.error(e.message || 'Failed to submit'); }
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [models, setModels] = useState([]);
  const { confirmFn, ConfirmDialogElement } = useConfirmDialog();
  const [activeModelId, setActiveModelId] = useState(null);
  const [sidebarTab, setSidebarTab] = useState(typeof window !== 'undefined' ? (localStorage.getItem('inzara.modelSidebarTab') || 'aggregates') : 'aggregates');
  // Full-width bottom detail dock — height / collapsed / density persisted per user.
  const [dockHeight, setDockHeight] = useState(() => { const v = parseInt(typeof window !== 'undefined' ? localStorage.getItem('inzara.modelDockHeight') : '', 10); return Number.isFinite(v) && v >= 140 && v <= 900 ? v : 320; });
  const [dockCollapsed, setDockCollapsed] = useState(typeof window !== 'undefined' ? localStorage.getItem('inzara.modelDockCollapsed') === '1' : false);
  const [dockDensity, setDockDensity] = useState(typeof window !== 'undefined' ? (localStorage.getItem('inzara.modelDockDensity') || 'comfortable') : 'comfortable');
  useEffect(() => { try { localStorage.setItem('inzara.modelDockHeight', String(dockHeight)); } catch (e) { /* quota */ } }, [dockHeight]);
  useEffect(() => { try { localStorage.setItem('inzara.modelDockCollapsed', dockCollapsed ? '1' : '0'); } catch (e) { /* quota */ } }, [dockCollapsed]);
  useEffect(() => { try { localStorage.setItem('inzara.modelDockDensity', dockDensity); } catch (e) { /* quota */ } }, [dockDensity]);
  const startDockResize = (e) => {
    e.preventDefault();
    const startY = e.clientY; const startH = dockHeight;
    const onMove = (ev) => { const next = Math.min(900, Math.max(140, startH + (startY - ev.clientY))); setDockHeight(next); };
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); document.body.style.userSelect = ''; };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
  };
  const dense = dockDensity === 'compact';
  // Resizable right-side table property panel — long column names otherwise
  // push the data-type / semantic-type buttons off the visible edge.
  const [propPanelWidth, setPropPanelWidth] = useState(() => { const v = parseInt(typeof window !== 'undefined' ? localStorage.getItem('inzara.modelPropPanelWidth') : '', 10); return Number.isFinite(v) && v >= 288 && v <= 720 ? v : 384; });
  useEffect(() => { try { localStorage.setItem('inzara.modelPropPanelWidth', String(propPanelWidth)); } catch (e) { /* quota */ } }, [propPanelWidth]);
  const startPropPanelResize = (e) => {
    e.preventDefault();
    const startX = e.clientX; const startW = propPanelWidth;
    const onMove = (ev) => { const next = Math.min(720, Math.max(288, startW + (startX - ev.clientX))); setPropPanelWidth(next); };
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
  };
  // Remember the most-recently-applied auto-layout style per model so the
  // toolbar's main action can re-apply with a single click. Backend writes
  // this onto the semantic_models doc whenever auto-layout runs.
  const [lastLayoutStyle, setLastLayoutStyle] = useState('star');
  const [layoutLoading, setLayoutLoading] = useState(false);
  const [model, setModel] = useState(null);
  const [pyramidFormulaOpen, setPyramidFormulaOpen] = useState(false);
  const [pyramidWizardOpen, setPyramidWizardOpen] = useState(false);
  const [entities, setEntities] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [joinSuggestions, setJoinSuggestions] = useState([]);
  const [joinWarnings, setJoinWarnings] = useState([]);
  const [dismissedJoins, setDismissedJoins] = useState(false);
  const [applyingJoin, setApplyingJoin] = useState(null);
  const [calcMeasures, setCalcMeasures] = useState([]);
  const [modelGlossary, setModelGlossary] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [fieldWindow, setFieldWindow] = useState(120);
  const [measureWindow, setMeasureWindow] = useState(120);
  const [fieldSearch, setFieldSearch] = useState('');
  const [panelTab, setPanelTab] = useState('fields');
  useEffect(function () { setFieldSearch(''); setFieldWindow(120); setMeasureWindow(120); }, [selectedEntity]);
  useEffect(function () { setFieldWindow(120); }, [fieldSearch]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [createModelOpen, setCreateModelOpen] = useState(false);
  const [newModelName, setNewModelName] = useState('');
  const [newModelDesc, setNewModelDesc] = useState('');
  // LookML import state
  const lookmlFileRef = React.useRef(null);
  const [lookmlImporting, setLookmlImporting] = useState(false);
  const [lookmlResult, setLookmlResult] = useState(null);
  const tableauFileRef = React.useRef(null);
  const [tableauImporting, setTableauImporting] = useState(false);
  const [tableauResult, setTableauResult] = useState(null);
  const ermodelFileRef = React.useRef(null);
  const [ermodelImporting, setErmodelImporting] = useState(false);
  const [ermodelResult, setErmodelResult] = useState(null);
  const [recomputing, setRecomputing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null); // { tables_synced, fields_added, fields_removed, details, skipped_tables }
  // Metadata CSV import — the dry-run result feeds the diff dialog;
  // pendingImportFile is what we actually POST when the user confirms.
  const [metadataDiff, setMetadataDiff] = useState(null);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [applyingMetadata, setApplyingMetadata] = useState(false);
  const [editModelOpen, setEditModelOpen] = useState(false);
  const [certHistory, setCertHistory] = useState({ open: false, loading: false, data: null, name: '' });
  const openCertHistory = async (m) => {
    setCertHistory({ open: true, loading: true, data: null, name: m.name });
    try {
      const data = await api.getMeasureCertHistory(m.measure_id);
      setCertHistory({ open: true, loading: false, data, name: m.name });
    } catch (e) {
      toast.error(e.message || 'Failed to load version history');
      setCertHistory({ open: false, loading: false, data: null, name: '' });
    }
  };
  const [savingAs, setSavingAs] = useState(false);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [addEntityOpen, setAddEntityOpen] = useState(false);
  const [queryTableOpen, setQueryTableOpen] = useState(false);
  const [editQueryEntity, setEditQueryEntity] = useState(null);
  const [newEntityName, setNewEntityName] = useState('');
  const [newEntityTable, setNewEntityTable] = useState('');
  const [newEntityType, setNewEntityType] = useState('fact');
  const [addRelOpen, setAddRelOpen] = useState(false);
  const [relSource, setRelSource] = useState('');
  const [relTarget, setRelTarget] = useState('');
  const [relMappings, setRelMappings] = useState([{ source: '', target: '' }]);
  const [relName, setRelName] = useState('');
  const [relCardinality, setRelCardinality] = useState('many_to_one');
  const [addMeasureOpen, setAddMeasureOpen] = useState(false);
  const [importMeasuresOpen, setImportMeasuresOpen] = useState(false);
  const [importMeasuresPlan, setImportMeasuresPlan] = useState(null);
  const [importMeasuresFile, setImportMeasuresFile] = useState(null);
  const [importMeasuresBusy, setImportMeasuresBusy] = useState(false);
  const [importUndoAvail, setImportUndoAvail] = useState(false);
  const importMeasuresInputRef = React.useRef(null);
  const analyzeMeasuresImport = async (file) => {
    if (!activeModelId || !file) return;
    setImportMeasuresBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/semantic/models/${activeModelId}/measures/import?dry_run=true`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) { const t = await res.json().catch(() => ({})); throw new Error(t.detail || 'Could not read the file'); }
      const data = await res.json();
      setImportMeasuresFile(file);
      setImportMeasuresPlan(data);
      setImportMeasuresOpen(true);
    } catch (e) { toast.error(e.message || 'Import failed'); }
    finally { setImportMeasuresBusy(false); }
  };
  const undoLastMeasuresImport = async () => {
    if (!activeModelId) return;
    try {
      const r = await api.undoMeasuresImport(activeModelId);
      toast.success(`Import undone — ${r.restored || 0} restored, ${r.deleted || 0} removed`);
      setImportUndoAvail(false);
      await loadModel(activeModelId);
    } catch (e) { toast.error(e.message || 'Undo failed'); }
  };
  const applyMeasuresImport = async () => {
    if (!activeModelId || !importMeasuresFile) return;
    setImportMeasuresBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', importMeasuresFile);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/semantic/models/${activeModelId}/measures/import?dry_run=false`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) { const t = await res.json().catch(() => ({})); throw new Error(t.detail || 'Import failed'); }
      const data = await res.json();
      const a = data.applied || {};
      setImportMeasuresOpen(false);
      setImportMeasuresPlan(null);
      setImportMeasuresFile(null);
      await loadModel(activeModelId);
      if (data.undo_available) {
        setImportUndoAvail(true);
        toast.success(`Imported: ${a.update || 0} updated, ${a.create || 0} created`, { action: { label: 'Undo', onClick: undoLastMeasuresImport } });
      } else {
        toast.success(`Imported: ${a.update || 0} updated, ${a.create || 0} created`);
      }
    } catch (e) { toast.error(e.message || 'Import failed'); }
    finally { setImportMeasuresBusy(false); }
  };
  React.useEffect(() => {
    if (!activeModelId) { setImportUndoAvail(false); return; }
    api.getMeasuresImportUndo(activeModelId).then(r => setImportUndoAvail(!!r.available)).catch(() => setImportUndoAvail(false));
  }, [activeModelId]);
  const [measureName, setMeasureName] = useState('');
  const [measureFormula, setMeasureFormula] = useState('');
  const [scopeMode, setScopeMode] = useState(false);
  const [scopeBaseField, setScopeBaseField] = useState('');
  const [scopeAgg, setScopeAgg] = useState('sum');
  const [scopeFilters, setScopeFilters] = useState([]);
  const [scopeIgnore, setScopeIgnore] = useState('');
  const [scopePct, setScopePct] = useState(false);
  const [tiMode, setTiMode] = useState(false);
  const [tiOp, setTiOp] = useState('ytd');
  const [tiOf, setTiOf] = useState('');
  const [tiN, setTiN] = useState(3); // window size for ROLLING_* time-intel ops
  const [measureEntity, setMeasureEntity] = useState('');
  const [measureDesc, setMeasureDesc] = useState('');
  const [measureFolder, setMeasureFolder] = useState('');
  const [measureCursorPos, setMeasureCursorPos] = useState(0);
  const [saving, setSaving] = useState(false);
  const [modelViewMode, setModelViewMode] = useState('cards');
  const [permModel, setPermModel] = useState(null);
  const [selectedModelIds, setSelectedModelIds] = useState(new Set());
  const [bulkShareOpen, setBulkShareOpen] = useState(false);
  const toggleModelSelect = (id) => setSelectedModelIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const [dataSources, setDataSources] = useState([]);
  const [newModelSourceId, setNewModelSourceId] = useState('');
  const [newModelSourceName, setNewModelSourceName] = useState('');
  const [createStep, setCreateStep] = useState(1);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteTargetName, setDeleteTargetName] = useState('');
  const [sourceTables, setSourceTables] = useState([]);
  const [sourceTablesLoading, setSourceTablesLoading] = useState(false);
  // Detail view hooks (must be before any conditional returns)
  const [draggingEntity, setDraggingEntity] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const draggingRef = React.useRef(null);
  const metadataFileRef = React.useRef(null);
  const [canvasZoom, setCanvasZoom] = useState(1);
  const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 });
  const [detectingJoins, setDetectingJoins] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [editingAttr, setEditingAttr] = useState(null);
  const [calcFieldOpen, setCalcFieldOpen] = useState(false);
  const [calcDimOpen, setCalcDimOpen] = useState(false);
  const [previewEntity, setPreviewEntity] = useState(null);
  const [calcFieldName, setCalcFieldName] = useState('');
  const [calcFieldFormula, setCalcFieldFormula] = useState('');
  const [calcFieldDesc, setCalcFieldDesc] = useState('');
  const [calcCursorPos, setCalcCursorPos] = useState(0);
  const [derivedMeasureOpen, setDerivedMeasureOpen] = useState(false);
  const [derivedSourceField, setDerivedSourceField] = useState('');
  const [derivedName, setDerivedName] = useState('');
  const [derivedDesc, setDerivedDesc] = useState('');
  const [derivedAgg, setDerivedAgg] = useState('avg');
  const [derivedFormat, setDerivedFormat] = useState('');
  const [hierarchyEditorOpen, setHierarchyEditorOpen] = useState(false);
  const [editHierarchyIdx, setEditHierarchyIdx] = useState(null);
  const [hierarchyName, setHierarchyName] = useState('');
  const [hierarchyLevels, setHierarchyLevels] = useState([]);
  const [editCalcMeasure, setEditCalcMeasure] = useState(null);
  const [glossaryAddOpen, setGlossaryAddOpen] = useState(false);
  const [newGlossaryTerm, setNewGlossaryTerm] = useState('');
  const [newGlossaryDef, setNewGlossaryDef] = useState('');
  const [aiGlossaryLoading, setAiGlossaryLoading] = useState(false);
  const [editCalcName, setEditCalcName] = useState('');
  const [editCalcFormula, setEditCalcFormula] = useState('');
  const [editCalcDesc, setEditCalcDesc] = useState('');
  const [editCalcEntity, setEditCalcEntity] = useState('');
  const [editCalcCursorPos, setEditCalcCursorPos] = useState(0);
  const [editRelOpen, setEditRelOpen] = useState(false);
  // Right-click context menu for entity (table) cards on the canvas.
  const [entityContextMenu, setEntityContextMenu] = useState(null);
  const [editRel, setEditRel] = useState(null);
  const [relContextMenu, setRelContextMenu] = useState(null);
  const [aiFormulaDialogOpen, setAiFormulaDialogOpen] = useState(false);
  const [activeFnCat, setActiveFnCat] = useState('Aggregation');
  const [semBrowseOpen, setSemBrowseOpen] = useState(false);
  const [semBrowseField, setSemBrowseField] = useState(null);
  const [semBrowseData, setSemBrowseData] = useState([]);
  const [semBrowseLoading, setSemBrowseLoading] = useState(false); // {x, y, rel}
  const [cardinalities, setCardinalities] = useState({});
  const [cardTotalRows, setCardTotalRows] = useState(0);
  const [cardLoading, setCardLoading] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  // Label this model's workspace tab with the model name. Keyed on the URL
  // `?model=` param (what the tab resolver uses) so the Models LIST tab keeps
  // its "Models" title and only the open-model tab gets the model name.
  useWorkspaceTitle(searchParams.get('model') && model ? model.name : 'Models');

  // Close the open-model Workbench session (not just navigate away). Clean
  // models close immediately; dirty ones trigger the dock's save/discard prompt.
  const ws = useWorkspace();
  const closeModelSession = React.useCallback(() => {
    const mid = searchParams.get('model');
    if (!mid) { setSearchParams({}, { replace: true }); return; }
    const closedNow = ws?.requestClose ? ws.requestClose(`model:${mid}`) : true;
    if (closedNow) {
      setActiveModelId(null); setModel(null); setSmHasUnsavedChanges(false);
      setSearchParams({}, { replace: true });
    }
  }, [ws, searchParams, setSearchParams]);

  // Load distinct-value cardinality for the selected entity's fields (one round-trip, cached server-side).
  const loadCardinality = React.useCallback((force) => {
    if (!activeModelId || !selectedEntity) return;
    setCardLoading(true);
    if (force) { setCardinalities({}); setCardTotalRows(0); }
    api.getEntityCardinality(activeModelId, selectedEntity, force)
      .then(res => { setCardinalities(res?.cardinality || {}); setCardTotalRows(res?.total_rows || 0); })
      .catch(() => { setCardinalities({}); setCardTotalRows(0); })
      .finally(() => setCardLoading(false));
  }, [activeModelId, selectedEntity]);

  useEffect(() => {
    if (!activeModelId || !selectedEntity) { setCardinalities({}); setCardTotalRows(0); return; }
    let cancelled = false;
    setCardLoading(true);
    setCardinalities({});
    setCardTotalRows(0);
    api.getEntityCardinality(activeModelId, selectedEntity)
      .then(res => { if (!cancelled) { setCardinalities(res?.cardinality || {}); setCardTotalRows(res?.total_rows || 0); } })
      .catch(() => { if (!cancelled) { setCardinalities({}); setCardTotalRows(0); } })
      .finally(() => { if (!cancelled) setCardLoading(false); });
    return () => { cancelled = true; };
  }, [activeModelId, selectedEntity]);

  const [smHasUnsavedChanges, setSmHasUnsavedChanges] = useState(false);

  const smIsDirty = smHasUnsavedChanges;
  const { confirmNavigation: smConfirmNav, UnsavedDialog: SmUnsavedDialog } = useUnsavedChanges(smIsDirty, 'You have unsaved model changes. Discard and leave?');
  const [connectMode, setConnectMode] = useState(false);
  const [connectSource, setConnectSource] = useState(null);
  // Drag-to-connect: live cursor in canvas-space + the side of the source
  // card we anchored from (left|right), plus the entity the cursor is
  // currently hovering over (highlighted in green during drag).
  const [connectSide, setConnectSide] = useState('right');
  const [connectMouse, setConnectMouse] = useState(null);
  const [connectHover, setConnectHover] = useState(null);
  const [warningsOpen, setWarningsOpen] = useState(false);
  const [detailViewMode, setDetailViewMode] = useState('canvas'); // 'canvas' | 'table'
  const [autoDiscovering, setAutoDiscovering] = useState(false);
  const [detectingPii, setDetectingPii] = useState(false);
  const [piiFixOpen, setPiiFixOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSel, setBulkSel] = useState({});
  const [bulkCat, setBulkCat] = useState('none');

  const applyBulk = async (patch) => {
    // Group selected fields (keyed 'entityId::name') by entity and update each entity once.
    const byEntity = {};
    Object.keys(bulkSel).filter(k => bulkSel[k]).forEach(k => {
      const [eid, name] = k.split('::');
      (byEntity[eid] = byEntity[eid] || []).push(name);
    });
    const ids = Object.keys(byEntity);
    if (ids.length === 0) { toast.error('Select at least one field'); return; }
    try {
      for (const eid of ids) {
        const en = entities.find(e => e.entity_id === eid);
        if (!en) continue;
        const names = new Set(byEntity[eid]);
        const na = (en.attributes || []).map(a => names.has(a.name) ? { ...a, ...patch } : a);
        const u = await api.updateSemanticEntity(eid, { attributes: na });
        setEntities(prev => prev.map(e => e.entity_id === eid ? u : e));
      }
      const n = Object.values(bulkSel).filter(Boolean).length;
      toast.success(`Updated ${n} field(s)`);
      setBulkSel({});
    } catch (err) { toast.error(err.message); }
  };
  const [aiClassifying, setAiClassifying] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardTables, setWizardTables] = useState([]);
  const [wizardSelectedTables, setWizardSelectedTables] = useState([]);
  const [wizardTableSearch, setWizardTableSearch] = useState('');
  const [wizardTablesLoading, setWizardTablesLoading] = useState(false);
  const [wizardAiSuggestions, setWizardAiSuggestions] = useState(null);
  const [wizardRelSuggestions, setWizardRelSuggestions] = useState(null);

  // Load model list + data sources
  useEffect(() => {
    Promise.all([api.getSemanticModels(), api.getDataSources()])
      .then(([m, s]) => { setModels(m); setDataSources(s); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  }, []);

  // Deep-link: `?model=<id>` auto-opens that model in the editor (so returning
  // from the Aggregate governance dashboard lands back on the model, not the list).
  useEffect(() => {
    const wanted = searchParams.get('model');
    if (wanted && models.length && activeModelId !== wanted) {
      loadModel(wanted);
    }
  }, [models, searchParams]);

  // Deep-link: `?measure=<id|name>` (e.g. the Certification Queue "Open" button)
  // opens that measure's editor and switches the sidebar to the Measures tab so
  // the steward lands ON the calculation, not just the model.
  useEffect(() => {
    const wantMeasure = searchParams.get('measure');
    if (!wantMeasure || !activeModelId || !calcMeasures.length) return;
    const m = calcMeasures.find(x => x.measure_id === wantMeasure || x.name === wantMeasure);
    if (!m) return;
    setSidebarTab('measures');
    setEditCalcMeasure(m);
    setEditCalcName(m.name);
    setEditCalcFormula(m.formula || '');
    setEditCalcDesc(m.description || '');
    setEditCalcEntity(m.source_entity_id || '');
    // Consume the param so re-renders / manual tab changes don't reopen it.
    const next = new URLSearchParams(searchParams);
    next.delete('measure');
    setSearchParams(next, { replace: true });
  }, [calcMeasures, activeModelId, searchParams]);

  // Load active model detail
  const loadModel = async (modelId) => {
    try {
      const full = await api.getSemanticModelFull(modelId);
      setModel(full);
      // Drop accidental duplicate entities (same physical table AND same entity
      // name) so the metadata list, search and canvas don't show a table twice.
      // Role-playing dimensions reuse a table under DIFFERENT entity names, so
      // keying on table_name + name preserves them while collapsing true dupes.
      const _rawEnts = full.entities || [];
      const _seenEnt = new Set();
      const _dedupEnts = [];
      for (const _e of _rawEnts) {
        const _k = (_e.table_name || '') + '::' + (_e.name || '');
        if (_seenEnt.has(_k)) continue;
        _seenEnt.add(_k);
        _dedupEnts.push(_e);
      }
      setEntities(_dedupEnts);
      setRelationships(full.relationships || []);
      setCalcMeasures(full.calculated_measures || []);
      setActiveModelId(modelId);
      setDismissedJoins(false);
      setJoinSuggestions([]);
      refreshJoinHealth(modelId);
      setLastLayoutStyle(full.last_layout_style || 'star');
      setSelectedEntity(null);
      // Reflect the open model in the URL so it's deep-linkable and survives a
      // round-trip to the governance dashboard (replace: no extra history entry).
      if (searchParams.get('model') !== modelId) {
        // Preserve other params (e.g. ?measure= deep-link) — don't clobber them.
        const next = new URLSearchParams(searchParams);
        next.set('model', modelId);
        setSearchParams(next, { replace: true });
      }
      // Load model-specific glossary terms
      api.getGlossary(null, null, modelId).then(terms => setModelGlossary(terms || [])).catch(() => setModelGlossary([]));
      // Load tables from the model's data source
      if (full.source_id) {
        setSourceTablesLoading(true);
        try {
          const schema = await api.introspectDataSource(full.source_id);
          setSourceTables(schema.tables || []);
        } catch { setSourceTables([]); }
        finally { setSourceTablesLoading(false); }
      } else {
        setSourceTables([]);
      }
    } catch (err) { toast.error(err.message); }
  };

  // Deterministic join suggestions based on shared key columns across tables.
  const refreshJoinSuggestions = async (mid) => {
    try {
      const r = await api.suggestModelJoins(mid);
      setJoinSuggestions(r.suggestions || []);
    } catch { setJoinSuggestions([]); }
    await refreshJoinHealth(mid);
  };
  // Join health is about EXISTING relationships (weak-overlap warnings) — this
  // still runs on model open; join *recommendations* are opt-in via the button.
  const refreshJoinHealth = async (mid) => {
    try {
      const h = await api.joinHealth(mid);
      setJoinWarnings(h.warnings || []);
    } catch { setJoinWarnings([]); }
  };
  // Opt-in: user clicks "Auto-Detect Joins" to fetch recommendations.
  const handleDetectJoins = async () => {
    if (!activeModelId) return;
    setDetectingJoins(true);
    try {
      const r = await api.suggestModelJoins(activeModelId);
      const sug = r.suggestions || [];
      setJoinSuggestions(sug);
      setDismissedJoins(false);
      await refreshJoinHealth(activeModelId);
      toast.success(sug.length ? `Found ${sug.length} suggested join${sug.length === 1 ? '' : 's'}` : 'No new join suggestions found');
    } catch (e) { toast.error(e.message || 'Join detection failed'); }
    finally { setDetectingJoins(false); }
  };
  // Center the diagram on a table matched by name (search box).
  const centerOnEntity = (entity) => {
    if (!entity) return;
    const el = document.querySelector('[data-canvas]');
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = (entity.position?.x || 0) + 130;
    const cy = (entity.position?.y || 0) + 40;
    setCanvasPan({ x: rect.width / 2 - cx * canvasZoom, y: rect.height / 2 - cy * canvasZoom });
    setSelectedEntity(entity.entity_id);
  };
  const _fieldLabels = (a) => [a.name, a.display_name, a.business_name].filter(Boolean).map(s => s.toLowerCase());
  // Find the entity + attribute a field-name term matches (exact wins over partial).
  const findFieldMatch = (term) => {
    let partial = null;
    for (const e of entities) {
      for (const a of (e.attributes || [])) {
        const labels = _fieldLabels(a);
        if (labels.some(l => l === term)) return { entity: e, attr: a };
        if (!partial && labels.some(l => l.includes(term))) partial = { entity: e, attr: a };
      }
    }
    return partial;
  };
  const handleTableSearch = (q) => {
    const term = (q || '').trim().toLowerCase();
    if (!term) return;
    // 1) Table (entity) name match first.
    const match = entities.find(e => (e.name || '').toLowerCase() === term)
      || entities.find(e => (e.name || '').toLowerCase().includes(term));
    if (match) { centerOnEntity(match); return; }
    // 2) Fall back to a field (attribute) match — center on its table.
    const fm = findFieldMatch(term);
    if (fm) {
      centerOnEntity(fm.entity);
      const label = fm.attr.business_name || fm.attr.display_name || fm.attr.name;
      toast.success(`Field "${label}" is in ${fm.entity.name}`);
      return;
    }
    toast.error(`No table or field matching "${q}"`);
  };
  const applyJoinSuggestion = async (sug) => {
    setApplyingJoin(sug.source_entity_id + sug.target_entity_id);
    try {
      await api.createSemanticRelationship(activeModelId, {
        name: `${sug.source_entity_name} to ${sug.target_entity_name}`,
        source_entity_id: sug.source_entity_id,
        target_entity_id: sug.target_entity_id,
        source_field: sug.source_attr,
        target_field: sug.target_attr,
        cardinality: sug.cardinality || 'many_to_one',
        join_type: 'inner',
        join_conditions: [{ source_attr: sug.source_attr, target_attr: sug.target_attr }],
      });
      const full = await api.getSemanticModelFull(activeModelId);
      setRelationships(full.relationships || []);
      toast.success(`Joined ${sug.source_entity_name} ↔ ${sug.target_entity_name} on ${sug.join_column}`);
      await refreshJoinSuggestions(activeModelId);
    } catch (e) { toast.error(e.message); }
    setApplyingJoin(null);
  };

  const handleCreateModel = async () => {
    setSaving(true);
    try {
      const m = await api.createSemanticModel({ name: newModelName, description: newModelDesc, source_id: newModelSourceId, source_name: newModelSourceName });
      setModels(prev => [...prev, m]);
      setCreateModelOpen(false);
      setNewModelName(''); setNewModelDesc(''); setNewModelSourceId(''); setNewModelSourceName(''); setCreateStep(1);
      // Open wizard immediately with loading state, load model in background
      startWizard(m);
      await loadModel(m.model_id);
    } catch (err) {
      const msg = err.message || 'Could not create model';
      if (/plan allows|upgrade to add|limit/i.test(msg)) {
        toast.error(msg, {
          action: { label: 'View plans', onClick: () => navigate('/billing') },
        });
      } else {
        toast.error(msg);
      }
    }
    finally { setSaving(false); }
  };

  const handleUpdateModel = async (versionDesc) => {
    setSaving(true);
    try {
      const updated = await api.updateSemanticModel(activeModelId, { name: model.name, description: model.description, status: model.status, ai_persona: model.ai_persona || '', fiscal_year_start_month: model.fiscal_year_start_month || 1, week_start_day: model.week_start_day ?? 0, answerability_strictness: model.answerability_strictness || 'balanced', data_freshness: model.data_freshness ?? null });
      setModel(updated);
      setModels(prev => prev.map(m => m.model_id === activeModelId ? { ...m, ...updated } : m));
      setEditModelOpen(false);
      await api.createVersion('semantic_model', activeModelId, versionDesc || 'Saved');
      setSaveDialogOpen(false);
      setSmHasUnsavedChanges(false);
      toast.success('Model saved & versioned');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  // Bridge the model editor's unsaved state into the Workbench so the bottom
  // dock shows a dirty dot and its close prompt offers "Save & close".
  useWorkspaceDirty(smIsDirty);
  useWorkspaceSaver(() => handleUpdateModel('Saved'));


  const confirmDeleteModel = (modelId, modelName) => {
    setDeleteTargetId(modelId);
    setDeleteTargetName(modelName || 'this model');
    setDeleteConfirmOpen(true);
  };

  const handleDeleteModel = async () => {
    const modelId = deleteTargetId;
    setDeleteConfirmOpen(false);
    setDeleteTargetId(null);
    try {
      await api.deleteSemanticModel(modelId);
      setModels(prev => prev.filter(m => m.model_id !== modelId));
      if (activeModelId === modelId) { setActiveModelId(null); setModel(null); setEntities([]); setRelationships([]); setCalcMeasures([]); }
      toast.success('Model deleted');
    } catch (err) { toast.error(err.message); }
  };

  const handleAddEntity = async (table) => {
    setSaving(true);
    try {
      // Columns may be missing for warehouse sources whose introspect only
      // returns table names — fetch them on demand so fields populate immediately.
      let cols = table.columns || [];
      if (cols.length === 0 && model && model.source_id) {
        try {
          const res = await api.describeTable(model.source_id, { table_name: table.table_name });
          cols = res.columns || [];
        } catch { /* fall back to empty — user can Load Columns manually */ }
      }
      // Build attributes from table columns
      const attrs = cols.map(col => ({
        name: col.name,
        data_type: col.data_type || 'string',
        semantic_type: col.semantic_type || _inferSemanticType(col.name, col.data_type),
        description: '',
      }));
      const ent = await api.createSemanticEntity(activeModelId, {
        name: table.table_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        table_name: table.table_name,
        entity_type: 'fact',
        attributes: attrs,
        position: { x: 50 + entities.length * 320, y: 100 },
      });
      setEntities(prev => [...prev, ent]);
      setAddEntityOpen(false);
      toast.success(`Entity "${table.table_name}" added with ${attrs.length} fields`);
      setDismissedJoins(false);
      refreshJoinSuggestions(activeModelId);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  // Simple heuristic
  const _inferSemanticType = (name, dataType) => {
    const n = (name || '').toLowerCase();
    const measureKw = ['amount', 'total', 'sum', 'count', 'rate', 'ratio', 'premium', 'loss', 'cost', 'revenue', 'price', 'value', 'severity'];
    if ((dataType === 'float' || dataType === 'integer') && measureKw.some(k => n.includes(k))) return 'measure';
    if (dataType === 'string' || n.includes('name') || n.includes('type') || n.includes('code') || n.includes('state') || n.includes('category')) return 'dimension';
    if (dataType === 'float' || dataType === 'integer') return 'measure';
    return 'dimension';
  };

  // #1 Auto-discover all tables as entities
  const handleConnectSource = async (sourceId) => {
    const ds = dataSources.find(d => d.source_id === sourceId);
    const mid = model?.model_id || activeModelId;
    if (!ds || !mid) return;
    try {
      await api.updateSemanticModel(mid, { source_id: ds.source_id, source_name: ds.name });
      toast.success(`Connected to ${ds.name}`);
      await loadModel(mid);
      try { await api.introspectDataSource(ds.source_id); } catch (e) { /* introspection warms lazily */ }
    } catch (err) { toast.error(err.message || 'Could not connect data source'); }
  };

  const handleAutoDetectPii = async () => {
    const mid = model?.model_id || activeModelId;
    if (!mid) return;
    setDetectingPii(true);
    try {
      const res = await api.autoDetectPii(mid);
      toast.success(res?.message || `Flagged ${res?.pii_count || 0} PII field(s)`);
      await loadModel(mid);
    } catch (err) {
      toast.error(err.message || 'PII detection failed');
    } finally {
      setDetectingPii(false);
    }
  };

  // One-click PII / classification consistency sweep across sibling entities
  // (shared PiiConsistencyDialog handles load + resolve + apply).

  const handleAutoDiscover = async () => {
    if (!model || !model.source_id || sourceTables.length === 0) { toast.error('No data source tables to discover'); return; }
    setAutoDiscovering(true);
    let added = 0;
    try {
      for (let i = 0; i < sourceTables.length; i++) {
        let table = sourceTables[i];
        if (entities.some(function(e) { return e.table_name === table.table_name; })) continue;
        let attrs = (table.columns || []).map(function(col) {
          return { name: col.name, data_type: col.data_type || 'string', semantic_type: _inferSemanticType(col.name, col.data_type), description: '', visible: true };
        });
        let ent = await api.createSemanticEntity(activeModelId, {
          name: table.table_name.replace(/_/g, ' ').replace(/\b\w/g, function(l) { return l.toUpperCase(); }),
          table_name: table.table_name, entity_type: 'fact', attributes: attrs,
          position: { x: 50 + (entities.length + added) * 320, y: 100 + Math.floor((entities.length + added) / 3) * 280 },
        });
        setEntities(function(prev) { return prev.concat([ent]); });
        added++;
      }
      toast.success('Discovered ' + added + ' entities from data source');
      setDismissedJoins(false);
      refreshJoinSuggestions(activeModelId);
    } catch (err) { toast.error(err.message); }
    finally { setAutoDiscovering(false); }
  };

  // #2 AI classify fields + suggest relationships
  const handleAiClassify = async () => {
    if (!activeModelId || entities.length === 0) { toast.error('Add entities first'); return; }
    setAiClassifying(true);
    try {
      // Suggest field classifications for each entity
      let updated = 0;
      for (let i = 0; i < entities.length; i++) {
        let ent = entities[i];
        if (!(ent.attributes || []).length) continue;
        try {
          let suggestions = await api.suggestFields(ent.table_name);
          if (suggestions.dimensions || suggestions.measures) {
            let dimNames = (suggestions.dimensions || []).map(function(d) { return d.name; });
            let msrMap = {};
            (suggestions.measures || []).forEach(function(m) { msrMap[m.name] = m.aggregation || 'sum'; });
            let newAttrs = (ent.attributes || []).map(function(attr) {
              // Never un-hide a field the user has hidden — AI classification
              // must not flip semantic_type 'hidden' (or visible:false) back to
              // a dimension/measure when the model is refreshed/reclassified.
              if (attr.semantic_type === 'hidden' || attr.visible === false) return attr;
              let isMeasure = msrMap[attr.name] !== undefined;
              let isDim = dimNames.indexOf(attr.name) >= 0;
              if (isMeasure) return Object.assign({}, attr, { semantic_type: 'measure', aggregation: msrMap[attr.name] });
              if (isDim) return Object.assign({}, attr, { semantic_type: 'dimension' });
              return attr;
            });
            await api.updateSemanticEntity(ent.entity_id, { attributes: newAttrs });
            updated++;
          }
        } catch (ex) { /* continue with next entity */ }
      }
      // Suggest relationships
      try {
        let relSuggestions = await api.suggestRelationships(activeModelId);
        if (relSuggestions.suggestions && relSuggestions.suggestions.length > 0) {
          // Dedupe: skip any suggestion whose source/target pair already has a
          // join (either direction) — the backend doesn't reject duplicates, so
          // AI Classify used to stack up identical joins on every run.
          let pairKey = function(a, b) { return [a, b].sort().join('::'); };
          let existingPairs = new Set((relationships || []).map(function(r) { return pairKey(r.source_entity_id, r.target_entity_id); }));
          // Suggestions arrive as a display name, sometimes as "Display Name (TABLE_NAME)".
          // Match on exact name or table_name before falling back to a substring match,
          // otherwise nothing ever matches and no join is created.
          let matchEntity = function(raw) {
            if (!raw) return null;
            let s = String(raw).trim().toLowerCase();
            let paren = s.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
            let namePart = (paren ? paren[1] : s).trim();
            let tablePart = paren ? paren[2].trim() : null;
            let byName = entities.find(function(e) { return (e.name || '').toLowerCase() === namePart; });
            if (byName) return byName;
            let byTable = entities.find(function(e) { return (e.table_name || '').toLowerCase() === (tablePart || namePart); });
            if (byTable) return byTable;
            // Substring fallback — only accept it when EXACTLY ONE entity matches,
            // so an ambiguous name never binds the suggestion to the wrong entity.
            let fuzzy = entities.filter(function(e) {
              let en = (e.name || '').toLowerCase(); let et = (e.table_name || '').toLowerCase();
              return (en && (en.includes(namePart) || namePart.includes(en))) || (tablePart && et === tablePart);
            });
            return fuzzy.length === 1 ? fuzzy[0] : null;
          };
          // Parse the LLM's "field1 = field2" join hints into the stored
          // join_conditions [{source_attr,target_attr,operator}] shape AND
          // validate each side against the real entity attributes — the LLM
          // sometimes invents fields (e.g. year/line_of_business on an entity
          // that doesn't have them), which creates non-functional joins.
          let attrNames = function(ent) {
            return new Set((ent.attributes || []).flatMap(function(a) {
              return [a.name, a.column_name, a.source_col].filter(Boolean).map(function(x) { return String(x).toLowerCase(); });
            }));
          };
          let stripQualifier = function(s) {
            let t = String(s).trim();
            return t.includes('.') ? t.split('.').pop().trim() : t;
          };
          let parseJoinFields = function(fields, srcEnt, tgtEnt) {
            let srcSet = attrNames(srcEnt);
            let tgtSet = attrNames(tgtEnt);
            return (fields || []).map(function(f) {
              let parts = String(f).split('=');
              if (parts.length !== 2) return null;
              let sa = stripQualifier(parts[0]);
              let ta = stripQualifier(parts[1]);
              // Only keep a condition when BOTH attrs actually exist on their
              // respective entities (try both orientations, since the LLM may
              // list them reversed).
              if (srcSet.has(sa.toLowerCase()) && tgtSet.has(ta.toLowerCase())) {
                return { source_attr: sa, target_attr: ta, operator: 'equals' };
              }
              if (srcSet.has(ta.toLowerCase()) && tgtSet.has(sa.toLowerCase())) {
                return { source_attr: ta, target_attr: sa, operator: 'equals' };
              }
              return null;
            }).filter(Boolean);
          };
          for (let j = 0; j < relSuggestions.suggestions.length; j++) {
            let sug = relSuggestions.suggestions[j];
            let srcEnt = matchEntity(sug.source_entity);
            let tgtEnt = matchEntity(sug.target_entity);
            if (srcEnt && tgtEnt && srcEnt.entity_id !== tgtEnt.entity_id) {
              let key = pairKey(srcEnt.entity_id, tgtEnt.entity_id);
              if (existingPairs.has(key)) continue;
              let joinConds = parseJoinFields(sug.join_fields, srcEnt, tgtEnt);
              // Don't create a join the data can't actually satisfy.
              if (joinConds.length === 0) continue;
              existingPairs.add(key);
              try {
                let rel = await api.createSemanticRelationship(activeModelId, { name: sug.source_entity + ' to ' + sug.target_entity, source_entity_id: srcEnt.entity_id, target_entity_id: tgtEnt.entity_id, cardinality: sug.cardinality || 'many_to_one', join_type: 'inner', join_conditions: joinConds });
                setRelationships(function(prev) { return prev.concat([rel]); });
              } catch (ex2) { /* skip duplicate */ }
            }
          }
        }
      } catch (ex3) { /* no suggestions */ }
      // Reload to get updated attrs
      await loadModel(activeModelId);
      toast.success('AI classification complete — ' + updated + ' entities updated');
    } catch (err) { toast.error(err.message); }
    finally { setAiClassifying(false); }
  };

  // #3 Guided wizard — launch after model creation
  const startWizard = async (modelObj) => {
    setWizardStep(1);
    setWizardSelectedTables([]);
    setWizardAiSuggestions(null);
    setWizardRelSuggestions(null);
    setWizardTableSearch('');
    setWizardOpen(true);
    // Load tables from the source (show spinner while loading)
    if (modelObj.source_id) {
      setWizardTablesLoading(true);
      try {
        let schema = await api.introspectDataSource(modelObj.source_id);
        setWizardTables(schema.tables || []);
        setWizardSelectedTables((schema.tables || []).map(function(t) { return t.table_name; }));
      } catch (ex) { setWizardTables([]); toast.error('Failed to load tables from data source'); }
      finally { setWizardTablesLoading(false); }
    }
  };

  const wizardAddEntities = async () => {
    setSaving(true);
    let added = 0;
    try {
      for (let i = 0; i < wizardTables.length; i++) {
        let table = wizardTables[i];
        if (wizardSelectedTables.indexOf(table.table_name) < 0) continue;
        // Don't re-add a table that's already an entity (re-running the wizard
        // previously created duplicate entities for the same physical table).
        if (entities.some(e => e.table_name === table.table_name)) continue;
        let attrs = (table.columns || []).map(function(col) {
          return { name: col.name, data_type: col.data_type || 'string', semantic_type: _inferSemanticType(col.name, col.data_type), description: '', visible: true };
        });
        await api.createSemanticEntity(activeModelId, {
          name: table.table_name.replace(/_/g, ' ').replace(/\b\w/g, function(l) { return l.toUpperCase(); }),
          table_name: table.table_name, entity_type: 'fact', attributes: attrs,
          position: { x: 50 + added * 320, y: 100 + Math.floor(added / 3) * 280 },
        });
        added++;
      }
      toast.success(added + ' entities added');
      setWizardStep(2);
      // Trigger AI classification
      await loadModel(activeModelId);
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const wizardRunAi = async () => {
    setAiClassifying(true);
    try {
      await handleAiClassify();
      setWizardStep(3);
    } catch (err) { toast.error(err.message); }
    finally { setAiClassifying(false); }
  };

  const wizardFinish = () => {
    setWizardOpen(false);
    setWizardStep(1);
  };

  const handleDeleteEntity = async (entityId) => {
    try {
      await api.deleteSemanticEntity(entityId);
      setEntities(prev => prev.filter(e => e.entity_id !== entityId));
      setRelationships(prev => prev.filter(r => r.source_entity_id !== entityId && r.target_entity_id !== entityId));
      if (selectedEntity === entityId) setSelectedEntity(null);
      toast.success('Entity deleted');
    } catch (err) { toast.error(err.message); }
  };

  const handleAddRelationship = async () => {
    setSaving(true);
    try {
      const valid = (relMappings || []).filter(m => m.source && m.target);
      const join_conditions = valid.map(m => ({ source_attr: m.source, target_attr: m.target }));
      const first = valid[0] || { source: '', target: '' };
      const rel = await api.createSemanticRelationship(activeModelId, { name: relName, source_entity_id: relSource, target_entity_id: relTarget, source_field: first.source, target_field: first.target, cardinality: relCardinality, join_type: 'inner', join_conditions });
      setRelationships(prev => [...prev, rel]);
      setAddRelOpen(false);
      setRelSource(''); setRelTarget(''); setRelName(''); setRelCardinality('many_to_one'); setRelMappings([{ source: '', target: '' }]);
      toast.success('Relationship added');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDeleteRelationship = async (relId) => {
    try {
      await api.deleteSemanticRelationship(relId);
      setRelationships(prev => prev.filter(r => r.relationship_id !== relId));
      toast.success('Relationship deleted');
    } catch (err) { toast.error(err.message); }
  };

  const handleSaveAsModel = () => {
    if (!activeModelId) return;
    setSaveAsOpen(true);
  };

  // Keyboard: ⌘/Ctrl+S saves the model, ⌘/Ctrl+Shift+S opens Save As.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
        if (!activeModelId) return;
        e.preventDefault();
        if (e.shiftKey) handleSaveAsModel();
        else setSaveDialogOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModelId]);

  const handleModelCloned = async (res) => {
    const nid = res?.model?.model_id;
    if (nid) { setActiveModelId(nid); setSearchParams({ model: nid }); await loadModel(nid); }
  };

  const handleAddMeasure = async () => {
    setSaving(true);
    try {
      const _ignore = (scopeIgnore || '').split(',').map(s => s.trim()).filter(Boolean);
      const _nullOps = new Set(['is_null', 'is_not_null']);
      const _listOps = new Set(['in', 'not_in']);
      const _clauses = (scopeFilters || []).map(f => {
        const clause = { field: f.field, operator: f.operator };
        if (!_nullOps.has(f.operator)) {
          clause.value = _listOps.has(f.operator)
            ? String(f.value).split(',').map(s => s.trim()).filter(Boolean)
            : f.value;
        }
        return clause;
      });
      const _isRolling = tiMode && /^ROLLING_/.test(tiOp);
      const payload = _isRolling
        ? { name: measureName, description: measureDesc, source_entity_id: measureEntity, formula: `${tiOp}({${tiOf}}, ${Math.max(1, parseInt(tiN, 10) || 1)})` }
        : tiMode
        ? { name: measureName, description: measureDesc, source_entity_id: measureEntity, time_intel: { op: tiOp, of: tiOf } }
        : scopeMode
        ? { name: measureName, description: measureDesc, source_entity_id: measureEntity, base_field: scopeBaseField, aggregation: scopeAgg, scope_filters: _clauses, ...(_ignore.length ? { ignore_filters: _ignore } : {}), ...(scopePct ? { pct_of_total: true } : {}) }
        : { name: measureName, formula: measureFormula, description: measureDesc, source_entity_id: measureEntity };
      if (measureFolder && measureFolder.trim()) payload.display_folder = measureFolder.trim();
      if (tiMode && !tiOf) { toast.error('Pick a source measure for time intelligence'); setSaving(false); return; }
      const m = await api.createSemanticMeasure(activeModelId, payload);
      setCalcMeasures(prev => [...prev, m]);
      setAddMeasureOpen(false);
      setMeasureName(''); setMeasureFormula(''); setMeasureEntity(''); setMeasureDesc(''); setMeasureFolder(''); setMeasureCursorPos(0);
      setScopeMode(false); setScopeBaseField(''); setScopeAgg('sum'); setScopeFilters([]); setScopeIgnore(''); setScopePct(false);
      setTiMode(false); setTiOp('ytd'); setTiOf(''); setTiN(3);
      toast.success('Measure added');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDeleteMeasure = async (measureId) => {
    const m = calcMeasures.find(x => x.measure_id === measureId);
    if (!(await confirmFn({ title: 'Delete measure?', message: `Delete the calculated measure "${m?.name || 'this measure'}"? This can't be undone and any report or dashboard using it will lose the calculation.`, confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await api.deleteSemanticMeasure(measureId);
      setCalcMeasures(prev => prev.filter(m => m.measure_id !== measureId));
      toast.success('Measure deleted');
    } catch (err) { toast.error(err.message); }
  };

  // Global mouseup to ensure drag always releases, even outside canvas
  React.useEffect(() => {
    const globalUp = () => {
      if (draggingRef.current) {
        const eid = draggingRef.current;
        draggingRef.current = null;
        setDraggingEntity(null);
        // Save position async (fire and forget)
        const en = entities.find(e => e.entity_id === eid);
        if (en?.position) { api.updateSemanticEntity(eid, { position: en.position }).catch(() => {}); }
      }
      setIsPanning(false);
    };
    window.addEventListener('mouseup', globalUp);
    return () => window.removeEventListener('mouseup', globalUp);
  }, [entities]);

  if (loading) return <div className="p-6"><div className="glass-card rounded-2xl h-40 animate-pulse" /></div>;

  // ─── Model List View ───
  if (!activeModelId) {
    return (
      <TooltipProvider>
      <div className="p-6 space-y-6" data-testid="semantic-models-page">
        <div className="flex flex-col gap-4 animate-slide-up">
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">Semantic Models</h1>
            <p className="text-sm text-muted-foreground mt-1">Governed business data models with ER relationships</p>
          </div>
          <div className="flex items-center flex-wrap gap-2">
            <div className="flex items-center border border-border/50 rounded-lg overflow-hidden">
              <Tooltip><TooltipTrigger asChild>
                <button onClick={() => setModelViewMode('cards')} className={`p-2 transition-colors ${modelViewMode === 'cards' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="sm-view-cards"><LayoutGrid className="w-4 h-4" /></button>
              </TooltipTrigger><TooltipContent><p className="text-xs">Card view</p></TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild>
                <button onClick={() => setModelViewMode('table')} className={`p-2 transition-colors ${modelViewMode === 'table' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="sm-view-table"><Table className="w-4 h-4" /></button>
              </TooltipTrigger><TooltipContent><p className="text-xs">Table view</p></TooltipContent></Tooltip>
            </div>
            <Button onClick={() => setCreateModelOpen(true)} data-testid="create-model-btn" className="rounded-xl bg-primary text-primary-foreground shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all active:scale-95">
              <Plus className="w-4 h-4 mr-2" /> New Model
            </Button>
            <ImportModelButton onImported={() => api.getSemanticModels().then(r => setModels(r || []))} />
            <Tooltip><TooltipTrigger asChild>
              <Button variant="outline" onClick={() => setPyramidWizardOpen(true)} className="rounded-xl border-amber-500/30 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" data-testid="import-from-pyramid-btn">
                <Pyramid className="w-4 h-4 mr-2" /> Import from Pyramid
              </Button>
            </TooltipTrigger><TooltipContent><p className="text-xs">Import a full semantic model from a Pyramid Analytics repository</p></TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild>
              <Button variant="outline" onClick={() => navigate('/semantic-models/compare')} disabled={models.length < 2} className="rounded-xl" data-testid="compare-models-btn">
                <GitCompare className="w-4 h-4 mr-2" /> Compare
              </Button>
            </TooltipTrigger><TooltipContent><p className="text-xs">{models.length < 2 ? 'Need at least 2 models to compare' : 'Compare two semantic models'}</p></TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild>
              <Button variant="outline" onClick={() => lookmlFileRef.current?.click()} disabled={lookmlImporting} className="rounded-xl" data-testid="import-lookml-btn">
                {lookmlImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Import LookML
              </Button>
            </TooltipTrigger><TooltipContent><p className="text-xs">Upload a Looker <code>.lkml</code>, <code>.lookml</code>, or <code>.zip</code> bundle to create a new semantic model from Looker views, dimensions, measures, and explores.</p></TooltipContent></Tooltip>
            <input
              ref={lookmlFileRef}
              type="file"
              accept=".lkml,.lookml,.zip"
              className="hidden"
              data-testid="lookml-file-input"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                e.target.value = '';
                setLookmlImporting(true);
                try {
                  const fd = new FormData(); fd.append('file', file);
                  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/semantic/import-lookml`, {
                    method: 'POST', credentials: 'include', body: fd,
                  });
                  if (!resp.ok) throw new Error(await resp.text());
                  const out = await resp.json();
                  setLookmlResult(out);
                  toast.success(`Imported ${out.summary.views_imported} view(s), ${out.summary.measures_imported} measure(s)`);
                  const refreshed = await api.getSemanticModels();
                  setModels(refreshed || []);
                  if (out?.model?.model_id) {
                    await loadModel(out.model.model_id);
                  }
                } catch (err) {
                  toast.error(err.message || 'LookML import failed');
                } finally {
                  setLookmlImporting(false);
                }
              }}
            />
            <Tooltip><TooltipTrigger asChild>
              <Button variant="outline" onClick={() => tableauFileRef.current?.click()} disabled={tableauImporting} className="rounded-xl" data-testid="import-tableau-btn">
                {tableauImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Import Tableau
              </Button>
            </TooltipTrigger><TooltipContent><p className="text-xs">Upload a Tableau <code>.twb</code>, <code>.twbx</code>, <code>.tds</code>, or <code>.tdsx</code> file to create a new semantic model from its datasources, dimensions, and measures.</p></TooltipContent></Tooltip>
            <input
              ref={tableauFileRef}
              type="file"
              accept=".twb,.twbx,.tds,.tdsx"
              className="hidden"
              data-testid="tableau-file-input"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                e.target.value = '';
                setTableauImporting(true);
                try {
                  const fd = new FormData(); fd.append('file', file);
                  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/semantic/import-tableau`, {
                    method: 'POST', credentials: 'include', body: fd,
                  });
                  if (!resp.ok) throw new Error(await resp.text());
                  const out = await resp.json();
                  setTableauResult(out);
                  const s = out.summary || {};
                  toast.success(`Imported ${s.datasources_imported} datasource(s), ${s.columns_imported} column(s)`);
                  const refreshed = await api.getSemanticModels();
                  setModels(refreshed || []);
                  if (out?.model?.model_id) {
                    await loadModel(out.model.model_id);
                  }
                } catch (err) {
                  toast.error(err.message || 'Tableau import failed');
                } finally {
                  setTableauImporting(false);
                }
              }}
            />
            <Tooltip><TooltipTrigger asChild>
              <Button variant="outline" onClick={() => ermodelFileRef.current?.click()} disabled={ermodelImporting} className="rounded-xl" data-testid="import-ermodel-btn">
                {ermodelImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Import Model JSON
              </Button>
            </TooltipTrigger><TooltipContent><p className="text-xs">Upload an ER-model / data-warehouse <code>.json</code> export (tables, columns, relationships) to create a new semantic model. Imports the model definition only — connect a data source to run queries.</p></TooltipContent></Tooltip>
            <input
              ref={ermodelFileRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              data-testid="ermodel-file-input"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                e.target.value = '';
                setErmodelImporting(true);
                try {
                  const fd = new FormData(); fd.append('file', file);
                  const nameGuess = (file.name || '').replace(/\.json$/i, '').replace(/^er-model-/i, '');
                  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/semantic/import-ermodel?model_name=${encodeURIComponent(nameGuess)}`, {
                    method: 'POST', credentials: 'include', body: fd,
                  });
                  if (!resp.ok) throw new Error(await resp.text());
                  const out = await resp.json();
                  setErmodelResult(out);
                  const s = out.summary || {};
                  toast.success(`Imported ${s.tables_imported} table(s), ${s.columns_imported} column(s), ${s.relationships_imported} relationship(s)`);
                  const refreshed = await api.getSemanticModels();
                  setModels(refreshed || []);
                  if (out?.model?.model_id) {
                    await loadModel(out.model.model_id);
                  }
                } catch (err) {
                  toast.error(err.message || 'Model import failed');
                } finally {
                  setErmodelImporting(false);
                }
              }}
            />
          </div>
        </div>

        {/* Bulk selection action bar */}
        {selectedModelIds.size > 0 && (
          <div className="mb-3 flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-2" data-testid="bulk-share-bar">
            <span className="text-xs text-foreground font-medium">{selectedModelIds.size} selected</span>
            <Button size="sm" className="rounded-lg text-xs h-7 bg-primary text-primary-foreground" onClick={() => setBulkShareOpen(true)} data-testid="bulk-share-open">
              <Shield className="w-3 h-3 mr-1" /> Set permissions
            </Button>
            <button onClick={() => setSelectedModelIds(new Set())} className="text-xs text-muted-foreground hover:text-foreground ml-auto" data-testid="bulk-share-clear">Clear</button>
          </div>
        )}

        {/* Card View */}
        {modelViewMode === 'cards' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children" data-testid="models-grid">
            {models.map(m => (
              <div key={m.model_id} className={`glass-card rounded-2xl p-5 hover:border-primary/20 transition-all cursor-pointer group relative ${selectedModelIds.has(m.model_id) ? 'border-primary/40' : ''}`} onClick={() => loadModel(m.model_id)} data-testid={`model-card-${m.model_id}`}>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleModelSelect(m.model_id); }}
                  className={`absolute top-3 left-3 w-5 h-5 rounded-md border flex items-center justify-center transition-all z-10 ${selectedModelIds.has(m.model_id) ? 'bg-primary border-primary text-primary-foreground opacity-100' : 'border-border/60 text-transparent opacity-0 group-hover:opacity-100'}`}
                  title="Select for bulk sharing"
                  data-testid={`model-select-${m.model_id}`}
                >
                  <CheckSquare className="w-3.5 h-3.5" />
                </button>
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 rounded-xl bg-indigo-500/10 border border-indigo-500/20"><GitBranch className="w-5 h-5 text-indigo-400" strokeWidth={1.5} /></div>
                  <div className="flex gap-1.5">
                    <Badge className={`text-[9px] ${m.status === 'published' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/15 text-amber-400 border-amber-500/20'}`}>{m.status}</Badge>
                    <Badge variant="outline" className="text-[9px] border-border/30">v{m.version}</Badge>
                  </div>
                </div>
                <h3 className="text-sm font-heading font-semibold text-foreground mb-1">{m.name}</h3>
                <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{m.description}</p>
                {m.source_name && (
                  <div className="flex items-center gap-1.5 mb-2 text-[10px] text-muted-foreground/80">
                    <Database className="w-3 h-3" /> {m.source_name}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground/80">{m.created_at ? new Date(m.created_at).toLocaleDateString() : ''}</span>
                  <div className="flex items-center gap-0.5">
                    <ModelCardActions model={m} onChanged={() => api.getSemanticModels().then(r => setModels(r || []))} />
                    {m._can_edit !== false && (
                    <Tooltip><TooltipTrigger asChild>
                      <button onClick={(e) => { e.stopPropagation(); setPermModel(m); }} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`permissions-model-${m.model_id}`}><Shield className="w-3.5 h-3.5" /></button>
                    </TooltipTrigger><TooltipContent><p className="text-xs">Permissions</p></TooltipContent></Tooltip>
                    )}
                    {m._can_delete !== false && (
                    <Tooltip><TooltipTrigger asChild>
                      <button onClick={(e) => { e.stopPropagation(); confirmDeleteModel(m.model_id, m.name); }} className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-model-${m.model_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </TooltipTrigger><TooltipContent><p className="text-xs">Delete model</p></TooltipContent></Tooltip>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Table View */}
        {modelViewMode === 'table' && (
          <div className="glass-card rounded-2xl overflow-hidden" data-testid="models-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Version</th>
                  <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-right text-[10px] font-heading font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {models.map(m => (
                  <tr key={m.model_id} className="border-b border-border/20 hover:bg-muted/20 transition-colors cursor-pointer group" onClick={() => loadModel(m.model_id)}>
                    <td className="px-4 py-3 font-medium text-foreground">{m.name}</td>
                    <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">{m.description}</td>
                    <td className="px-4 py-3"><Badge className={`text-[9px] ${m.status === 'published' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>{m.status}</Badge></td>
                    <td className="px-4 py-3 text-muted-foreground">v{m.version}</td>
                    <td className="px-4 py-3 text-muted-foreground">{m.created_at ? new Date(m.created_at).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Tooltip><TooltipTrigger asChild><button onClick={(e) => { e.stopPropagation(); loadModel(m.model_id); }} className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"><Eye className="w-3.5 h-3.5" /></button></TooltipTrigger><TooltipContent><p className="text-xs">Open</p></TooltipContent></Tooltip>
                        {m._can_edit !== false && <Tooltip><TooltipTrigger asChild><button onClick={(e) => { e.stopPropagation(); setPermModel(m); }} className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary" data-testid={`permissions-model-row-${m.model_id}`}><Shield className="w-3.5 h-3.5" /></button></TooltipTrigger><TooltipContent><p className="text-xs">Permissions</p></TooltipContent></Tooltip>}
                        {m._can_delete !== false && <Tooltip><TooltipTrigger asChild><button onClick={(e) => { e.stopPropagation(); confirmDeleteModel(m.model_id, m.name); }} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button></TooltipTrigger><TooltipContent><p className="text-xs">Delete</p></TooltipContent></Tooltip>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Model Dialog — 2 steps: pick source, then name/desc */}
        <CreateModelDialog
          open={createModelOpen}
          onOpenChange={v => { setCreateModelOpen(v); if (!v) { setCreateStep(1); setNewModelSourceId(''); setNewModelSourceName(''); setNewModelName(''); setNewModelDesc(''); } }}
          step={createStep}
          setStep={setCreateStep}
          sources={dataSources}
          sourceId={newModelSourceId}
          setSource={(id, nm) => { setNewModelSourceId(id); setNewModelSourceName(nm); }}
          name={newModelName}
          setName={setNewModelName}
          desc={newModelDesc}
          setDesc={setNewModelDesc}
          saving={saving}
          onSubmit={handleCreateModel}
        />

        {/* Model Permissions Dialog */}
        <ModelPermissionsDialog model={permModel} open={!!permModel} onOpenChange={(v) => { if (!v) setPermModel(null); }} />

        {/* Bulk Model Share Dialog */}
        <BulkModelShareDialog
          open={bulkShareOpen}
          onOpenChange={setBulkShareOpen}
          modelIds={Array.from(selectedModelIds)}
          onApplied={() => setSelectedModelIds(new Set())}
        />

        {/* LookML Import Summary Dialog */}
        <LookmlSummaryDialog result={lookmlResult} onClose={() => setLookmlResult(null)} />

        {/* Pyramid semantic-model import wizard */}
        <PyramidImportWizard
          open={pyramidWizardOpen}
          onOpenChange={setPyramidWizardOpen}
          onImported={(newModelId, openIt) => {
            api.getSemanticModels().then(r => setModels(r || []));
            if (openIt && newModelId) loadModel(newModelId);
          }}
        />

        {/* Tableau Import Summary Dialog */}
        <Dialog open={!!tableauResult} onOpenChange={v => { if (!v) setTableauResult(null); }}>
          <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="tableau-summary-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-foreground flex items-center gap-2">
                <Database className="w-4 h-4 text-orange-400" /> Tableau Import Complete
              </DialogTitle>
            </DialogHeader>
            {tableauResult && <TableauImportSummary result={tableauResult} onClose={() => setTableauResult(null)} />}
          </DialogContent>
        </Dialog>

        {/* ER-model / DW JSON Import Summary Dialog */}
        <Dialog open={!!ermodelResult} onOpenChange={v => { if (!v) setErmodelResult(null); }}>
          <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="ermodel-summary-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-foreground flex items-center gap-2">
                <Database className="w-4 h-4 text-emerald-400" /> Model Imported
              </DialogTitle>
            </DialogHeader>
            {ermodelResult && (
              <div className="space-y-4" data-testid="ermodel-summary">
                <p className="text-sm text-muted-foreground">
                  <span className="text-foreground font-medium">{ermodelResult.model?.name}</span> was created from your data-warehouse export.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['Tables', ermodelResult.summary?.tables_imported],
                    ['Relationships', ermodelResult.summary?.relationships_imported],
                    ['Columns', ermodelResult.summary?.columns_imported],
                    ['Hidden columns', ermodelResult.summary?.hidden_columns],
                    ['Fact tables', ermodelResult.summary?.fact_tables],
                    ['Dimension tables', ermodelResult.summary?.dimension_tables],
                  ].map(([label, val]) => (
                    <div key={label} className="rounded-xl border border-border/50 bg-background/40 p-3">
                      <div className="text-lg font-semibold text-foreground">{val ?? 0}</div>
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
                    </div>
                  ))}
                </div>
                {ermodelResult.summary?.relationships_skipped > 0 && (
                  <p className="text-xs text-amber-400">{ermodelResult.summary.relationships_skipped} relationship(s) skipped (endpoint table not found).</p>
                )}
                <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-3 text-xs text-sky-200/90">
                  This is the model <strong>definition</strong> only — no row data was imported. Connect the underlying data source to run queries and dashboards on it.
                </div>
                <Button className="w-full rounded-xl" onClick={() => setErmodelResult(null)} data-testid="ermodel-summary-close">Done</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation (in list view) */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent className="glass-card border-border/50 rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading text-foreground">Delete Semantic Model</AlertDialogTitle>
              <AlertDialogDescription className="text-muted-foreground text-sm">
                Are you sure you want to delete <span className="font-semibold text-foreground">{deleteTargetName}</span>? This will permanently remove the model and all its entities, relationships, and calculated measures. This action cannot be undone.
              </AlertDialogDescription>
              {deleteConfirmOpen && deleteTargetId && <ImpactWarning kind="model" id={deleteTargetId} />}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteModel} className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-model">Delete Model</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      </TooltipProvider>
    );
  }


  // Detail View
  const handleEntityDragStart = (e, entity) => { const r = e.currentTarget.getBoundingClientRect(); setDragOffset({ x: e.clientX - r.left, y: e.clientY - r.top }); setDraggingEntity(entity.entity_id); draggingRef.current = entity.entity_id; };
  const handleEntityDrag = (e) => { if (!draggingRef.current || !e.clientX) return; const c = e.currentTarget.closest('[data-canvas]'); if (!c) return; const cr = c.getBoundingClientRect(); let rawX = (e.clientX - cr.left - canvasPan.x) / canvasZoom - dragOffset.x; let rawY = (e.clientY - cr.top - canvasPan.y) / canvasZoom - dragOffset.y; setEntities(prev => prev.map(en => en.entity_id === draggingRef.current ? { ...en, position: { x: Math.max(0, Math.round(rawX)), y: Math.max(0, Math.round(rawY)) } } : en)); };
  const handleEntityDragEnd = async () => { const eid = draggingRef.current; if (!eid) return; draggingRef.current = null; setDraggingEntity(null); const en = entities.find(e => e.entity_id === eid); if (en) { try { await api.updateSemanticEntity(eid, { position: en.position }); } catch(ex) { /* silent */ } } };



  const updateEntityType = async (entityId, newType) => { try { const u = await api.updateSemanticEntity(entityId, { entity_type: newType }); setEntities(prev => prev.map(e => e.entity_id === entityId ? u : e)); toast.success(`Marked as ${newType} table`); } catch (err) { toast.error(err.message); } };

  const updateAttribute = async (entityId, attrIndex, updates) => { const en = entities.find(e => e.entity_id === entityId); if (!en) return; if ('business_name' in updates) { const nv = (updates.business_name || '').trim().toLowerCase(); if (nv && (en.attributes || []).some((a, i) => i !== attrIndex && ((a.business_name || '').trim().toLowerCase() === nv))) { toast.error(`Display Name "${updates.business_name}" is already used by another field in this table`); return; } } const na = [...(en.attributes || [])]; na[attrIndex] = { ...na[attrIndex], ...updates }; try { const u = await api.updateSemanticEntity(entityId, { attributes: na }); setEntities(prev => prev.map(e => e.entity_id === entityId ? u : e)); } catch (err) { toast.error(err.message); } };
  const setDefaultDate = async (entityId, attrName) => { const en = entities.find(e => e.entity_id === entityId); if (!en) return; const next = en.default_date_field === attrName ? null : attrName; try { const u = await api.updateSemanticEntity(entityId, { default_date_field: next }); setEntities(prev => prev.map(e => e.entity_id === entityId ? u : e)); toast.success(next ? `Default business date: ${attrName}` : 'Default date cleared'); } catch (err) { toast.error(err.message); } };
  const toggleAllowedDate = async (entityId, attrName) => { const en = entities.find(e => e.entity_id === entityId); if (!en) return; const cur = Array.isArray(en.allowed_date_fields) ? en.allowed_date_fields : []; const next = cur.includes(attrName) ? cur.filter(n => n !== attrName) : [...cur, attrName]; try { const u = await api.updateSemanticEntity(entityId, { allowed_date_fields: next }); setEntities(prev => prev.map(e => e.entity_id === entityId ? u : e)); toast.success(next.includes(attrName) ? `Allowed alternate date: ${attrName}` : `Removed alternate: ${attrName}`); } catch (err) { toast.error(err.message); } };
  const removeAttribute = async (entityId, attrIndex) => { const en = entities.find(e => e.entity_id === entityId); if (!en) return; try { const u = await api.updateSemanticEntity(entityId, { attributes: (en.attributes || []).filter((_, i) => i !== attrIndex) }); setEntities(prev => prev.map(e => e.entity_id === entityId ? u : e)); toast.success('Removed'); } catch (err) { toast.error(err.message); } };

  const addCalcField = async () => { if (!selectedEntity || !calcFieldName.trim()) return; const en = entities.find(e => e.entity_id === selectedEntity); if (!en) return; const na = { name: calcFieldName.replace(/\s+/g, '_').toLowerCase(), data_type: 'float', semantic_type: 'measure', description: calcFieldDesc, aggregation: 'sum', formula: calcFieldFormula, is_calculated: true, visible: true }; try { const u = await api.updateSemanticEntity(selectedEntity, { attributes: [...(en.attributes || []), na] }); setEntities(prev => prev.map(e => e.entity_id === selectedEntity ? u : e)); setCalcFieldOpen(false); setCalcFieldName(''); setCalcFieldFormula(''); setCalcFieldDesc(''); toast.success('Calculated field added'); } catch (err) { toast.error(err.message); } };

  const addCalcDimension = async ({ name, display_name, formula, sort_field, subtype, bucket_spec, sort_expr }) => {
    if (!selectedEntity) return;
    const en = entities.find(e => e.entity_id === selectedEntity);
    if (!en) return;
    const na = {
      name, display_name,
      data_type: 'string', semantic_type: 'dimension',
      description: subtype === 'bucket' ? `Bucketed: ${bucket_spec?.base_field || formula}` : `Calculated: ${formula}`,
      formula, is_calculated: true,
      sort_field: sort_field || null,
      visible: true,
      ...(subtype ? { subtype } : {}),
      ...(bucket_spec ? { bucket_spec } : {}),
      ...(sort_expr ? { sort_expr } : {}),
    };
    try {
      const u = await api.updateSemanticEntity(selectedEntity, { attributes: [...(en.attributes || []), na] });
      setEntities(prev => prev.map(e => e.entity_id === selectedEntity ? u : e));
      setCalcDimOpen(false);
      toast.success(`Calculated dimension "${display_name}" added`);
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteCalcDimension = async (attrName) => {
    if (!selectedEntity) return;
    const en = entities.find(e => e.entity_id === selectedEntity);
    if (!en) return;
    const keep = (a) => !(a.name === attrName && a.is_calculated && a.semantic_type === 'dimension');
    try {
      await api.updateSemanticEntity(selectedEntity, {
        attributes: (en.attributes || []).filter(keep),
      });
      setEntities(prev => prev.map(e => e.entity_id === selectedEntity
        ? { ...e, attributes: (e.attributes || []).filter(keep) }
        : e));
      toast.success('Calculated dimension removed');
    } catch (err) { toast.error(err.message); }
  };

  const handleDeleteCalcMeasureAttr = async (attrName) => {
    if (!selectedEntity) return;
    const en = entities.find(e => e.entity_id === selectedEntity);
    if (!en) return;
    if (!(await confirmFn({ title: 'Delete measure?', message: `Delete the calculated measure "${attrName}"? This can't be undone and any report or dashboard using it will lose the calculation.`, confirmLabel: 'Delete', destructive: true }))) return;
    const keep = (a) => !(a.name === attrName && a.is_calculated && a.semantic_type === 'measure');
    try {
      await api.updateSemanticEntity(selectedEntity, {
        attributes: (en.attributes || []).filter(keep),
      });
      // Update from the PREVIOUS state deterministically (don't rely on the
      // response object identity) so the removed measure disappears immediately.
      setEntities(prev => prev.map(e => e.entity_id === selectedEntity
        ? { ...e, attributes: (e.attributes || []).filter(keep) }
        : e));
      toast.success('Calculated measure removed');
    } catch (err) { toast.error(err.message); }
  };



  const addDerivedMeasure = async () => {
    if (!selectedEntity || !derivedSourceField || !derivedName.trim()) return;
    let en = entities.find(function(e) { return e.entity_id === selectedEntity; });
    if (!en) return;
    let srcAttr = (en.attributes || []).find(function(a) { return a.name === derivedSourceField; });
    let newName = derivedName.replace(/\s+/g, '_').toLowerCase();
    let na = {
      name: newName,
      data_type: srcAttr ? srcAttr.data_type : 'float',
      semantic_type: 'measure',
      description: derivedDesc || derivedName,
      aggregation: derivedAgg,
      format: derivedFormat || (srcAttr ? srcAttr.format : ''),
      source_field: derivedSourceField,
      is_derived: true,
      visible: true,
    };
    try {
      let u = await api.updateSemanticEntity(selectedEntity, { attributes: (en.attributes || []).concat([na]) });
      setEntities(function(prev) { return prev.map(function(e) { return e.entity_id === selectedEntity ? u : e; }); });
      setDerivedMeasureOpen(false);
      setDerivedSourceField(''); setDerivedName(''); setDerivedDesc(''); setDerivedAgg('avg'); setDerivedFormat('');
      toast.success('Derived measure "' + derivedName + '" added');
    } catch (err) { toast.error(err.message); }
  };

  const handleSemBrowse = async (attr) => {
    setSemBrowseField(attr);
    setSemBrowseOpen(true);
    setSemBrowseLoading(true);
    setSemBrowseData([]);
    try {
      let res = await api.executeQuery({ model_id: activeModelId, dimensions: [attr.name], measures: [], limit: 50 });
      setSemBrowseData((res.data || []).map(function(r) { return r[attr.name]; }).filter(function(v) { return v !== null && v !== undefined; }));
    } catch (err) { toast.error(err.message); }
    finally { setSemBrowseLoading(false); }
  };


  const handleEntityClick = (entityId) => { if (connectMode && connectSource) { if (connectSource !== entityId) { const sn = entities.find(e => e.entity_id === connectSource); const tn = entities.find(e => e.entity_id === entityId); setRelSource(connectSource); setRelTarget(entityId); setRelName((sn ? sn.name : '') + ' to ' + (tn ? tn.name : '')); setRelMappings([{ source: '', target: '' }]); setAddRelOpen(true); } setConnectMode(false); setConnectSource(null); } else { setSelectedEntity(entityId); } };

  const validationWarnings = [];

  // Aggregate rollup bindings are invisible acceleration objects — hidden from
  // the canvas & entity list (managed in the Aggregate panel), still used for routing.
  const visibleEntities = entities.filter(function(e) { return !(e.derived_from_entity && e.aggregation_role === 'aggregate'); });
  const maxX = Math.max.apply(null, visibleEntities.map(function(e) { return (e.position ? e.position.x : 0) + 280; }).concat([800]));
  const maxY = Math.max.apply(null, visibleEntities.map(function(e) { return (e.position ? e.position.y : 0) + 250; }).concat([500]));
  const selectedEntityData = entities.find(function(e) { return e.entity_id === selectedEntity; });

  return (
    <>
    <TooltipProvider>
    <div className="flex flex-col h-full" data-testid="semantic-model-detail">
      {/* Toolbar — full width across top */}
      <div className="px-4 py-2 border-b border-border/50 flex items-center gap-2 overflow-x-auto flex-shrink-0" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }}>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="ghost" size="icon" onClick={() => smConfirmNav(() => { setActiveModelId(null); setModel(null); setSmHasUnsavedChanges(false); setSearchParams({}, { replace: true }); })} className="rounded-lg w-7 h-7 flex-shrink-0" data-testid="back-to-models"><ArrowLeft className="w-4 h-4" /></Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 min-w-0 rounded-lg px-1.5 py-1 hover:bg-muted/30 transition-colors active:scale-[0.98]"
                  data-testid="model-source-trigger"
                  title={model?.source_id ? `Data source: ${model.source_name || 'connected'} — click to change` : 'Click to connect a data source'}
                >
                  <div className="p-1 rounded-md bg-indigo-500/10 border border-indigo-500/20 flex-shrink-0"><GitBranch className="w-3.5 h-3.5 text-indigo-400" /></div>
                  <h2 className="text-xs font-heading font-bold text-foreground truncate max-w-[240px]" title={model?.name}>{model && model.name}</h2>
                  <ChevronDown className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
                <div className="px-2 py-1.5 text-[11px] font-semibold text-muted-foreground">{model?.source_id ? 'Change data source' : 'Connect to a data source'}</div>
                {dataSources.length === 0 ? (
                  <DropdownMenuItem disabled className="text-xs">No data sources — add one on the Data Sources page</DropdownMenuItem>
                ) : dataSources.map(d => (
                  <DropdownMenuItem key={d.source_id} onSelect={() => handleConnectSource(d.source_id)} data-testid={`connect-source-opt-${d.source_id}`} className="text-xs">
                    <Database className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
                    <span className="truncate">{d.name}</span>
                    {d.source_type ? <span className="text-muted-foreground ml-1 text-[10px]">({d.source_type})</span> : null}
                    {model?.source_id === d.source_id ? <span className="ml-auto text-emerald-400 text-[10px]">connected</span> : null}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <ProcessHelp
              testId="semantic-model-help"
              title="Semantic Model"
              intro="Design the governed model your reports and AI run on — tables, fields, relationships and calculated metrics."
              steps={[
                'Click a table to open its fields on the right; use the field search to find a field by its name or display name.',
                'Click a field to set its Semantic Type (dimension / measure), aggregation, PII flag and Data Category (e.g. “Geographic - Country” to enable maps).',
                'Add calculated measures and dimensions, and draw relationships between tables so the AI can join them.',
                'Certify the model to snapshot a governed version that stewards can diff and roll back to.',
              ]}
            />
            {/* Compact status: shrink the badge to a colored dot when the
                model name is long so the toolbar row never wraps. */}
            {model && ((model.name || '').length > 22 ? (
              <Tooltip><TooltipTrigger asChild>
                <span
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${model.status === 'active' ? 'bg-emerald-400' : model.status === 'published' ? 'bg-blue-400' : 'bg-amber-400'}`}
                  data-testid="model-status-dot"
                  aria-label={`Status: ${model.status}`}
                />
              </TooltipTrigger><TooltipContent><p className="text-xs capitalize">{model.status}</p></TooltipContent></Tooltip>
            ) : (
              <Badge className="text-[8px] bg-emerald-500/15 text-emerald-400 flex-shrink-0" data-testid="model-status-badge">{model.status}</Badge>
            ))}
          </div>
          <div className="flex-1 min-w-[8px]" />
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* View mode toggle */}
            <div className="flex items-center border border-border/30 rounded-lg overflow-hidden" data-testid="detail-view-toggle">
              <Tooltip><TooltipTrigger asChild>
                <button onClick={() => setDetailViewMode('canvas')} className={`p-1.5 transition-colors ${detailViewMode === 'canvas' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}`} data-testid="view-canvas"><LayoutGrid className="w-3.5 h-3.5" /></button>
              </TooltipTrigger><TooltipContent><p className="text-xs">Canvas View</p></TooltipContent></Tooltip>
              <Tooltip><TooltipTrigger asChild>
                <button onClick={() => setDetailViewMode('table')} className={`p-1.5 transition-colors ${detailViewMode === 'table' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}`} data-testid="view-table"><Table className="w-3.5 h-3.5" /></button>
              </TooltipTrigger><TooltipContent><p className="text-xs">Metadata Table View</p></TooltipContent></Tooltip>
            </div>
            <Tooltip><TooltipTrigger asChild><Button variant="outline" size="sm" onClick={function() { setEditModelOpen(true); }} className="rounded-lg text-xs h-7 px-2"><Pencil className="w-3 h-3 mr-1" />Edit</Button></TooltipTrigger><TooltipContent><p className="text-xs">Edit model name and status</p></TooltipContent></Tooltip>
            {canCertify && (certStatusOf(model) === 'certified' ? (
              <Tooltip><TooltipTrigger asChild><Badge className="text-[10px] h-7 px-2 flex items-center gap-1 bg-emerald-500/15 text-emerald-400 border border-emerald-500/40" data-testid="model-cert-badge"><ShieldCheck className="w-3 h-3" />Certified{model?.certification?.version ? ` v${model.certification.version}` : ''}</Badge></TooltipTrigger><TooltipContent><p className="text-xs">This model bundle is certified{model?.certification?.certified_by_email ? ` by ${model.certification.certified_by_email}` : ''}. Certify a measure again to re-open it.</p></TooltipContent></Tooltip>
            ) : (
              <Tooltip><TooltipTrigger asChild><Button variant="outline" size="sm" onClick={handleCertifyModel} disabled={certifyingModel || !activeModelId} className="rounded-lg text-xs h-7 px-2 border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10" data-testid="certify-model-btn">{certifyingModel ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}Certify model</Button></TooltipTrigger><TooltipContent><p className="text-xs">Bundle-certify this model. Requires every measure on it to be certified first.</p></TooltipContent></Tooltip>
            ))}
            <Tooltip><TooltipTrigger asChild><Button variant="outline" size="sm" onClick={function() { setAddEntityOpen(true); }} className="rounded-lg text-xs h-7 px-2"><Plus className="w-3 h-3 mr-1" />Entity</Button></TooltipTrigger><TooltipContent><p className="text-xs">Add a table/entity from the data source</p></TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild><Button variant="outline" size="sm" onClick={function() { setQueryTableOpen(true); }} className="rounded-lg text-xs h-7 px-2" data-testid="add-query-table-btn"><Code2 className="w-3 h-3 mr-1" />Query</Button></TooltipTrigger><TooltipContent><p className="text-xs">Add a SQL query that behaves like a table</p></TooltipContent></Tooltip>
            {/* All data-prep + metadata actions live in one "Model Tools" menu
                so the toolbar row stays short and Save/Close are never pushed
                off-screen. */}
            <div className="hidden">
            <Tooltip><TooltipTrigger asChild><Button variant="outline" size="sm" onClick={handleAutoDetectPii} disabled={detectingPii} className="rounded-lg text-xs h-7 px-2" data-testid="auto-detect-pii-btn">{detectingPii ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldAlert className="w-3 h-3 mr-1" />}Auto-Detect PII</Button></TooltipTrigger><TooltipContent><p className="text-xs">AI reviews every field and flags personally identifiable information</p></TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild><Button variant="outline" size="sm" onClick={function() { setBulkSel({}); setBulkOpen(true); }} className="rounded-lg text-xs h-7 px-2" data-testid="bulk-classify-btn"><CheckSquare className="w-3 h-3 mr-1" />Bulk Classify</Button></TooltipTrigger><TooltipContent><p className="text-xs">Set Semantic Category or PII on many fields at once</p></TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild><Button variant="outline" size="sm" onClick={handleAutoDiscover} disabled={autoDiscovering || !model || !model.source_id} className="rounded-lg text-xs h-7 px-2" data-testid="auto-discover-btn">{autoDiscovering ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}Discover All</Button></TooltipTrigger><TooltipContent><p className="text-xs">Auto-discover all tables from the data source</p></TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild><Button variant="outline" size="sm" onClick={handleAiClassify} disabled={aiClassifying || entities.length === 0} className="rounded-lg text-xs h-7 px-2" data-testid="ai-classify-btn">{aiClassifying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}AI Classify</Button></TooltipTrigger><TooltipContent><p className="text-xs">Use AI to classify fields as dimensions or measures</p></TooltipContent></Tooltip>
            <Tooltip><TooltipTrigger asChild><Button
              variant="outline" size="sm"
              onClick={async () => {
                if (!model?.model_id) return;
                try {
                  const r = await api.autoFormatModelDisplayNames(model.model_id, false);
                  if (r.total === 0) toast.info('No blank display names to fill');
                  else toast.success(`Filled ${r.total} display name${r.total === 1 ? '' : 's'} using regex conventions`);
                  await loadModel(model.model_id);
                } catch (err) { toast.error(err.message || 'Auto-format failed'); }
              }}
              disabled={entities.length === 0}
              className="rounded-lg text-xs h-7 px-2"
              data-testid="auto-format-names-btn"
            ><Wand2 className="w-3 h-3 mr-1" />Auto-Format</Button></TooltipTrigger><TooltipContent><p className="text-xs">Regex-fill blank Display Names (snake_case, camelCase, abbreviations, acronyms)</p></TooltipContent></Tooltip>
            </div>
            {/* Model Tools — always-visible menu holding data-prep + metadata */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-lg text-xs h-7 px-2 gap-1" data-testid="toolbar-more-btn">
                  <Wand2 className="w-3 h-3" />
                  Model Tools
                  <ChevronDown className="w-3 h-3 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Enhance with AI</DropdownMenuLabel>
                <DropdownMenuItem onSelect={handleAutoDetectPii} disabled={detectingPii} data-testid="toolbar-more-detect-pii">
                  {detectingPii ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5 mr-2" />}
                  Auto-Detect PII
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setPiiFixOpen(true)} data-testid="toolbar-more-fix-pii-consistency">
                  <ShieldAlert className="w-3.5 h-3.5 mr-2" />
                  Fix PII Inconsistencies
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => { setBulkSel({}); setBulkOpen(true); }} data-testid="toolbar-more-bulk-classify">
                  <CheckSquare className="w-3.5 h-3.5 mr-2" />
                  Bulk Classify
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleAutoDiscover} disabled={autoDiscovering || !model || !model.source_id} data-testid="toolbar-more-discover">
                  {autoDiscovering ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-2" />}
                  Discover All
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleAiClassify} disabled={aiClassifying || entities.length === 0} data-testid="toolbar-more-ai-classify">
                  {aiClassifying ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-2" />}
                  AI Classify
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={async () => {
                    if (!model?.model_id) return;
                    try {
                      const r = await api.autoFormatModelDisplayNames(model.model_id, false);
                      if (r.total === 0) toast.info('No blank display names to fill');
                      else toast.success(`Filled ${r.total} display name${r.total === 1 ? '' : 's'} using regex conventions`);
                      await loadModel(model.model_id);
                    } catch (err) { toast.error(err.message || 'Auto-format failed'); }
                  }}
                  disabled={entities.length === 0}
                  data-testid="toolbar-more-auto-format-names"
                >
                  <Wand2 className="w-3.5 h-3.5 mr-2" />
                  Auto-Format Display Names
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Schema</DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={async () => {
                    if (!model?.model_id || !model?.source_id || syncing) return;
                    setSyncing(true);
                    try {
                      const r = await api.syncModelSchema(model.model_id);
                      setSyncResult(r);
                      await loadModel(model.model_id);
                    } catch (err) {
                      toast.error(err.message || 'Synchronize failed');
                    } finally { setSyncing(false); }
                  }}
                  disabled={syncing || !model || !model.source_id}
                  data-testid="toolbar-more-sync-schema"
                >
                  {syncing ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-2" />}
                  Synchronize with Warehouse
                </DropdownMenuItem>
                {activeModelId && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Field Metadata</DropdownMenuLabel>
                    <DropdownMenuItem data-testid="toolbar-more-export-metadata" onSelect={async () => {
                      try {
                        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/semantic/models/${activeModelId}/metadata/export`, { credentials: 'include' });
                        if (!resp.ok) throw new Error(await resp.text());
                        const blob = await resp.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = `${(model?.name || 'model').replace(/\s+/g, '_')}_metadata.csv`;
                        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                        toast.success('Metadata exported');
                      } catch (e) { toast.error(e.message || 'Export failed'); }
                    }}>
                      <Download className="w-3.5 h-3.5 mr-2" />Export Metadata CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="toolbar-more-import-metadata" onSelect={() => metadataFileRef.current?.click()}>
                      <Upload className="w-3.5 h-3.5 mr-2" />Import Metadata CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="toolbar-more-recompute" disabled={recomputing} onSelect={async () => {
                      setRecomputing(true);
                      try {
                        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/semantic/models/${activeModelId}/recompute-measures`, { method: 'POST', credentials: 'include' });
                        if (!resp.ok) throw new Error(await resp.text());
                        const out = await resp.json();
                        if (out.measures_recomputed === 0) toast.info(out.message || 'No row-level measures to recompute');
                        else toast.success(`Recomputed ${out.measures_recomputed} measure(s) across ${out.rows_updated} row(s)`);
                      } catch (err) { toast.error(err.message || 'Recompute failed'); }
                      finally { setRecomputing(false); }
                    }}>
                      {recomputing ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-2" />}Recompute Measures
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Schema sync result */}
            <Dialog open={!!syncResult} onOpenChange={(v) => { if (!v) setSyncResult(null); }}>
              <DialogContent className="sm:max-w-md" data-testid="sync-schema-result-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-primary" /> Synchronized with warehouse</DialogTitle>
                  <DialogDescription>
                    The model's tables now match the data warehouse. Entities were not changed.
                  </DialogDescription>
                </DialogHeader>
                {syncResult && (
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <div className="flex-1 rounded-xl border border-border/50 p-3 text-center" data-testid="sync-added-count">
                        <div className="text-2xl font-bold text-emerald-500">{syncResult.fields_added}</div>
                        <div className="text-xs text-muted-foreground">Fields added</div>
                      </div>
                      <div className="flex-1 rounded-xl border border-border/50 p-3 text-center" data-testid="sync-removed-count">
                        <div className="text-2xl font-bold text-rose-500">{syncResult.fields_removed}</div>
                        <div className="text-xs text-muted-foreground">Fields removed</div>
                      </div>
                    </div>
                    {syncResult.fields_added === 0 && syncResult.fields_removed === 0 ? (
                      <p className="text-sm text-muted-foreground text-center">Already up to date — no changes.</p>
                    ) : (
                      <div className="max-h-56 overflow-auto space-y-2">
                        {(syncResult.details || []).map((d, i) => (
                          <div key={i} className="rounded-lg border border-border/40 p-2 text-xs">
                            <div className="font-medium text-foreground mb-1">{d.entity} <span className="text-muted-foreground">({d.table})</span></div>
                            {d.added > 0 && <div className="text-emerald-600 dark:text-emerald-400">+ {d.added}: {(d.added_fields || []).join(', ')}</div>}
                            {d.removed > 0 && <div className="text-rose-600 dark:text-rose-400">− {d.removed}: {(d.removed_fields || []).join(', ')}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    {(syncResult.skipped_tables || []).length > 0 && (
                      <p className="text-[11px] text-amber-500">
                        Not found in the warehouse (left unchanged): {syncResult.skipped_tables.join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </DialogContent>
            </Dialog>
            {(() => {
              const LAYOUT_OPTS = [
                { value: 'star',     label: 'Star schema',         desc: 'Hub center, dims around', Icon: Star },
                { value: 'vertical', label: 'Vertical hierarchy',  desc: 'Hub top, dims fan down', Icon: AlignVerticalSpaceBetween },
                { value: 'force',    label: 'Force-directed',      desc: 'Spring physics — cards repel', Icon: Atom },
              ];
              const applyLayout = async (styleValue) => {
                if (layoutLoading) return; // ignore clicks while a layout is computing
                setLayoutLoading(true);
                try {
                  const res = await api.autoLayoutModel(activeModelId, styleValue);
                  setLastLayoutStyle(styleValue);
                  await loadModel(activeModelId);
                  toast.success(res?.message || `${LAYOUT_OPTS.find(o => o.value === styleValue)?.label || 'Layout'} applied`);
                } catch (ex) { toast.error(ex.message); }
                finally { setLayoutLoading(false); }
              };
              const activeOpt = LAYOUT_OPTS.find(o => o.value === lastLayoutStyle) || LAYOUT_OPTS[0];
              const ActiveIcon = activeOpt.Icon;
              return (
                <div className="inline-flex items-stretch rounded-lg overflow-hidden border border-border bg-background">
                  {/* Main action: re-apply the last-used layout in one click */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => applyLayout(lastLayoutStyle)}
                        disabled={layoutLoading}
                        className="flex items-center gap-1 text-xs h-7 px-2 hover:bg-muted/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="auto-layout-btn"
                      >
                        {layoutLoading ? <Loader2 className="w-3 h-3 text-primary animate-spin" /> : <ActiveIcon className="w-3 h-3 text-primary" />}
                        {/* Label collapses at narrower widths:
                              md: icon only
                              lg: icon + "Layout"
                              xl+: icon + "Auto Layout · Star"           */}
                        {layoutLoading ? (
                          <span className="hidden lg:inline">Updating…</span>
                        ) : (
                          <>
                            <span className="hidden lg:inline xl:hidden">Layout</span>
                            <span className="hidden xl:inline">Auto Layout</span>
                            <span className="hidden xl:inline text-muted-foreground/70">·</span>
                            <span className="hidden xl:inline text-muted-foreground">{activeOpt.label.split(' ')[0]}</span>
                          </>
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent><p className="text-xs">{layoutLoading ? 'Applying layout…' : `Re-apply last layout (${activeOpt.label})`}</p></TooltipContent>
                  </Tooltip>
                  {/* Chevron: opens the style picker */}
                  <DropdownMenu>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            disabled={layoutLoading}
                            className="flex items-center px-1.5 h-7 border-l border-border hover:bg-muted/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="auto-layout-menu-btn"
                            aria-label="Pick layout style"
                          >
                            <ChevronDown className="w-3 h-3 opacity-70" />
                          </button>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      <TooltipContent><p className="text-xs">Pick layout style</p></TooltipContent>
                    </Tooltip>
                    <DropdownMenuContent align="end" className="w-60">
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">Layout style</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {LAYOUT_OPTS.map(opt => {
                        const Icon = opt.Icon;
                        const isActive = opt.value === lastLayoutStyle;
                        return (
                          <DropdownMenuItem
                            key={opt.value}
                            onClick={() => applyLayout(opt.value)}
                            data-testid={`layout-${opt.value}`}
                            className="flex items-start gap-2 py-2 cursor-pointer"
                          >
                            <Icon className="w-3.5 h-3.5 mt-0.5 text-primary flex-shrink-0" />
                            <div className="flex flex-col flex-1 min-w-0">
                              <span className="text-xs font-medium flex items-center gap-1.5">
                                {opt.label}
                                {isActive && <span className="text-[9px] text-primary/80 font-normal">· last used</span>}
                              </span>
                              <span className="text-[10px] text-muted-foreground leading-tight">{opt.desc}</span>
                            </div>
                            {isActive && <CheckCircle2 className="w-3 h-3 text-primary flex-shrink-0 mt-1" />}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })()}
            {activeModelId && <VersionHistoryButton resourceType="semantic_model" resourceId={activeModelId} resourceName={model?.name || ''} onRestored={() => loadModel(activeModelId)} />}
            <input
              ref={metadataFileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file || !activeModelId) return;
                e.target.value = '';
                try {
                  // Dry-run first so we can show a compact before/after
                  // preview instead of a plain "N changes" confirm.
                  const fd = new FormData(); fd.append('file', file);
                  const dryResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/semantic/models/${activeModelId}/metadata/import?dry_run=true`, { method: 'POST', credentials: 'include', body: fd });
                  if (!dryResp.ok) throw new Error(await dryResp.text());
                  const dry = await dryResp.json();
                  if (dry.total_changes === 0) {
                    toast.info(`No changes detected${dry.errors?.length ? ` (${dry.errors.length} row errors)` : ''}`);
                    return;
                  }
                  setMetadataDiff(dry);
                  setPendingImportFile(file);
                } catch (err) {
                  toast.error(err.message || 'Metadata import failed');
                }
              }}
              data-testid="metadata-file-input"
            />
            <div className="w-px h-5 bg-border/60 mx-1 flex-shrink-0" />
            <SaveSplitButton
              onSave={() => setSaveDialogOpen(true)}
              onSaveAs={handleSaveAsModel}
              saving={saving}
              disabled={saving}
              saveAsDisabled={savingAs || !activeModelId}
              testIdPrefix="save-model"
              title="Save all model changes (⌘S)"
              saveAsTitle="Duplicate this model under a new name (⌘⇧S)"
            />
            <Tooltip><TooltipTrigger asChild><Button size="sm" variant="ghost" onClick={closeModelSession} className="rounded-lg text-xs h-7 px-2 text-muted-foreground hover:text-foreground active:scale-95" data-testid="close-model-btn"><X className="w-3.5 h-3.5 mr-1" />Close</Button></TooltipTrigger><TooltipContent><p className="text-xs">Close this model (prompts if unsaved)</p></TooltipContent></Tooltip>
          </div>
        </div>
      {validationWarnings.length > 0 && (
        <div className="px-4 py-1 bg-amber-500/5 border-b border-amber-500/20 relative">
          <button onClick={() => setWarningsOpen(w => !w)} className="flex items-center gap-2 hover:bg-amber-500/10 rounded px-2 py-0.5 transition-colors" data-testid="warnings-toggle">
            <AlertTriangle className="w-3 h-3 text-amber-400" />
            <span className="text-[9px] text-amber-400/80">{validationWarnings.length} warning(s)</span>
            <svg className={`w-2.5 h-2.5 text-amber-400/60 transition-transform ${warningsOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7" /></svg>
          </button>
          {warningsOpen && (
            <div className="absolute left-4 top-full z-50 mt-0.5 w-80 glass-card rounded-xl border border-amber-500/20 shadow-2xl p-3 space-y-1.5" data-testid="warnings-panel">
              <p className="text-[9px] text-amber-400 uppercase tracking-wider font-semibold mb-1">Model Warnings</p>
              {validationWarnings.map((w, i) => (
                <div key={`${w.entity || 'na'}-${w.msg}-${i}`} className="flex items-start gap-2 p-2 rounded-lg bg-amber-500/5 border border-amber-500/10" data-testid={`warning-${i}`}>
                  <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] font-medium text-foreground">{w.entity}</p>
                    <p className="text-[9px] text-muted-foreground">{w.msg}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Canvas + Right Panel / Table View */}
      {detailViewMode === 'table' ? (
        <MetadataTableView entities={entities} setEntities={setEntities} modelId={activeModelId} model={model} setModel={setModel} calcMeasures={calcMeasures} setCalcMeasures={setCalcMeasures} modelGlossary={modelGlossary} setModelGlossary={setModelGlossary} onEntityDeleted={(id) => { setRelationships(prev => prev.filter(r => r.source_entity_id !== id && r.target_entity_id !== id)); if (selectedEntity === id) setSelectedEntity(null); }} />
      ) : (
      <div className="flex flex-col flex-1 overflow-hidden">
      <div className="flex flex-1 overflow-hidden min-h-0">
      {/* canvas + entity field-list sidebar */}
        <div className="flex-1 overflow-hidden relative" data-canvas="true"
          onWheel={function(e) { e.preventDefault(); let delta = e.deltaY > 0 ? -0.1 : 0.1; setCanvasZoom(function(z) { return Math.max(0.3, Math.min(2, z + delta)); }); }}
          onMouseDown={function(e) { if (e.target === e.currentTarget || e.target.tagName === 'svg' || e.target.tagName === 'DIV' && !e.target.closest('[data-testid^="entity-"]')) { if (!draggingEntity && !connectMode) { setIsPanning(true); setPanStart({ x: e.clientX - canvasPan.x, y: e.clientY - canvasPan.y }); } } }}
          onMouseMove={function(e) {
            if (isPanning) { setCanvasPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y }); return; }
            if (draggingEntity) { handleEntityDrag(e); return; }
            if (connectMode) {
              // Translate viewport coords into canvas-space so the preview
              // line tracks the cursor under pan + zoom.
              const c = e.currentTarget.closest('[data-canvas]'); if (!c) return;
              const cr = c.getBoundingClientRect();
              const x = (e.clientX - cr.left - canvasPan.x) / canvasZoom;
              const y = (e.clientY - cr.top - canvasPan.y) / canvasZoom;
              setConnectMouse({ x, y });
            }
          }}
          onMouseUp={function() {
            setIsPanning(false);
            handleEntityDragEnd();
            // If we were dragging a connection and released over a target
            // entity (hover state set by the per-card onMouseEnter), open
            // the Add Relationship dialog pre-filled. Otherwise just cancel.
            if (connectMode) {
              if (connectHover && connectSource && connectHover !== connectSource) {
                const sn = entities.find(en => en.entity_id === connectSource);
                const tn = entities.find(en => en.entity_id === connectHover);
                setRelSource(connectSource); setRelTarget(connectHover);
                setRelName((sn ? sn.name : '') + ' to ' + (tn ? tn.name : ''));
                setRelMappings([{ source: '', target: '' }]);
                setAddRelOpen(true);
              }
              setConnectMode(false); setConnectSource(null);
              setConnectMouse(null); setConnectHover(null);
            }
          }}
          onMouseLeave={function() {
            setIsPanning(false); handleEntityDragEnd();
            // Leaving the canvas mid-drag cancels the in-flight connection.
            if (connectMode) { setConnectMode(false); setConnectSource(null); setConnectMouse(null); setConnectHover(null); }
          }}
          style={{ cursor: isPanning ? 'grabbing' : (draggingEntity ? 'grabbing' : 'default') }}>
          {/* Zoom controls */}
          <div className="absolute top-3 right-3 z-10 flex items-center gap-1 glass-card rounded-lg px-1.5 py-1 border border-border/30">
            <button onClick={function() { setCanvasZoom(function(z) { return Math.max(0.3, z - 0.15); }); }} className="px-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground rounded hover:bg-muted/30">-</button>
            <span className="text-[9px] text-muted-foreground w-10 text-center">{Math.round(canvasZoom * 100)}%</span>
            <button onClick={function() { setCanvasZoom(function(z) { return Math.min(2, z + 0.15); }); }} className="px-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground rounded hover:bg-muted/30">+</button>
            <button onClick={function() { setCanvasZoom(1); setCanvasPan({ x: 0, y: 0 }); }} className="px-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground rounded hover:bg-muted/30 ml-1">Reset</button>
          </div>
          {/* Table search + opt-in join detection */}
          <div className="absolute top-3 left-3 z-20 flex items-center gap-2">
            <form onSubmit={function(e) { e.preventDefault(); handleTableSearch(tableSearch); }} className="flex items-center gap-1 rounded-lg border border-border bg-popover px-2 py-1 shadow-md">
              <Search className="w-3.5 h-3.5 text-muted-foreground" />
              <input list="model-table-list" value={tableSearch}
                onChange={function(e) { const v = e.target.value; setTableSearch(v); const lv = v.toLowerCase(); const exact = entities.find(function(en) { return (en.name || '').toLowerCase() === lv; }); if (exact) { centerOnEntity(exact); return; } for (const en of entities) { const a = (en.attributes || []).find(function(at) { return _fieldLabels(at).some(function(s) { return s === lv; }); }); if (a) { centerOnEntity(en); break; } } }}
                placeholder="Find table or field…" className="bg-transparent text-xs outline-none w-36 text-foreground placeholder:text-muted-foreground" data-testid="table-search-input" />
              <datalist id="model-table-list">
                {entities.map(function(e) { return <option key={e.entity_id} value={e.name} label={'Table · ' + ((e.attributes || []).length) + ' fields'} />; })}
                {entities.flatMap(function(e) { return (e.attributes || []).map(function(a) { return <option key={e.entity_id + ':' + a.name} value={a.business_name || a.display_name || a.name} label={'Field · ' + e.name} />; }); })}
              </datalist>
            </form>
            <Tooltip><TooltipTrigger asChild>
              <Button size="sm" onClick={handleDetectJoins} disabled={detectingJoins || !activeModelId} className="rounded-lg text-xs h-7 px-2 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md" data-testid="detect-joins-btn">
                {detectingJoins ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}Auto-Detect Joins
              </Button>
            </TooltipTrigger><TooltipContent><p className="text-xs">Scan tables for shared keys and suggest join relationships</p></TooltipContent></Tooltip>
          </div>
          {/* Join health warnings — existing relationships whose key values barely overlap */}
          {joinWarnings.length > 0 && (
            <div className="absolute top-3 right-3 z-10 max-w-xs glass-card rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 shadow-lg" data-testid="join-health-banner">
              <p className="text-xs font-semibold text-amber-400 flex items-center gap-1.5 mb-1.5">
                <ShieldAlert className="w-3.5 h-3.5" /> {joinWarnings.length} weak join{joinWarnings.length === 1 ? '' : 's'}
              </p>
              <div className="space-y-1 max-h-40 overflow-auto">
                {joinWarnings.map((w, i) => (
                  <div key={i} className="text-[10px] text-foreground/80" data-testid={`join-warning-${i}`}>
                    <span className="font-medium">{w.source_entity_name} ↔ {w.target_entity_name}</span> on {w.join_column}:
                    <span className="text-amber-400"> {w.status === 'none' ? 'no matching values' : `only ${w.overlap_pct}% of values match`}</span> — results may be empty or incomplete.
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Auto-join suggestions (deterministic shared-key detection) */}
          {joinSuggestions.length > 0 && !dismissedJoins && (
            <div className="absolute top-16 left-3 z-10 max-w-md glass-card rounded-xl border border-primary/30 bg-primary/5 p-3 shadow-lg" data-testid="join-suggestions-banner">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Wand2 className="w-3.5 h-3.5 text-primary" />
                  {joinSuggestions.length} suggested join{joinSuggestions.length === 1 ? '' : 's'}
                </p>
                <button onClick={() => setDismissedJoins(true)} className="text-muted-foreground hover:text-foreground text-xs" data-testid="dismiss-join-suggestions">Dismiss</button>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-auto">
                {joinSuggestions.map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`join-suggestion-${i}`}>
                    <div className="min-w-0">
                      <span className="truncate text-foreground/90 block">
                        <span className="font-medium">{s.source_entity_name}</span> ↔ <span className="font-medium">{s.target_entity_name}</span>
                        <span className={`ml-1.5 text-[9px] px-1 py-0.5 rounded ${s.confidence === 'high' ? 'bg-emerald-500/15 text-emerald-400' : s.confidence === 'low' ? 'bg-muted text-muted-foreground' : 'bg-amber-500/15 text-amber-400'}`}>{s.confidence}</span>
                      </span>
                      <span className="text-[10px] text-muted-foreground block truncate" title={s.reason}>{s.reason || `on ${s.join_column}`}</span>
                    </div>
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] flex-shrink-0"
                      disabled={applyingJoin === (s.source_entity_id + s.target_entity_id)}
                      onClick={() => applyJoinSuggestion(s)} data-testid={`apply-join-${i}`}>
                      {applyingJoin === (s.source_entity_id + s.target_entity_id) ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add join'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <CanvasMemo entities={entities} relationships={relationships} canvasPan={canvasPan} canvasZoom={canvasZoom} connectMode={connectMode} connectSource={connectSource} connectSide={connectSide} connectHover={connectHover} connectMouse={connectMouse} selectedEntity={selectedEntity} draggingEntity={draggingEntity} maxX={maxX} maxY={maxY}>
          <div className="relative p-6 origin-top-left" style={{ transform: 'translate(' + canvasPan.x + 'px, ' + canvasPan.y + 'px) scale(' + canvasZoom + ')', width: maxX + 200, height: maxY + 200, minWidth: '200%', minHeight: '200%' }}>
            <svg className="absolute inset-0" width={maxX + 200} height={maxY + 200} style={{ pointerEvents: 'none' }}>
              {/* Relationship lines — strokeDasharray driven by line_style;
                stroke color driven by line_color (active-state only —
                inactive relationships always stay muted). Default falls
                back to the original blue when no color is persisted. */}
            {relationships.map(function(rel) { let s = entities.find(function(e) { return e.entity_id === rel.source_entity_id; }); let t = entities.find(function(e) { return e.entity_id === rel.target_entity_id; }); if (!s || !t) return null; let ep = nearestEndpoints(s, t); let sx = ep.sx; let sy = ep.sy; let tx = ep.tx; let ty = ep.ty; let mx = (sx + tx) / 2; let my = (sy + ty) / 2; let colorByName = { default: 'rgba(59,130,246,0.55)', cyan: 'rgba(6,182,212,0.65)', emerald: 'rgba(16,185,129,0.65)', amber: 'rgba(245,158,11,0.7)', violet: 'rgba(139,92,246,0.65)', pink: 'rgba(236,72,153,0.65)', slate: 'rgba(148,163,184,0.6)', red: 'rgba(239,68,68,0.65)' }; let activeColor = colorByName[rel.line_color] || 'rgba(59,130,246,0.25)'; let endpointFill = (colorByName[rel.line_color] || 'rgba(59,130,246,0.4)').replace(/[\d.]+\)$/, '0.85)'); let lineColor = rel.is_active === false ? 'hsl(var(--muted-foreground) / 0.15)' : activeColor; let isDark = document.documentElement.classList.contains('dark'); let boxFill = isDark ? 'rgba(9,9,11,0.85)' : 'rgba(255,255,255,0.95)'; let boxStroke = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.12)'; let textFill = isDark ? '#a1a1aa' : '#71717a'; let dashByStyle = { solid: '', dashed: '6 3', dotted: '2 4', dashed_thin: '3 5' }; let dash = rel.is_active === false ? '2 4' : (dashByStyle[rel.line_style] !== undefined ? dashByStyle[rel.line_style] : '6 3'); return (<g key={rel.relationship_id}><line x1={sx} y1={sy} x2={tx} y2={ty} stroke={lineColor} strokeWidth="2" strokeDasharray={dash} strokeLinecap="round" /><circle cx={sx} cy={sy} r="3" fill={endpointFill} opacity="0.65" /><circle cx={tx} cy={ty} r="3" fill={endpointFill} opacity="0.65" /><rect x={mx - 35} y={my - 15} width="70" height="28" rx="6" fill={boxFill} stroke={boxStroke} style={{ pointerEvents: 'all', cursor: 'context-menu' }} onContextMenu={function(e) { e.preventDefault(); e.stopPropagation(); setRelContextMenu({ x: e.clientX, y: e.clientY, rel: rel }); }} /><text x={mx} y={my - 3} textAnchor="middle" fontSize="7" fill={textFill} fontFamily="monospace" style={{ pointerEvents: 'none' }}>{(rel.cardinality || '').replace('_', ':')}</text><text x={mx} y={my + 8} textAnchor="middle" fontSize="6" fill="#6366f1" fontFamily="monospace" style={{ pointerEvents: 'none' }}>{rel.join_type || 'inner'}</text></g>); })}
            {/* In-flight drag-to-connect preview — emerald dashed straight
                line from the anchored endpoint to the live cursor, with a
                destination dot that fills when over a valid drop target. */}
            {connectMode && connectMouse && connectSource && (() => {
              const s = entities.find(en => en.entity_id === connectSource);
              if (!s) return null;
              const sx = (s.position ? s.position.x : 0) + (connectSide === 'right' ? 260 : 0);
              const sy = (s.position ? s.position.y : 0) + 40;
              const tx = connectMouse.x;
              const ty = connectMouse.y;
              const hasTarget = !!connectHover;
              const stroke = hasTarget ? 'rgba(16,185,129,0.85)' : 'rgba(16,185,129,0.55)';
              return (
                <g style={{ pointerEvents: 'none' }} data-testid="connect-preview">
                  <line x1={sx} y1={sy} x2={tx} y2={ty}
                    stroke={stroke} strokeWidth="2.5" strokeDasharray="6 4" strokeLinecap="round" />
                  <circle cx={sx} cy={sy} r="5" fill="rgba(16,185,129,0.95)" />
                  <circle cx={tx} cy={ty} r={hasTarget ? 6 : 4}
                    fill={hasTarget ? 'rgba(16,185,129,0.95)' : 'none'}
                    stroke="rgba(16,185,129,0.95)" strokeWidth="2" />
                </g>
              );
            })()}
            </svg>
            {visibleEntities.map(function(entity) { let colors = ENTITY_COLORS[entity.entity_type] || ENTITY_COLORS.fact; let ms = (entity.attributes || []).filter(function(a) { return a.semantic_type === 'measure'; }); let ds = (entity.attributes || []).filter(function(a) { return a.semantic_type === 'dimension'; }); let isSel = selectedEntity === entity.entity_id; return (
              <div key={entity.entity_id}
                className={'absolute glass-card rounded-2xl p-3 w-[260px] transition-shadow ' + (isSel ? 'ring-2 ring-primary border-primary/30' : 'hover:border-white/20') + (connectSource === entity.entity_id ? ' ring-2 ring-emerald-500' : '') + (connectMode && connectHover === entity.entity_id && connectSource !== entity.entity_id ? ' ring-2 ring-emerald-400 shadow-[0_0_24px_rgba(16,185,129,0.35)]' : '') + ' cursor-grab'}
                style={{ left: entity.position ? entity.position.x : 0, top: entity.position ? entity.position.y : 0 }}
                onMouseDown={function(e) { if (e.target.dataset.connectPoint) return; e.stopPropagation(); handleEntityDragStart(e, entity); }}
                onMouseUp={function() { handleEntityDragEnd(); }}
                onMouseEnter={function() { if (connectMode && connectSource && entity.entity_id !== connectSource) setConnectHover(entity.entity_id); }}
                onMouseLeave={function() { if (connectMode && connectHover === entity.entity_id) setConnectHover(null); }}
                onClick={function() { if (!draggingEntity) handleEntityClick(entity.entity_id); }}
                onContextMenu={function(e) { e.preventDefault(); e.stopPropagation(); setEntityContextMenu({ x: e.clientX, y: e.clientY, entity: entity }); }}
                data-testid={'entity-' + entity.entity_id}>
                {/* Connection points — drag from either side to another card to create a join */}
                <div data-connect-point="right" className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-primary/20 border-2 border-primary/40 hover:bg-primary/40 hover:border-primary cursor-crosshair z-10 transition-colors" title="Drag to connect" onMouseDown={function(e) { e.stopPropagation(); setConnectSource(entity.entity_id); setConnectSide('right'); setConnectMode(true); setConnectMouse({ x: (entity.position ? entity.position.x : 0) + 260, y: (entity.position ? entity.position.y : 0) + 40 }); }} />
                <div data-connect-point="left"  className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-primary/20 border-2 border-primary/40 hover:bg-primary/40 hover:border-primary cursor-crosshair z-10 transition-colors" title="Drag to connect" onMouseDown={function(e) { e.stopPropagation(); setConnectSource(entity.entity_id); setConnectSide('left');  setConnectMode(true); setConnectMouse({ x: (entity.position ? entity.position.x : 0),       y: (entity.position ? entity.position.y : 0) + 40 }); }} />
                <div className="flex items-center gap-2 mb-1.5"><div className={'p-1 rounded-md ' + colors.bg + ' border ' + colors.border}><Table2 className={'w-3.5 h-3.5 ' + colors.text} /></div><h3 className="text-[11px] font-heading font-semibold text-foreground truncate flex-1">{entity.name}</h3></div>
                {ds.length > 0 && <p className="text-xs text-muted-foreground">Dims: {ds.length} | Measures: {ms.length}</p>}
              </div>); })}
          </div>
          </CanvasMemo>
        </div>
        {/* Right-click context menu for relationships */}
        {relContextMenu && (
          <div className="fixed inset-0 z-50" onClick={function() { setRelContextMenu(null); }} onContextMenu={function(e) { e.preventDefault(); setRelContextMenu(null); }}>
            <div className="glass-card rounded-lg shadow-2xl border border-border/50 py-1 w-36" style={{ position: 'fixed', left: relContextMenu.x, top: relContextMenu.y }} onClick={function(e) { e.stopPropagation(); }}>
              <button onClick={function() { setEditRel(Object.assign({}, relContextMenu.rel)); setEditRelOpen(true); setRelContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/30 transition-colors" data-testid="ctx-edit-rel">
                <Pencil className="w-3 h-3 text-muted-foreground" /> Edit Relationship
              </button>
              <button onClick={function() { handleDeleteRelationship(relContextMenu.rel.relationship_id); setRelContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 transition-colors" data-testid="ctx-delete-rel">
                <Trash2 className="w-3 h-3" /> Delete Relationship
              </button>
            </div>
          </div>
        )}
        {/* Right-click context menu for entities (tables) on the canvas */}
        {entityContextMenu && (
          <div className="fixed inset-0 z-50" onClick={function() { setEntityContextMenu(null); }} onContextMenu={function(e) { e.preventDefault(); setEntityContextMenu(null); }}>
            <div className="glass-card rounded-lg shadow-2xl border border-border/50 py-1 w-44" style={{ position: 'fixed', left: entityContextMenu.x, top: entityContextMenu.y }} onClick={function(e) { e.stopPropagation(); }}>
              <div className="px-3 py-1 border-b border-border/30 mb-1">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider truncate" title={entityContextMenu.entity.name}>{entityContextMenu.entity.name}</p>
              </div>
              <button onClick={function() { handleEntityClick(entityContextMenu.entity.entity_id); setEntityContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/30 transition-colors" data-testid="ctx-open-entity">
                <Pencil className="w-3 h-3 text-muted-foreground" /> Open / Edit
              </button>
              <button onClick={async function() { setEntityContextMenu(null); if (await confirmFn({ title: 'Remove table from model?', message: 'This removes ' + entityContextMenu.entity.name + ' from this semantic model. Related relationships will also be removed. The underlying data source is not modified.', confirmLabel: 'Remove', destructive: true })) { handleDeleteEntity(entityContextMenu.entity.entity_id); } }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 transition-colors" data-testid="ctx-delete-entity">
                <Trash2 className="w-3 h-3" /> Remove from model
              </button>
            </div>
          </div>
        )}
      <div
        onMouseDown={startPropPanelResize}
        title="Drag to resize"
        data-testid="model-prop-panel-resize"
        className="group relative w-1.5 shrink-0 cursor-col-resize bg-transparent hover:bg-primary/40 active:bg-primary/60 transition-colors"
      />
      <div className="border-l border-border/50 flex flex-col overflow-hidden flex-shrink-0" style={{ width: propPanelWidth, background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }}>
        {selectedEntityData ? (
          <React.Fragment>
            <div className="p-3 border-b border-border/50">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-heading font-semibold text-foreground">{selectedEntityData.name}</h3>
                {selectedEntityData.attributes?.length === 0 && model?.source_id && (
                  <Button variant="outline" size="sm" onClick={async () => {
                    try {
                      const res = await api.describeTable(model.source_id, { table_name: selectedEntityData.table_name || selectedEntityData.name });
                      if (res.columns?.length > 0) {
                        const attrs = res.columns.map(c => ({ name: c.name, description: c.name.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()), data_type: c.data_type?.toLowerCase().includes('number') || c.data_type?.toLowerCase().includes('float') || c.data_type?.toLowerCase().includes('int') || c.data_type?.toLowerCase().includes('decimal') ? 'float' : c.data_type?.toLowerCase().includes('date') || c.data_type?.toLowerCase().includes('timestamp') ? 'date' : 'string', semantic_type: c.data_type?.toLowerCase().includes('number') || c.data_type?.toLowerCase().includes('float') || c.data_type?.toLowerCase().includes('decimal') ? 'measure' : 'dimension' }));
                        await api.updateSemanticEntity(selectedEntityData.entity_id, { attributes: attrs });
                        const updated = entities.map(e => e.entity_id === selectedEntityData.entity_id ? { ...e, attributes: attrs } : e);
                        setEntities(updated);
                        toast.success(`Loaded ${attrs.length} columns`);
                      } else { toast.error('No columns found'); }
                    } catch (err) { toast.error(err.message); }
                  }} className="text-xs h-6 gap-1 mt-1"><RefreshCw className="w-3 h-3" /> Load Columns</Button>
                )}
              </div>
              <Input defaultValue={selectedEntityData.entity_description || ''} onBlur={function(e) { api.updateSemanticEntity(selectedEntityData.entity_id, { entity_description: e.target.value }).then(function() { setEntities(function(prev) { return prev.map(function(en) { return en.entity_id === selectedEntityData.entity_id ? Object.assign({}, en, { entity_description: e.target.value }) : en; }); }); }).catch(function() {}); }} placeholder="Describe what this table represents..." className="h-6 !text-xs rounded-lg bg-muted/20 border-border/20 mb-2 text-muted-foreground" />
              <div className="flex items-center justify-between mb-1">
                <div className="flex gap-1">
                  {selectedEntityData.table_type === 'sql_query' && (
                    <Button variant="ghost" size="sm" onClick={function() { setEditQueryEntity(selectedEntityData); setQueryTableOpen(true); }} className="h-6 px-1.5 text-[8px] text-sky-400 hover:text-sky-300 gap-1" data-testid="edit-query-table-btn" title="Edit or re-validate this query's SQL"><Code2 className="w-3 h-3" />Edit Query</Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={function() { setAddMeasureOpen(true); }} className="h-6 px-1.5 text-[8px] text-primary hover:text-primary/80 gap-1" data-testid="add-calc-field-btn" title="Add calculated measure (formula or scoped)"><Plus className="w-3 h-3" />Calc Measure</Button>
                  <Button variant="ghost" size="sm" onClick={function() { setPyramidFormulaOpen(true); }} className="h-6 px-1.5 text-[8px] text-amber-400 hover:text-amber-300 gap-1" data-testid="import-pyramid-formulas-btn" title="Import calculations from Pyramid Analytics"><Pyramid className="w-3 h-3" />Import from Pyramid</Button>
                  <Button variant="ghost" size="sm" onClick={function() { setCalcDimOpen(true); }} className="h-6 px-1.5 text-[8px] text-blue-400 hover:text-blue-300 gap-1" data-testid="add-calc-dim-btn" title="Add calculated dimension (e.g. month of a date)"><Plus className="w-3 h-3" />Calc Dimension</Button>
                  <Button variant="ghost" size="sm" onClick={function() { setPreviewEntity(selectedEntityData); }} className="h-6 px-1.5 text-[8px] text-emerald-400 hover:text-emerald-300 gap-1" data-testid="preview-entity-rows-btn" title="Preview sample rows with all calculations applied"><Table2 className="w-3 h-3" />Preview Rows</Button>
                  <Button variant="ghost" size="icon" onClick={function() { handleDeleteEntity(selectedEntityData.entity_id); }} className="w-6 h-6 text-muted-foreground hover:text-destructive" title="Delete entity"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground">{selectedEntityData.table_name}</p>
              <div className="flex gap-1 mt-1 items-center"><Select value={selectedEntityData.entity_type || 'fact'} onValueChange={function(v) { updateEntityType(selectedEntityData.entity_id, v); }}><SelectTrigger className="h-5 !text-[8px] px-1.5 rounded-full w-auto gap-1 border-border/40 bg-muted/30" data-testid="entity-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="fact" className="text-xs">Fact table</SelectItem><SelectItem value="dimension" className="text-xs">Dimension table</SelectItem></SelectContent></Select><Badge variant="outline" className="text-[8px]">{(selectedEntityData.attributes || []).length} fields</Badge>{cardTotalRows > 0 && <Badge variant="outline" className="text-[8px] border-sky-500/30 text-sky-400">{cardTotalRows.toLocaleString()} rows</Badge>}<button onClick={function() { loadCardinality(true); }} disabled={cardLoading} className="ml-auto p-0.5 rounded hover:bg-muted/50 text-muted-foreground/70 hover:text-foreground transition-all disabled:opacity-40" title="Recompute field cardinality" data-testid="refresh-cardinality-btn"><RefreshCw className={'w-3 h-3 ' + (cardLoading ? 'animate-spin' : '')} /></button></div>
            </div>
            <Tabs value={panelTab} onValueChange={setPanelTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="mx-2 mt-2 grid grid-cols-2 h-8 bg-muted/30 flex-shrink-0">
                <TabsTrigger value="fields" className="text-[10px] h-6" data-testid="panel-tab-fields">Fields ({(selectedEntityData.attributes || []).length})</TabsTrigger>
                <TabsTrigger value="relationships" className="text-[10px] h-6" data-testid="panel-tab-relationships">Relationships ({relationships.filter(function(r) { return r.source_entity_id === selectedEntityData.entity_id || r.target_entity_id === selectedEntityData.entity_id; }).length})</TabsTrigger>
              </TabsList>
              <TabsContent value="fields" className="flex-1 overflow-hidden m-0 mt-0" data-testid="panel-content-fields">
                <ScrollArea className="h-full">
              <div className="p-2 space-y-1">
                <p className="text-[9px] text-muted-foreground uppercase tracking-wider px-1 mb-1">Fields - click to edit</p>
                <div className="relative mb-1.5 px-0.5">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground/60 pointer-events-none" />
                  <Input value={fieldSearch} onChange={function(e) { setFieldSearch(e.target.value); }} placeholder="Search fields by name or display name…" className="h-7 pl-7 !text-xs rounded-lg bg-muted/20 border-border/30" data-testid="field-search-input" />
                </div>
                {fieldSearch.trim() && !(selectedEntityData.attributes || []).some(function(a) { return (((a.name || '') + ' ' + (a.display_name || '') + ' ' + (a.business_name || '')).toLowerCase()).indexOf(fieldSearch.trim().toLowerCase()) !== -1; }) && (
                  <p className="text-[10px] text-muted-foreground px-1 py-2" data-testid="field-search-empty">No fields match “{fieldSearch}”.</p>
                )}
                {(() => { let _shown = 0; return (selectedEntityData.attributes || []).map(function(attr, idx) {
                  if (fieldSearch.trim()) {
                    var _q = fieldSearch.trim().toLowerCase();
                    var _hay = ((attr.name || '') + ' ' + (attr.display_name || '') + ' ' + (attr.business_name || '')).toLowerCase();
                    if (_hay.indexOf(_q) === -1) return null;
                  }
                  if (_shown >= fieldWindow) return null;
                  _shown++;
                  let isEd = editingAttr && editingAttr.entityId === selectedEntityData.entity_id && editingAttr.attrIndex === idx;
                  return (
                    <div key={attr.name + '-' + idx} className={'rounded-lg border transition-all group ' + (isEd ? 'border-primary/30 bg-primary/5 p-2.5' : 'border-border/20 hover:border-border/40 p-2 cursor-pointer')} onClick={function() { if (!isEd) setEditingAttr({ entityId: selectedEntityData.entity_id, attrIndex: idx }); }} data-testid={'field-' + attr.name}>
                      {isEd ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div><span className="text-[10px] text-foreground font-medium">{attr.business_name || attr.display_name || attr.name}</span><p className="text-[8px] font-mono text-muted-foreground/70">{attr.name}</p></div>
                            <button onClick={function(e) { e.stopPropagation(); setEditingAttr(null); }}><X className="w-3 h-3 text-muted-foreground" /></button>
                          </div>
                          <div className="grid grid-cols-2 gap-1.5">
                            <div>
                              <div className="flex items-center h-4">
                                <Label className="text-xs text-muted-foreground">Semantic Type</Label>
                              </div>
                              <Select value={attr.semantic_type || 'dimension'} onValueChange={function(v) { updateAttribute(selectedEntityData.entity_id, idx, { semantic_type: v }); }}>
                                <SelectTrigger className="h-6 text-xs rounded-lg bg-muted/30 border-border/30 mt-0.5"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="dimension">Dimension</SelectItem><SelectItem value="measure">Measure</SelectItem><SelectItem value="hidden">Hidden (Technical)</SelectItem></SelectContent>
                              </Select>
                            </div>
                            <div>
                              <div className="flex items-center h-4 gap-1">
                                <Label className="text-xs text-muted-foreground">{attr.semantic_type === 'measure' ? 'Default Aggregation' : 'Summarize As'}</Label>
                                {(attr.data_type === 'float' || attr.data_type === 'integer') && (
                                  <Tooltip><TooltipTrigger asChild>
                                    <button onClick={function(e) { e.stopPropagation(); setDerivedSourceField(attr.name); setDerivedAgg('avg'); let prefix = 'Average'; setDerivedName(prefix + ' ' + (attr.business_name || attr.display_name || attr.name)); setDerivedDesc(''); setDerivedFormat(''); setDerivedMeasureOpen(true); }} className="text-indigo-400 hover:text-indigo-300 transition-colors" data-testid={'add-derived-' + attr.name}><Plus className="w-3 h-3" /></button>
                                  </TooltipTrigger><TooltipContent side="top"><p className="text-xs">Add derived measure</p></TooltipContent></Tooltip>
                                )}
                              </div>
                              <Select value={attr.aggregation || (attr.semantic_type === 'measure' ? 'sum' : 'none')} onValueChange={function(v) { updateAttribute(selectedEntityData.entity_id, idx, { aggregation: v }); }}>
                                <SelectTrigger className="h-6 text-xs rounded-lg bg-muted/30 border-border/30 mt-0.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Don't Summarize</SelectItem>
                                  {(attr.data_type === 'float' || attr.data_type === 'integer') && <SelectItem value="sum">Sum</SelectItem>}
                                  {(attr.data_type === 'float' || attr.data_type === 'integer') && <SelectItem value="avg">Average</SelectItem>}
                                  <SelectItem value="count">Count</SelectItem>
                                  {(attr.data_type === 'float' || attr.data_type === 'integer') && <SelectItem value="min">Min</SelectItem>}
                                  {(attr.data_type === 'float' || attr.data_type === 'integer') && <SelectItem value="max">Max</SelectItem>}
                                  <SelectItem value="count_distinct">Count Distinct</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {/* PII classification (Data Category is set below; Semantic Category removed as redundant) */}
                          <div className="flex items-end gap-2">
                            <button
                              onClick={function() { updateAttribute(selectedEntityData.entity_id, idx, { pii: !attr.pii }); }}
                              className={'h-6 px-2 mb-0.5 rounded-lg border text-[10px] flex items-center gap-1 transition-colors ' + (attr.pii ? 'border-rose-500/50 bg-rose-500/15 text-rose-300' : 'border-border/30 bg-muted/30 text-muted-foreground hover:text-foreground')}
                              data-testid={'attr-pii-' + attr.name}
                              title="Mark this field as personally identifiable information">
                              <ShieldAlert className="w-3 h-3" /> PII{attr.pii ? ' ✓' : ''}
                            </button>
                          </div>
                          {attr.semantic_type === 'measure' && (
                          <div>
                            <div className="flex items-center h-4 gap-1">
                              <Label className="text-xs text-muted-foreground">Time Aggregation</Label>
                              <Tooltip><TooltipTrigger><AlertTriangle className="w-2.5 h-2.5 text-muted-foreground/70" /></TooltipTrigger><TooltipContent side="top" className="max-w-[200px]"><p className="text-xs">How this measure aggregates across time periods. Use "Latest" for point-in-time values like outstanding reserves or inforce counts.</p></TooltipContent></Tooltip>
                            </div>
                            <Select value={attr.time_aggregation || 'sum'} onValueChange={function(v) { updateAttribute(selectedEntityData.entity_id, idx, { time_aggregation: v }); }}>
                              <SelectTrigger className="h-6 text-xs rounded-lg bg-muted/30 border-border/30 mt-0.5"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sum">Sum (additive over time)</SelectItem>
                                <SelectItem value="latest">Latest (point-in-time snapshot)</SelectItem>
                                <SelectItem value="first">First (earliest value)</SelectItem>
                                <SelectItem value="none">None (don't aggregate across time)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          )}
                          {/* Display unit — heuristic default with manual override pill */}
                          {attr.semantic_type === 'measure' && (
                          <div>
                            <div className="flex items-center h-4 gap-1">
                              <Label className="text-xs text-muted-foreground">Display Unit</Label>
                              <Tooltip><TooltipTrigger><Info className="w-2.5 h-2.5 text-muted-foreground/70" /></TooltipTrigger><TooltipContent side="top" className="max-w-[220px]"><p className="text-xs">How values render in charts, KPIs, and AI narratives. "Auto-detect" infers from the field name; pick a code to override.</p></TooltipContent></Tooltip>
                            </div>
                            <div className="mt-1">
                              <MeasureUnitPill
                                value={attr.unit || ''}
                                name={attr.name}
                                description={attr.description}
                                dataType={attr.data_type}
                                onChange={async (newUnit) => {
                                  updateAttribute(selectedEntityData.entity_id, idx, { unit: newUnit });
                                  toast.success(newUnit ? `Unit set to ${newUnit}` : 'Cleared override — auto-detect active');
                                }}
                              />
                            </div>
                          </div>
                          )}
                          {/* Derived measures for this field */}
                          {(function() {
                            let derived = (selectedEntityData.attributes || []).filter(function(a) { return a.is_derived && a.source_field === attr.name; });
                            if (derived.length === 0) return null;
                            return (
                              <div className="p-1.5 rounded-lg bg-indigo-500/5 border border-indigo-500/10">
                                <p className="text-[9px] text-indigo-400/70 uppercase tracking-wider mb-1">Derived Measures</p>
                                {derived.map(function(dm, dmi) {
                                  let dmIdx = (selectedEntityData.attributes || []).findIndex(function(a) { return a.name === dm.name; });
                                  return (
                                    <div key={`${dm.name}-${dmi}`} className="flex items-center justify-between py-0.5 group/dm">
                                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                        <TrendingUp className="w-2.5 h-2.5 text-indigo-400 flex-shrink-0" />
                                        <span className="text-[9px] text-foreground truncate">{dm.description || dm.name}</span>
                                        <Badge className="text-[9px] h-3 bg-indigo-500/15 text-indigo-400 border-indigo-500/20 flex-shrink-0">{dm.aggregation}</Badge>
                                      </div>
                                      <div className="flex gap-0.5 opacity-0 group-hover/dm:opacity-100 transition-opacity">
                                        <Select value={dm.aggregation || 'avg'} onValueChange={function(v) { updateAttribute(selectedEntityData.entity_id, dmIdx, { aggregation: v }); }}>
                                          <SelectTrigger className="h-4 w-14 text-[9px] rounded border-0 bg-transparent p-0"><SelectValue /></SelectTrigger>
                                          <SelectContent><SelectItem value="sum">Sum</SelectItem><SelectItem value="avg">Avg</SelectItem><SelectItem value="count">Count</SelectItem><SelectItem value="min">Min</SelectItem><SelectItem value="max">Max</SelectItem></SelectContent>
                                        </Select>
                                        <button onClick={function(e) { e.stopPropagation(); removeAttribute(selectedEntityData.entity_id, dmIdx); }} className="text-muted-foreground/30 hover:text-destructive"><X className="w-2.5 h-2.5" /></button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                          <div>
                            <Label className="text-xs text-muted-foreground">Display Name</Label>
                            <Input defaultValue={attr.business_name || attr.display_name || ''} onBlur={function(e) { updateAttribute(selectedEntityData.entity_id, idx, { business_name: e.target.value }); }} placeholder="Business-friendly name" className="h-6 !text-xs rounded-lg bg-muted/30 border-border/30 mt-0.5" />
                          </div>
                          {/* Field Description */}
                          <div>
                            <Label className="text-xs text-muted-foreground">Description</Label>
                            <Input defaultValue={attr.description || ''} onBlur={function(e) { updateAttribute(selectedEntityData.entity_id, idx, { description: e.target.value }); }} placeholder="What this field represents..." className="h-6 !text-xs rounded-lg bg-muted/30 border-border/30 mt-0.5" />
                          </div>
                          {/* Field synonyms — alternate terms users type for this field (e.g. "state" for Policy State). Resolves in AI Explore before any AI guess. */}
                          <div>
                            <Label className="text-xs text-muted-foreground">Synonyms — other words users call this field (comma-separated)</Label>
                            <Input defaultValue={(attr.synonyms && attr.synonyms.length) ? attr.synonyms.join(', ') : ''} onBlur={function(e) { const arr = e.target.value.split(',').map(function(s) { return s.trim(); }).filter(Boolean); updateAttribute(selectedEntityData.entity_id, idx, { synonyms: arr }); }} placeholder="e.g. state, region, geography" className="h-6 !text-xs rounded-lg bg-muted/30 border-border/30 mt-0.5" data-testid={'field-synonyms-' + attr.name} />
                          </div>
                          <div className="grid grid-cols-2 gap-1.5">
                            <div>
                              <Label className="text-xs text-muted-foreground">Format</Label>
                              <Select value={attr.format || '_custom_'} onValueChange={function(v) { if (v !== '_custom_') updateAttribute(selectedEntityData.entity_id, idx, { format: v }); }}>
                                <SelectTrigger className="h-6 text-xs rounded-lg bg-muted/30 border-border/30 mt-0.5 font-mono"><SelectValue placeholder="Choose format" /></SelectTrigger>
                                <SelectContent>
                                  {attr.data_type === 'float' || attr.data_type === 'integer' ? (
                                    <React.Fragment>
                                      <SelectItem value="#,##0">Number: 1,234</SelectItem>
                                      <SelectItem value="#,##0.00">Decimal: 1,234.56</SelectItem>
                                      <SelectItem value="#,##0.0">1 Decimal: 1,234.5</SelectItem>
                                      <SelectItem value="$#,##0">Currency: $1,234</SelectItem>
                                      <SelectItem value="$#,##0.00">Currency: $1,234.56</SelectItem>
                                      <SelectItem value="$#,##0.0M">Millions: $1.2M</SelectItem>
                                      <SelectItem value="$#,##0.0B">Billions: $1.2B</SelectItem>
                                      <SelectItem value="#0.0%">Percent: 65.4%</SelectItem>
                                      <SelectItem value="#0.00%">Percent: 65.43%</SelectItem>
                                      <SelectItem value="#0%">Percent: 65%</SelectItem>
                                      <SelectItem value="+#0.0%;-#0.0%">Signed %: +6.8%</SelectItem>
                                      <SelectItem value="#,##0.0x">Multiple: 2.5x</SelectItem>
                                      <SelectItem value="0">Integer: 1234</SelectItem>
                                    </React.Fragment>
                                  ) : attr.data_type === 'string' ? (
                                    <React.Fragment>
                                      <SelectItem value="uppercase">UPPERCASE</SelectItem>
                                      <SelectItem value="lowercase">lowercase</SelectItem>
                                      <SelectItem value="titlecase">Title Case</SelectItem>
                                      <SelectItem value="truncate:50">Truncate at 50 chars</SelectItem>
                                      <SelectItem value="truncate:100">Truncate at 100 chars</SelectItem>
                                    </React.Fragment>
                                  ) : (
                                    <React.Fragment>
                                      <SelectItem value="YYYY-MM-DD">Date: 2025-03-14</SelectItem>
                                      <SelectItem value="MM/DD/YYYY">Date: 03/14/2025</SelectItem>
                                      <SelectItem value="DD MMM YYYY">Date: 14 Mar 2025</SelectItem>
                                      <SelectItem value="MMM YYYY">Month: Mar 2025</SelectItem>
                                      <SelectItem value="YYYY">Year: 2025</SelectItem>
                                    </React.Fragment>
                                  )}
                                  <SelectItem value="_custom_">Custom...</SelectItem>
                                </SelectContent>
                              </Select>
                              <Input defaultValue={attr.format || ''} onBlur={function(e) { updateAttribute(selectedEntityData.entity_id, idx, { format: e.target.value }); }} placeholder="Custom format expression" className="h-6 !text-xs rounded bg-muted/20 border-border/20 mt-0.5 font-mono" />
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">Data Category</Label>
                              <Select value={attr.data_category || 'none'} onValueChange={function(v) { updateAttribute(selectedEntityData.entity_id, idx, { data_category: v }); }}>
                                <SelectTrigger className="h-6 text-xs rounded-lg bg-muted/30 border-border/30 mt-0.5"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="Date">Date</SelectItem><SelectItem value="Geographic - Country">Geographic - Country</SelectItem><SelectItem value="Geographic - State">Geographic - State</SelectItem><SelectItem value="Geographic - County">Geographic - County</SelectItem><SelectItem value="Geographic - City">Geographic - City</SelectItem><SelectItem value="Geographic - Postal Code">Geographic - Postal Code</SelectItem><SelectItem value="Geographic - Latitude">Geographic - Latitude</SelectItem><SelectItem value="Geographic - Longitude">Geographic - Longitude</SelectItem><SelectItem value="url">URL</SelectItem><SelectItem value="image">Image URL</SelectItem><SelectItem value="currency">Currency</SelectItem><SelectItem value="percentage">Percentage</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                              </Select>
                            </div>
                          </div>
                          {(attr.data_category === 'Date' || /date|timestamp|time/i.test(attr.data_type || '')) && (
                            <div className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-2 space-y-1.5" data-testid={'date-settings-' + attr.name}>
                              <Label className="text-[10px] uppercase tracking-wider text-violet-300/80">Date field settings</Label>
                              <div className="flex items-center gap-2 flex-wrap">
                                <button onClick={function() { setDefaultDate(selectedEntityData.entity_id, attr.name); }}
                                  className={'px-2 py-0.5 rounded-full text-[10px] border transition-all ' + (selectedEntityData.default_date_field === attr.name ? 'border-violet-500/60 bg-violet-500/15 text-violet-200' : 'border-border/40 text-muted-foreground hover:border-violet-500/40')}
                                  data-testid={'default-date-toggle-' + attr.name}>
                                  {selectedEntityData.default_date_field === attr.name ? '★ Default business date' : '☆ Set as default date'}
                                </button>
                                {selectedEntityData.default_date_field !== attr.name && (
                                  <button onClick={function() { toggleAllowedDate(selectedEntityData.entity_id, attr.name); }}
                                    className={'px-2 py-0.5 rounded-full text-[10px] border transition-all ' + ((selectedEntityData.allowed_date_fields || []).includes(attr.name) ? 'border-sky-500/60 bg-sky-500/15 text-sky-300' : 'border-border/40 text-muted-foreground hover:border-sky-500/40')}
                                    data-testid={'allowed-date-toggle-' + attr.name}>
                                    {(selectedEntityData.allowed_date_fields || []).includes(attr.name) ? '✓ Allowed alternate' : '+ Allow as alternate'}
                                  </button>
                                )}
                                <button onClick={function() { updateAttribute(selectedEntityData.entity_id, idx, { is_technical_date: !attr.is_technical_date }); }}
                                  className={'px-2 py-0.5 rounded-full text-[10px] border transition-all ' + (attr.is_technical_date ? 'border-amber-500/60 bg-amber-500/15 text-amber-300' : 'border-border/40 text-muted-foreground hover:border-amber-500/40')}
                                  data-testid={'technical-date-toggle-' + attr.name}>
                                  {attr.is_technical_date ? 'Technical date' : 'Mark technical'}
                                </button>
                              </div>
                              <div>
                                <Label className="text-[10px] text-muted-foreground">Synonyms — how users refer to this date (comma-separated)</Label>
                                <Input defaultValue={(attr.date_synonyms && attr.date_synonyms.length) ? attr.date_synonyms.join(', ') : suggestDateSynonyms(attr)}
                                  onBlur={function(e) { const arr = e.target.value.split(',').map(function(s) { return s.trim(); }).filter(Boolean); updateAttribute(selectedEntityData.entity_id, idx, { date_synonyms: arr }); }}
                                  placeholder="e.g. booked, written, book date" className="h-6 !text-xs rounded-lg bg-muted/30 border-border/30 mt-0.5" data-testid={'date-synonyms-' + attr.name} />
                              </div>
                              <p className="text-[9px] text-muted-foreground/70 leading-tight">The default date is used for time questions and Insight date-range/as-of unless the user names another date. Mark other dates as "allowed alternate" to let Insight authors switch to them. Technical dates (e.g. load/ETL) are used only when explicitly named.</p>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-1.5">
                            <div>
                              <Label className="text-xs text-muted-foreground">Folder / Group</Label>
                              <Input defaultValue={attr.folder || ''} onBlur={function(e) { updateAttribute(selectedEntityData.entity_id, idx, { folder: e.target.value }); }} placeholder="e.g. Financial" className="h-6 !text-xs rounded-lg bg-muted/30 border-border/30 mt-0.5" />
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground">Sort by Column</Label>
                              <Select value={attr.sort_by_field || '_default_'} onValueChange={function(v) { updateAttribute(selectedEntityData.entity_id, idx, { sort_by_field: v === '_default_' ? null : v }); }}>
                                <SelectTrigger className="h-6 text-xs rounded-lg bg-muted/30 border-border/30 mt-0.5"><SelectValue placeholder="Default" /></SelectTrigger>
                                <SelectContent><SelectItem value="_default_">Default (alpha)</SelectItem>{(selectedEntityData.attributes || []).filter(function(a2) { return a2.name !== attr.name; }).map(function(a2) { return <SelectItem key={a2.name} value={a2.name}>{a2.description || a2.name}</SelectItem>; })}</SelectContent>
                              </Select>
                            </div>
                          </div>
                          {attr.formula && (
                            <div>
                              <Label className="text-xs text-muted-foreground">Formula</Label>
                              <Input defaultValue={attr.formula} onBlur={function(e) { updateAttribute(selectedEntityData.entity_id, idx, { formula: e.target.value }); }} className="h-6 !text-xs rounded-lg bg-muted/30 border-border/30 mt-0.5 font-mono" />
                            </div>
                          )}
                          <div className="flex items-center justify-end">
                            <button onClick={function(e) { e.stopPropagation(); removeAttribute(selectedEntityData.entity_id, idx); }} className="text-[9px] text-destructive hover:underline">Remove</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className={'w-1.5 h-1.5 rounded-full flex-shrink-0 ' + (attr.semantic_type === 'measure' ? 'bg-primary' : attr.semantic_type === 'hidden' ? 'bg-muted-foreground/30' : 'bg-emerald-400')} />
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] text-foreground truncate">{attr.business_name || attr.display_name || attr.name}</span>
                            {attr.is_calculated && <span className="text-[9px] text-amber-400 font-bold ml-1">fx</span>}
                            {attr.is_derived && <span className="text-[9px] text-indigo-400 font-bold ml-1">{attr.aggregation}</span>}
                            {(attr.business_name || attr.display_name) && <p className="text-[8px] font-mono text-muted-foreground/70 truncate">{attr.name}</p>}
                            {!(attr.business_name || attr.display_name) && <p className="text-xs text-muted-foreground/70 italic truncate">no display name</p>}
                          </div>
                          <div className="flex gap-0.5 flex-shrink-0 flex-wrap justify-end items-center">
                            {attr.pii && (
                              <Badge variant="outline" className="text-[8px] h-3.5 px-1 border-rose-500/40 text-rose-300 gap-0.5" title="Personally identifiable information" data-testid={'pii-badge-' + attr.name}>
                                <ShieldAlert className="w-2 h-2" />PII
                              </Badge>
                            )}
                            {attr.data_category && attr.data_category !== 'none' && (
                              <Badge variant="outline" className="text-[8px] h-3.5 px-1 border-teal-500/30 text-teal-300" data-testid={'cat-badge-' + attr.name}>
                                {attr.data_category === 'Date' || attr.data_category === 'date' ? 'Date' : String(attr.data_category).startsWith('Geographic') || String(attr.data_category).startsWith('geo') ? 'Geo' : 'Other'}
                              </Badge>
                            )}
                            <button onClick={function(e) { e.stopPropagation(); handleSemBrowse(attr); }} className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-muted/50 text-muted-foreground/70 hover:text-foreground transition-all" title="Browse data" data-testid={'browse-data-' + attr.name}><Eye className="w-3 h-3" /></button>
                            {!attr.is_calculated && !attr.formula && cardinalities[attr.name] !== undefined && (
                              <Badge variant="outline" className="text-[8px] h-3.5 px-1 border-sky-500/30 text-sky-400 gap-0.5" title={cardinalities[attr.name].toLocaleString() + ' distinct values'} data-testid={'cardinality-' + attr.name}>
                                <Hash className="w-2 h-2" />{cardinalities[attr.name].toLocaleString()}
                              </Badge>
                            )}
                            {!attr.is_calculated && !attr.formula && cardTotalRows > 0 && cardinalities[attr.name] === cardTotalRows && (
                              <Badge variant="outline" className="text-[8px] h-3.5 px-1 border-amber-500/40 text-amber-400 gap-0.5" title="Unique across all rows — likely a primary key" data-testid={'pk-' + attr.name}>
                                <KeyRound className="w-2 h-2" />key
                              </Badge>
                            )}
                            {attr.folder && <Badge variant="outline" className="text-[6px] h-3 px-1 border-indigo-500/30 text-indigo-400">{attr.folder}</Badge>}
                            {attr.data_category && attr.data_category !== 'none' && <Badge variant="outline" className="text-[6px] h-3 px-1 border-amber-500/30 text-amber-400">{attr.data_category}</Badge>}
                            <Badge variant="outline" className="text-[9px] h-3.5">{attr.data_type}</Badge>
                            <Badge className={'text-[9px] h-3.5 ' + (attr.semantic_type === 'measure' ? 'bg-primary/20 text-primary' : attr.semantic_type === 'hidden' ? 'bg-muted-foreground/20 text-muted-foreground' : 'bg-emerald-500/20 text-emerald-400')}>{attr.semantic_type === 'measure' ? (attr.aggregation || 'sum') : attr.semantic_type === 'hidden' ? 'hidden' : 'dim'}</Badge>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }); })()}
                {(() => {
                  var _fl = (selectedEntityData.attributes || []).filter(function(attr) { if (!fieldSearch.trim()) return true; var _q = fieldSearch.trim().toLowerCase(); return (((attr.name || '') + ' ' + (attr.display_name || '') + ' ' + (attr.business_name || '')).toLowerCase()).indexOf(_q) !== -1; });
                  if (_fl.length <= fieldWindow) return null;
                  return <VirtualMore remaining={_fl.length - fieldWindow} onMore={function() { setFieldWindow(function(w) { return w + 150; }); }} kind="fields" />;
                })()}
              </div>
                </ScrollArea>
              </TabsContent>
              {/* Relationships for this entity */}
              <TabsContent value="relationships" className="flex-1 overflow-hidden m-0 mt-0" data-testid="panel-content-relationships">
                <ScrollArea className="h-full">
              <div className="p-2">
                <div className="flex items-center justify-between px-1 mb-1">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Relationships</p>
                  <button
                    data-testid="panel-add-relationship-btn"
                    title="Add a join from this table"
                    onClick={function() { setRelSource(selectedEntityData.entity_id); setRelTarget(''); setRelName(''); setRelCardinality('many_to_one'); setRelMappings([{ source: '', target: '' }]); setAddRelOpen(true); }}
                    className="flex items-center gap-1 text-[9px] text-primary hover:text-primary/80 transition-colors"
                  >
                    <Plus className="w-3 h-3" /> Add join
                  </button>
                </div>
                {relationships.filter(function(r) { return r.source_entity_id === selectedEntityData.entity_id || r.target_entity_id === selectedEntityData.entity_id; }).map(function(rel) {
                  let isSource = rel.source_entity_id === selectedEntityData.entity_id;
                  let otherEnt = entities.find(function(e) { return e.entity_id === (isSource ? rel.target_entity_id : rel.source_entity_id); });
                  return (
                    <div key={rel.relationship_id} className="p-1.5 rounded-lg hover:bg-muted/20 cursor-pointer group/rl mb-1" onClick={function() { setEditRel(Object.assign({}, rel)); setEditRelOpen(true); }}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-[9px] font-medium text-foreground">{rel.name}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <Badge variant="outline" className="text-[9px] h-3">{rel.join_type || 'inner'}</Badge>
                            <Badge variant="outline" className="text-[9px] h-3">{(rel.cardinality || '').replace('_', ':')}</Badge>
                            <span className="text-xs text-muted-foreground">{isSource ? '\u2192' : '\u2190'} {otherEnt ? otherEnt.name : '?'}</span>
                          </div>
                        </div>
                        <button onClick={function(e) { e.stopPropagation(); handleDeleteRelationship(rel.relationship_id); }} className="text-muted-foreground/30 hover:text-destructive opacity-0 group-hover/rl:opacity-100 transition-opacity"><Trash2 className="w-2.5 h-2.5" /></button>
                      </div>
                    </div>
                  );
                })}
                {relationships.filter(function(r) { return r.source_entity_id === selectedEntityData.entity_id || r.target_entity_id === selectedEntityData.entity_id; }).length === 0 && <p className="text-xs text-muted-foreground/70 italic px-1">No relationships</p>}
              </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </React.Fragment>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <Database className="w-6 h-6 text-muted-foreground/30 mb-2" />
            <p className="text-xs text-foreground">Select an Entity</p>
          </div>
        )}
        {/* Detail sections relocated out of the narrow sidebar into a full-width,
            resizable, collapsible bottom dock spanning canvas + sidebar. */}
        </div>{/* close entity field-list sidebar */}
      </div>{/* close canvas + sidebar row */}

      <div
        className="border-t border-border/60 bg-background flex flex-col relative shrink-0"
        style={{ height: dockCollapsed ? 38 : dockHeight }}
        data-testid="model-detail-dock"
      >
        {!dockCollapsed && (
          <div onMouseDown={startDockResize} className="absolute -top-1.5 left-0 right-0 h-3 cursor-row-resize z-20 group" title="Drag to resize" data-testid="model-dock-resize">
            <div className="mx-auto mt-1 h-1 w-16 rounded-full bg-border group-hover:bg-primary/60 transition-colors" />
          </div>
        )}
        <Tabs
          value={sidebarTab}
          onValueChange={(v) => { setSidebarTab(v); try { localStorage.setItem('inzara.modelSidebarTab', v); } catch (e) { /* ignore quota errors */ } }}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="flex items-center justify-between border-b border-border/40 bg-muted/20 pr-2">
            <TabsList
              className="h-9 justify-start rounded-none bg-transparent px-1.5 gap-1 shrink-0"
              data-testid="model-details-tabs-list"
            >
              <TabsTrigger value="aggregates" className="text-xs h-7 px-3 gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="tab-aggregates"><Layers className="w-3.5 h-3.5" />Aggregates<HelpIcon docKey="aggregates" label="Aggregates" /></TabsTrigger>
              <TabsTrigger value="itd" className="text-xs h-7 px-3 gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="tab-itd"><CalendarClock className="w-3.5 h-3.5" />Inception-to-Date</TabsTrigger>
              <TabsTrigger value="measures" className="text-xs h-7 px-3 gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="tab-measures"><Zap className="w-3.5 h-3.5" />Measures<HelpIcon docKey="calculated-measures" label="Calculated Measures" />{(() => { const t = calcMeasures.length + (selectedEntityData?.attributes || []).filter(a => a.is_calculated && a.semantic_type === 'measure').length; return t > 0 && <Badge variant="outline" className="text-[9px] h-4 px-1 py-0 border-primary/30 text-primary">{t}</Badge>; })()}</TabsTrigger>
              <TabsTrigger value="dimensions" className="text-xs h-7 px-3 gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="tab-dimensions"><GitBranch className="w-3.5 h-3.5" />Dimensions<HelpIcon docKey="calculated-dimensions" label="Calculated Dimensions" />{(() => { const t = (selectedEntityData?.attributes || []).filter(a => a.is_calculated && a.semantic_type === 'dimension').length; return t > 0 && <Badge variant="outline" className="text-[9px] h-4 px-1 py-0 border-blue-500/30 text-blue-400">{t}</Badge>; })()}</TabsTrigger>
              <TabsTrigger value="hierarchies" className="text-xs h-7 px-3 gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="tab-hierarchies"><ChevronRight className="w-3.5 h-3.5" />Hierarchies<HelpIcon docKey="hierarchies" label="Hierarchies" />{(() => { const t = (selectedEntityData?.hierarchies || []).length; return t > 0 && <Badge variant="outline" className="text-[9px] h-4 px-1 py-0 border-primary/30 text-primary">{t}</Badge>; })()}</TabsTrigger>
              <TabsTrigger value="asofdates" className="text-xs h-7 px-3 gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="tab-asofdates"><Calendar className="w-3.5 h-3.5" />As of Dates{(() => { const t = (entities || []).reduce((n, e) => n + (e.default_date_field ? 1 : 0) + (e.allowed_date_fields || []).length, 0); return t > 0 && <Badge variant="outline" className="text-[9px] h-4 px-1 py-0 border-violet-500/30 text-violet-400">{t}</Badge>; })()}</TabsTrigger>
              <TabsTrigger value="glossary" className="text-xs h-7 px-3 gap-1.5 data-[state=active]:bg-background data-[state=active]:shadow-sm" data-testid="tab-glossary"><BookOpen className="w-3.5 h-3.5" />Glossary<HelpIcon docKey="glossary" label="Glossary" />{modelGlossary.length > 0 && <Badge variant="outline" className="text-[9px] h-4 px-1 py-0 border-indigo-500/30 text-indigo-400">{modelGlossary.length}</Badge>}</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => setDockDensity(d => d === 'compact' ? 'comfortable' : 'compact')} className="text-[10px] px-2 h-6 rounded-md border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors" data-testid="model-dock-density" title="Toggle row density">{dense ? 'Comfortable' : 'Compact'}</button>
              <button onClick={() => setDockCollapsed(c => !c)} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors" data-testid="model-dock-collapse" title={dockCollapsed ? 'Expand panel' : 'Collapse panel'}>{dockCollapsed ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button>
            </div>
          </div>
          {!dockCollapsed && (<>
            <TabsContent value="aggregates" className="flex-1 overflow-auto m-0 p-0 data-[state=inactive]:hidden">
              {activeModelId ? <AggregatePanel modelId={activeModelId} /> : <p className="text-xs text-muted-foreground/70 italic p-3">Save the model to manage aggregates</p>}
            </TabsContent>

            <TabsContent value="itd" className="flex-1 overflow-auto m-0 p-0 data-[state=inactive]:hidden" data-testid="itd-section">
              <CalculatedMeasuresPanel modelId={activeModelId} />
            </TabsContent>

            <TabsContent value="asofdates" className="flex-1 overflow-auto m-0 p-3 data-[state=inactive]:hidden" data-testid="asofdates-section">
              {!activeModelId ? (
                <p className="text-xs text-muted-foreground/70 italic">Save the model to manage as-of dates</p>
              ) : (() => {
                const isDate = (a) => String(a.data_type || '').toLowerCase().includes('date') || String(a.data_type || '').toLowerCase().includes('time');
                const entsWithDates = (entities || []).map(e => ({ e, dates: (e.attributes || []).filter(isDate) })).filter(x => x.dates.length > 0);
                return (
                  <div className="space-y-4" data-testid="asofdates-body">
                    <p className="text-[11px] text-muted-foreground/70 leading-tight">
                      The <span className="font-semibold text-violet-300">default</span> date drives time questions and Insight/AI-Explore date-range &amp; as-of unless the user names another. Mark other dates as <span className="font-semibold text-sky-300">allowed alternates</span> so authors can switch to them. Technical dates (load/ETL) stay off both lists and are only used when named explicitly.
                    </p>
                    {entsWithDates.length === 0 && (
                      <p className="text-xs text-muted-foreground" data-testid="asofdates-empty">No date columns found on this model's entities. Add a date-typed attribute first.</p>
                    )}
                    {entsWithDates.map(({ e, dates }) => (
                      <div key={e.entity_id} className="rounded-xl border border-border/30 bg-background/40 p-3" data-testid={`asofdates-entity-${e.entity_id}`}>
                        <div className="flex items-center gap-1.5 mb-2">
                          <Table2 className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-xs font-semibold text-foreground">{e.business_name || e.display_name || e.name}</span>
                          <span className="text-[10px] text-muted-foreground/70">{dates.length} date field{dates.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="space-y-1.5">
                          {dates.map(attr => {
                            const isDefault = e.default_date_field === attr.name;
                            const isAllowed = (e.allowed_date_fields || []).includes(attr.name);
                            return (
                              <div key={attr.name} className="flex items-center gap-2 flex-wrap" data-testid={`asofdate-row-${e.entity_id}-${attr.name}`}>
                                <span className="text-[11px] text-foreground/90 min-w-[8rem]">{attr.business_name || attr.display_name || attr.name}</span>
                                <button
                                  onClick={() => setDefaultDate(e.entity_id, attr.name)}
                                  className={'px-2 py-0.5 rounded-full text-[10px] border transition-all ' + (isDefault ? 'border-violet-500/60 bg-violet-500/15 text-violet-200' : 'border-border/40 text-muted-foreground hover:border-violet-500/40')}
                                  data-testid={`asofdate-default-${e.entity_id}-${attr.name}`}
                                >{isDefault ? '★ Default business date' : '☆ Set as default'}</button>
                                {!isDefault && (
                                  <button
                                    onClick={() => toggleAllowedDate(e.entity_id, attr.name)}
                                    className={'px-2 py-0.5 rounded-full text-[10px] border transition-all ' + (isAllowed ? 'border-sky-500/60 bg-sky-500/15 text-sky-300' : 'border-border/40 text-muted-foreground hover:border-sky-500/40')}
                                    data-testid={`asofdate-allowed-${e.entity_id}-${attr.name}`}
                                  >{isAllowed ? '✓ Allowed alternate' : '+ Allow as alternate'}</button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </TabsContent>

            <TabsContent value="measures" className="flex-1 overflow-auto m-0 p-2 data-[state=inactive]:hidden" data-testid="calc-measures-section">
              {(() => {
                const entityCalcMeasures = (selectedEntityData?.attributes || []).filter(a => a.is_calculated && a.semantic_type === 'measure');
                const total = calcMeasures.length + entityCalcMeasures.length;
                return (
                  <>
                    <div className="flex items-center justify-between px-2 pt-0.5 pb-2 sticky top-0 bg-background/90 backdrop-blur z-[2]">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground/80">Measures</span>
                        {total > 0 && <span className="text-[9px] px-1.5 rounded-full bg-muted/60 text-muted-foreground">{total}</span>}
                      </div>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button onClick={() => setAddMeasureOpen(true)} className="flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 transition-colors" data-testid="add-measure-btn"><Plus className="w-3.5 h-3.5" /> Add measure</button>
                        </TooltipTrigger>
                        <TooltipContent><p className="text-xs">Add a calculated measure with formula</p></TooltipContent>
                      </Tooltip>
                      {total > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={async () => {
                                if (!activeModelId) return;
                                try {
                                  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/semantic/models/${activeModelId}/measures/export`, { credentials: 'include' });
                                  if (!res.ok) throw new Error('Export failed');
                                  const blob = await res.blob();
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url; a.download = `measures_${activeModelId}.xlsx`;
                                  document.body.appendChild(a); a.click(); a.remove();
                                  URL.revokeObjectURL(url);
                                } catch (e) { toast.error('Could not export measures'); }
                              }}
                              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                              data-testid="export-measures-btn"
                            ><Download className="w-3.5 h-3.5" /> Export to Excel</button>
                          </TooltipTrigger>
                          <TooltipContent><p className="text-xs">Download measures (name, formula, expanded formula, unit, source) as .xlsx</p></TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => { if (importMeasuresInputRef.current) { importMeasuresInputRef.current.value = ''; importMeasuresInputRef.current.click(); } }}
                            disabled={importMeasuresBusy}
                            className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                            data-testid="import-measures-btn"
                          ><Upload className="w-3.5 h-3.5" /> {importMeasuresBusy ? 'Reading…' : 'Import from Excel'}</button>
                        </TooltipTrigger>
                        <TooltipContent><p className="text-xs">Upload an edited Measures .xlsx to bulk-update formulas &amp; units (preview before applying)</p></TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => setPyramidFormulaOpen(true)}
                            disabled={!activeModelId}
                            className="flex items-center gap-1 text-[11px] text-amber-500 hover:text-amber-400 transition-colors disabled:opacity-50"
                            data-testid="import-pyramid-measures-btn"
                          ><Pyramid className="w-3.5 h-3.5" /> Import from Pyramid</button>
                        </TooltipTrigger>
                        <TooltipContent><p className="text-xs">Import Pyramid Analytics calculations as measures — converted where possible, otherwise created with the raw PQL for you to fix</p></TooltipContent>
                      </Tooltip>
                      <input
                        ref={importMeasuresInputRef}
                        type="file"
                        accept=".xlsx"
                        className="hidden"
                        data-testid="import-measures-input"
                        onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) analyzeMeasuresImport(f); }}
                      />
                      {importUndoAvail && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={undoLastMeasuresImport}
                              className="flex items-center gap-1 text-[11px] text-amber-400 hover:text-amber-300 transition-colors"
                              data-testid="undo-import-measures-btn"
                            ><RefreshCw className="w-3.5 h-3.5" /> Undo last import</button>
                          </TooltipTrigger>
                          <TooltipContent><p className="text-xs">Roll back the last Excel import — restores changed measures &amp; removes created ones</p></TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                    {total > 0 && (
                      <div className={`grid grid-cols-[1.5fr_1.9fr_84px_150px_36px] gap-2 px-2 ${dense ? 'py-1' : 'py-1.5'} border-b border-border/40 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80`}>
                        <span>Name</span><span>Formula</span><span>Unit</span><span>Status</span><span></span>
                      </div>
                    )}
                    {calcMeasures.slice(0, measureWindow).map(function(m) { return (
                      <div key={m.measure_id} className={`grid grid-cols-[1.5fr_1.9fr_84px_150px_36px] gap-2 items-center px-2 ${dense ? 'py-1' : 'py-2'} rounded-md hover:bg-muted/20 cursor-pointer group/cm border-b border-border/10`} onClick={function() { setEditCalcMeasure(m); setEditCalcName(m.name); setEditCalcFormula(m.formula || ''); setEditCalcDesc(m.description || ''); setEditCalcEntity(m.source_entity_id || ''); }} data-testid={`calc-measure-row-${m.name}`}>
                        <span className={`${dense ? 'text-[11px]' : 'text-xs'} text-foreground truncate`} title={m.name}>{m.name}</span>
                        <span className={`${dense ? 'text-[10px]' : 'text-[11px]'} font-mono text-primary/70 truncate`} title={calcFormulaText(m)}>{calcFormulaText(m)}</span>
                        <div onClick={(e) => e.stopPropagation()} className="min-w-0">
                          <MeasureUnitPill
                            value={m.unit || ''}
                            name={m.name}
                            description={m.description}
                            dataType={m.data_type || 'float'}
                            compact
                            onChange={async (newUnit) => {
                              try {
                                const updated = await api.updateSemanticMeasure(m.measure_id, { unit: newUnit });
                                setCalcMeasures(prev => prev.map(x => x.measure_id === m.measure_id ? { ...x, unit: updated.unit || '' } : x));
                                toast.success(newUnit ? `Unit set to ${newUnit}` : 'Cleared override — auto-detect active');
                              } catch (err) { toast.error(err.message || 'Failed to update unit'); }
                            }}
                          />
                        </div>
                        <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 min-w-0">
                          {m.needs_fix && (
                            <span title={m.source_pql ? `Raw Pyramid PQL: ${m.source_pql}` : 'Imported without a translatable formula — open to fix'} className="text-[9px] px-1.5 py-0.5 rounded-full border flex-shrink-0 border-amber-500/50 bg-amber-500/15 text-amber-500 flex items-center gap-0.5" data-testid={`measure-needs-fix-${m.name}`}><AlertTriangle className="w-2.5 h-2.5" /> Needs fixing</span>
                          )}
                          {(() => {
                            const st = certStatusOf(m);
                            const map = {
                              certified: { label: 'Certified', cls: 'border-emerald-500/50 bg-emerald-500/15 text-emerald-400' },
                              pending: { label: 'In review', cls: 'border-amber-500/50 bg-amber-500/15 text-amber-300' },
                              in_review: { label: 'In review', cls: 'border-amber-500/50 bg-amber-500/15 text-amber-300' },
                              rejected: { label: 'Rejected', cls: 'border-red-500/50 bg-red-500/15 text-red-400' },
                              deprecated: { label: 'Deprecated', cls: 'border-border/50 text-muted-foreground line-through' },
                            };
                            const s = map[st] || { label: 'Draft', cls: 'border-border/50 bg-muted-foreground/10 text-muted-foreground' };
                            const cert = m.certification || {};
                            const trail = st === 'certified'
                              ? `Certified${cert.certified_by_email ? ` by ${cert.certified_by_email}` : ''}${cert.certified_at ? ` on ${new Date(cert.certified_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}` : ''}`
                              : undefined;
                            return <span title={trail} className={`text-[9px] px-1.5 py-0.5 rounded-full border flex-shrink-0 ${st === 'certified' ? 'cursor-help ' : ''}${s.cls}`} data-testid={`measure-cert-status-${m.name}`}>{st === 'certified' ? '✓ ' : ''}{s.label}{st === 'certified' && cert.version ? ` v${cert.version}` : ''}</span>;
                          })()}
                          {certStatusOf(m) === 'certified' && (
                            <button onClick={() => openCertHistory(m)} title="Version history" className="text-[9px] px-1 py-0.5 rounded-full border border-border/40 text-muted-foreground hover:bg-muted/30 hover:text-foreground flex-shrink-0" data-testid={`measure-cert-history-${m.name}`}><History className="w-3 h-3" /></button>
                          )}
                          {certStatusOf(m) !== 'certified' && (
                            canCertify
                              ? <button onClick={() => certifyMeasureRow(m)} className="text-[9px] px-1.5 py-0.5 rounded-full border border-primary/40 text-primary hover:bg-primary/10 flex-shrink-0" data-testid={`measure-certify-${m.name}`}>Certify</button>
                              : (certStatusOf(m) === 'draft' && <button onClick={() => submitMeasureRow(m)} className="text-[9px] px-1.5 py-0.5 rounded-full border border-border/50 text-muted-foreground hover:bg-muted/30 flex-shrink-0" data-testid={`measure-submit-${m.name}`}>Submit</button>)
                          )}
                        </div>
                        <button onClick={function(e) { e.stopPropagation(); handleDeleteMeasure(m.measure_id); }} className="text-muted-foreground/70 hover:text-destructive opacity-0 group-hover/cm:opacity-100 transition-opacity justify-self-end"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ); })}
                    {calcMeasures.length > measureWindow && (
                      <VirtualMore remaining={calcMeasures.length - measureWindow} onMore={function() { setMeasureWindow(function(w) { return w + 150; }); }} kind="measures" />
                    )}
                    {entityCalcMeasures.map(function(a, cmi) { return (
                      <div key={`attr-${a.name}-${cmi}`} className={`grid grid-cols-[1.5fr_1.9fr_84px_150px_36px] gap-2 items-center px-2 ${dense ? 'py-1' : 'py-2'} rounded-md hover:bg-muted/20 group/cm border-b border-border/10`} data-testid={`calc-measure-row-${a.name}`}>
                        <span className={`${dense ? 'text-[11px]' : 'text-xs'} text-foreground truncate`} title={a.display_name || a.name}>{a.display_name || a.name}</span>
                        <span className={`${dense ? 'text-[10px]' : 'text-[11px]'} font-mono text-primary/70 truncate`} title={a.formula}>{a.formula}</span>
                        <span className="text-[10px] text-muted-foreground/70">—</span>
                        <span className="text-[10px] text-muted-foreground truncate" title={selectedEntityData?.name}>{(selectedEntityData?.name) || 'field'}</span>
                        <button onClick={function(e) { e.stopPropagation(); handleDeleteCalcMeasureAttr(a.name); }} className="text-muted-foreground/70 hover:text-destructive opacity-0 group-hover/cm:opacity-100 transition-opacity justify-self-end" title="Remove calculated measure"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ); })}
                    {total === 0 && <p className="text-xs text-muted-foreground/70 italic">No calculated measures</p>}
                  </>
                );
              })()}
            </TabsContent>

            <TabsContent value="dimensions" className="flex-1 overflow-auto m-0 p-2 data-[state=inactive]:hidden" data-testid="calc-dimensions-section">
              {!selectedEntityData ? (
                <p className="text-xs text-muted-foreground/70 italic">Select an entity to view its calculated dimensions</p>
              ) : (
                <>
                  {(() => { const dims = (selectedEntityData.attributes || []).filter(a => a.is_calculated && a.semantic_type === 'dimension'); return dims.length > 0 ? (
                    <div className={`grid grid-cols-[1.6fr_2.4fr_140px_36px] gap-2 px-2 ${dense ? 'py-1' : 'py-1.5'} border-b border-border/40 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80`}>
                      <span>Name</span><span>Formula</span><span>Sort</span><span></span>
                    </div>
                  ) : null; })()}
                  {(selectedEntityData.attributes || []).filter(a => a.is_calculated && a.semantic_type === 'dimension').map(function(a, cdi) { return (
                    <div key={`${a.name}-${cdi}`} className={`grid grid-cols-[1.6fr_2.4fr_140px_36px] gap-2 items-center px-2 ${dense ? 'py-1' : 'py-2'} rounded-md hover:bg-muted/20 group/cd border-b border-border/10`} data-testid={`calc-dim-row-${a.name}`}>
                      <span className={`${dense ? 'text-[11px]' : 'text-xs'} text-foreground truncate`} title={a.display_name || a.name}>{a.display_name || a.name}</span>
                      <span className={`${dense ? 'text-[10px]' : 'text-[11px]'} font-mono text-blue-400/80 truncate`} title={a.formula}>{a.formula}</span>
                      <span className="text-[10px] text-muted-foreground truncate" title={a.sort_field || ''}>{a.sort_field || '—'}</span>
                      <button onClick={function(e) { e.stopPropagation(); handleDeleteCalcDimension(a.name); }} className="text-muted-foreground/70 hover:text-destructive opacity-0 group-hover/cd:opacity-100 transition-opacity justify-self-end" title="Remove calculated dimension"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ); })}
                  {(selectedEntityData.attributes || []).filter(a => a.is_calculated && a.semantic_type === 'dimension').length === 0 && (
                    <button onClick={() => setCalcDimOpen(true)} className="text-xs text-blue-400 hover:text-blue-300 italic" data-testid="calc-dim-empty-add">No calculated dimensions — click to add one</button>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="hierarchies" className="flex-1 overflow-auto m-0 p-2 data-[state=inactive]:hidden">
              {!selectedEntityData ? (
                <p className="text-xs text-muted-foreground/70 italic">Select an entity to view its hierarchies</p>
              ) : (
                <>
                  <div className="flex items-center justify-end mb-1">
                    <button onClick={function() { setEditHierarchyIdx(null); setHierarchyName(''); setHierarchyLevels([]); setHierarchyEditorOpen(true); }} className="text-primary flex items-center gap-1 text-[10px]" data-testid="add-hierarchy-btn"><Plus className="w-3 h-3" />Add</button>
                  </div>
                  {(selectedEntityData.hierarchies || []).map(function(h, hi) { return (
                    <div key={hi} className="p-1.5 rounded-lg bg-muted/20 border border-border/20 mb-1 group/h">
                      <div className="flex items-center justify-between">
                        <button onClick={function() { setEditHierarchyIdx(hi); setHierarchyName(h.name); setHierarchyLevels(h.levels || []); setHierarchyEditorOpen(true); }} className="text-[9px] font-medium text-foreground hover:text-primary transition-colors">{h.name}</button>
                        <button onClick={function() { let en = entities.find(function(e) { return e.entity_id === selectedEntityData.entity_id; }); let nh = (en.hierarchies || []).filter(function(_, i) { return i !== hi; }); api.updateSemanticEntity(selectedEntityData.entity_id, { hierarchies: nh }).then(function(u) { setEntities(function(p) { return p.map(function(e) { return e.entity_id === selectedEntityData.entity_id ? u : e; }); }); }); }} className="text-muted-foreground/70 hover:text-destructive opacity-0 group-hover/h:opacity-100 transition-opacity"><Trash2 className="w-2.5 h-2.5" /></button>
                      </div>
                      <div className="flex items-center gap-0.5 mt-1 flex-wrap">{(h.levels || []).map(function(l, li) { return (<React.Fragment key={li}>{li > 0 && <ChevronRight className="w-2.5 h-2.5 text-muted-foreground/30" />}<Badge variant="outline" className="text-[9px]">{(function() { let a = (selectedEntityData.attributes || []).find(function(at) { return at.name === l; }); return a && a.description ? a.description : l; })()}</Badge></React.Fragment>); })}{(h.levels || []).length === 0 && <span className="text-xs text-muted-foreground/70 italic">Empty - click to edit</span>}</div>
                    </div>
                  ); })}
                  {(selectedEntityData.hierarchies || []).length === 0 && <p className="text-xs text-muted-foreground/70 italic">No hierarchies defined</p>}
                </>
              )}
            </TabsContent>

            <TabsContent value="glossary" className="flex-1 overflow-auto m-0 p-2 data-[state=inactive]:hidden">
              <div className="flex items-center justify-end gap-1 mb-1">
                <button onClick={async () => {
                  if (!activeModelId) return;
                  setAiGlossaryLoading(true);
                  try {
                    const full = await api.getSemanticModelFull(activeModelId);
                    const fields = [];
                    (full.entities || []).forEach(e => (e.attributes || []).forEach(a => { if (!modelGlossary.some(g => g.term.toLowerCase() === (a.description || a.name).toLowerCase())) fields.push(a); }));
                    if (fields.length === 0) { toast.info('All fields already have glossary entries'); return; }
                    let created = 0;
                    for (const f of fields.slice(0, 10)) {
                      const term = f.description || f.name.replace(/_/g, ' ');
                      const def = `${term} — ${f.data_type} field from the ${full.name || 'data'} model.${f.aggregation ? ' Aggregation: ' + f.aggregation + '.' : ''}`;
                      await api.createGlossaryTerm({ term, definition: def, model_id: activeModelId, related_fields: [f.name], category: f.semantic_type === 'measure' ? 'Measures' : 'Dimensions' });
                      created++;
                    }
                    const terms = await api.getGlossary(null, null, activeModelId);
                    setModelGlossary(terms || []);
                    toast.success(`Generated ${created} glossary entries`);
                  } catch (err) { toast.error(err.message); }
                  finally { setAiGlossaryLoading(false); }
                }} className="text-indigo-400 hover:text-indigo-300 disabled:opacity-40 flex items-center gap-1 text-[10px]" disabled={aiGlossaryLoading} title="Auto-generate glossary entries from fields">
                  {aiGlossaryLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}Auto
                </button>
                <button onClick={() => setGlossaryAddOpen(true)} className="text-primary hover:text-primary/80 flex items-center gap-1 text-[10px]" title="Add glossary term"><Plus className="w-3 h-3" />Add</button>
              </div>
              {modelGlossary.map(g => (
                <div key={g.term_id} className="flex items-start gap-2 p-1.5 rounded-lg hover:bg-muted/20 group/g">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-medium text-foreground">{g.term}</p>
                    <p className="text-[9px] text-muted-foreground/70 line-clamp-2">{g.definition}</p>
                  </div>
                  <button onClick={() => { api.deleteGlossaryTerm(g.term_id).then(() => setModelGlossary(prev => prev.filter(t => t.term_id !== g.term_id))).catch(e => toast.error(e.message)); }} className="text-muted-foreground/70 hover:text-destructive opacity-0 group-hover/g:opacity-100 transition-opacity flex-shrink-0 mt-0.5"><Trash2 className="w-2.5 h-2.5" /></button>
                </div>
              ))}
              {modelGlossary.length === 0 && <p className="text-xs text-muted-foreground/70 italic">No glossary terms. Click <Sparkles className="w-2.5 h-2.5 inline" /> to auto-generate.</p>}
              {glossaryAddOpen && (
                <div className="mt-2 p-2 border border-border/30 rounded-lg space-y-1.5">
                  <Input value={newGlossaryTerm} onChange={e => setNewGlossaryTerm(e.target.value)} placeholder="Term name" className="h-7 text-[10px]" />
                  <Input value={newGlossaryDef} onChange={e => setNewGlossaryDef(e.target.value)} placeholder="Definition" className="h-7 text-[10px]" />
                  <div className="flex gap-1">
                    <Button size="sm" className="h-6 text-[10px] flex-1" onClick={async () => {
                      if (!newGlossaryTerm.trim()) return;
                      try {
                        const t = await api.createGlossaryTerm({ term: newGlossaryTerm, definition: newGlossaryDef, model_id: activeModelId });
                        setModelGlossary(prev => [...prev, t]);
                        setNewGlossaryTerm(''); setNewGlossaryDef(''); setGlossaryAddOpen(false);
                        toast.success('Term added');
                      } catch (err) { toast.error(err.message); }
                    }}>Add</Button>
                    <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => { setGlossaryAddOpen(false); setNewGlossaryTerm(''); setNewGlossaryDef(''); }}>Cancel</Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </>)}
          </Tabs>
        </div>{/* close bottom detail dock */}
      </div>
      )}

      <EditModelDialog
        open={editModelOpen}
        onOpenChange={setEditModelOpen}
        model={model}
        setModel={setModel}
        entities={entities}
        saving={saving}
        smHasUnsavedChanges={smHasUnsavedChanges}
        setSmHasUnsavedChanges={setSmHasUnsavedChanges}
        loadModel={loadModel}
        activeModelId={activeModelId}
        onSave={() => setSaveDialogOpen(true)}
      />

      <Dialog open={certHistory.open} onOpenChange={(v) => setCertHistory((s) => ({ ...s, open: v }))}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="cert-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <History className="w-4 h-4 text-primary" /> Certification History
            </DialogTitle>
          </DialogHeader>
          <div className="mt-1">
            <p className="text-xs text-muted-foreground mb-3">{certHistory.name}</p>
            {certHistory.loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</div>
            ) : (certHistory.data?.versions?.length ? (
              <div className="space-y-2 max-h-80 overflow-auto">
                {certHistory.data.versions.map((v) => (
                  <div key={v.version} className="rounded-lg border border-border/40 bg-muted/20 p-3" data-testid={`cert-version-${v.version}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-emerald-400">v{v.version}{v.version === certHistory.data.current_version ? ' (current)' : ''}</span>
                      <span className="text-[10px] text-muted-foreground">{v.at ? new Date(v.at).toLocaleString() : ''}</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">by {v.by}</p>
                    {v.reason ? <p className="text-xs text-foreground/80 mt-1">{v.reason}</p> : <p className="text-xs text-muted-foreground/80 italic mt-1">No reason recorded</p>}
                    {(v.changes || []).length > 0 && (
                      <div className="mt-2 space-y-1" data-testid={`cert-version-changes-${v.version}`}>
                        {v.changes.map((ch, ci) => (
                          <div key={ci} className="text-[11px] rounded-md bg-background/60 border border-border/40 px-2 py-1">
                            <span className="font-medium text-foreground/90">{ch.field}: </span>
                            <span className="text-rose-600 dark:text-rose-300 line-through break-all">{ch.from == null || ch.from === '' ? '—' : String(ch.from)}</span>
                            <span className="text-muted-foreground"> → </span>
                            <span className="text-emerald-700 dark:text-emerald-300 break-all">{ch.to == null || ch.to === '' ? '—' : String(ch.to)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-6 text-center">No certification versions yet.</p>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <PiiConsistencyDialog
        modelId={model?.model_id || activeModelId}
        open={piiFixOpen}
        onOpenChange={setPiiFixOpen}
        onApplied={() => loadModel(model?.model_id || activeModelId)}
      />


      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg" data-testid="bulk-classify-dialog">
          <DialogHeader><DialogTitle>Bulk Classify Fields</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2 flex-wrap pb-2 border-b border-border/40">
            <Select value={bulkCat} onValueChange={setBulkCat}>
              <SelectTrigger className="h-7 text-xs rounded-lg w-52" data-testid="bulk-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Category: None</SelectItem>
                <SelectItem value="Date">Date</SelectItem>
                <SelectItem value="Geographic - Country">Geographic - Country</SelectItem>
                <SelectItem value="Geographic - State">Geographic - State</SelectItem>
                <SelectItem value="Geographic - County">Geographic - County</SelectItem>
                <SelectItem value="Geographic - City">Geographic - City</SelectItem>
                <SelectItem value="Geographic - Postal Code">Geographic - Postal Code</SelectItem>
                <SelectItem value="Geographic - Latitude">Geographic - Latitude</SelectItem>
                <SelectItem value="Geographic - Longitude">Geographic - Longitude</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={function() { applyBulk({ data_category: bulkCat }); }} data-testid="bulk-apply-category">Set Category</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs text-rose-300 border-rose-500/40" onClick={function() { applyBulk({ pii: true }); }} data-testid="bulk-mark-pii">Mark PII</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={function() { applyBulk({ pii: false }); }} data-testid="bulk-clear-pii">Clear PII</Button>
          </div>
          <div className="max-h-[46vh] overflow-y-auto space-y-2 mt-2">
            {visibleEntities.map(function(en) { return (
              <div key={en.entity_id}>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{en.name}</div>
                {(en.attributes || []).map(function(a) { const key = en.entity_id + '::' + a.name; return (
                  <label key={key} className="flex items-center gap-2 py-0.5 text-xs cursor-pointer" data-testid={'bulk-field-' + a.name}>
                    <input type="checkbox" checked={!!bulkSel[key]} onChange={function() { setBulkSel(function(s) { return { ...s, [key]: !s[key] }; }); }} />
                    <span className="font-mono">{a.name}</span>
                    {a.pii && <Badge variant="outline" className="text-[8px] h-3.5 px-1 border-rose-500/40 text-rose-300">PII</Badge>}
                    {a.data_category && a.data_category !== 'none' && <Badge variant="outline" className="text-[8px] h-3.5 px-1 border-teal-500/30 text-teal-300">{a.data_category === 'Date' || a.data_category === 'date' ? 'Date' : String(a.data_category).startsWith('Geographic') || String(a.data_category).startsWith('geo') ? 'Geo' : 'Other'}</Badge>}
                  </label>
                ); })}
              </div>
            ); })}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}><AlertDialogContent className="glass-card border-border/50 rounded-2xl"><AlertDialogHeader><AlertDialogTitle>Delete Model</AlertDialogTitle><AlertDialogDescription>Delete {deleteTargetName}? This cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteModel} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>

      <AddEntityDialog
        open={addEntityOpen}
        onOpenChange={setAddEntityOpen}
        sourceTables={sourceTables}
        sourceTablesLoading={sourceTablesLoading}
        entities={entities}
        model={model}
        onPickTable={handleAddEntity}
        saving={saving}
      />

      <QueryTableDialog
        open={queryTableOpen}
        onOpenChange={(o) => { setQueryTableOpen(o); if (!o) setEditQueryEntity(null); }}
        modelId={activeModelId}
        entities={entities}
        editEntity={editQueryEntity}
        onCreated={() => { if (activeModelId) loadModel(activeModelId); }}
      />

      <SaveAsModelDialog
        open={saveAsOpen}
        onOpenChange={setSaveAsOpen}
        model={model}
        onCloned={handleModelCloned}
      />


      <AddRelationshipDialog
        open={addRelOpen}
        onOpenChange={setAddRelOpen}
        entities={entities}
        relName={relName}
        setRelName={setRelName}
        relSource={relSource}
        setRelSource={setRelSource}
        relTarget={relTarget}
        setRelTarget={setRelTarget}
        mappings={relMappings}
        setMappings={setRelMappings}
        relCardinality={relCardinality}
        setRelCardinality={setRelCardinality}
        saving={saving}
        onAdd={handleAddRelationship}
      />


      <PyramidFormulaImportDialog
        open={pyramidFormulaOpen}
        onOpenChange={setPyramidFormulaOpen}
        modelId={activeModelId}
        modelName={model && model.name}
        onImported={() => { if (activeModelId) loadModel(activeModelId); }}
      />

      <AddMeasureDialog        open={addMeasureOpen}
        onOpenChange={setAddMeasureOpen}
        modelId={activeModelId}
        measureName={measureName}
        setMeasureName={setMeasureName}
        measureFormula={measureFormula}
        setMeasureFormula={setMeasureFormula}
        measureCursorPos={measureCursorPos}
        setMeasureCursorPos={setMeasureCursorPos}
        measureEntity={measureEntity}
        setMeasureEntity={setMeasureEntity}
        measureDesc={measureDesc}
        setMeasureDesc={setMeasureDesc}
        measureFolder={measureFolder}
        setMeasureFolder={setMeasureFolder}
        entities={entities}
        measures={calcMeasures}
        saving={saving}
        onCreate={handleAddMeasure}
        onAiGenerate={() => setAiFormulaDialogOpen(true)}
        onResetForm={() => { setMeasureName(''); setMeasureFormula(''); setMeasureEntity(''); setMeasureDesc(''); setMeasureFolder(''); setMeasureCursorPos(0); setScopeMode(false); setScopeBaseField(''); setScopeAgg('sum'); setScopeFilters([]); setTiMode(false); setTiOp('ytd'); setTiOf(''); setTiN(3); }}
        scopeMode={scopeMode} setScopeMode={setScopeMode}        scopeBaseField={scopeBaseField} setScopeBaseField={setScopeBaseField}
        scopeAgg={scopeAgg} setScopeAgg={setScopeAgg}
        scopeFilters={scopeFilters} setScopeFilters={setScopeFilters}
        scopeIgnore={scopeIgnore} setScopeIgnore={setScopeIgnore}
        scopePct={scopePct} setScopePct={setScopePct}
        tiMode={tiMode} setTiMode={setTiMode}
        tiOp={tiOp} setTiOp={setTiOp}
        tiOf={tiOf} setTiOf={setTiOf}
        tiN={tiN} setTiN={setTiN}
      />

      <CalcFieldDialog
        open={calcFieldOpen}
        onOpenChange={setCalcFieldOpen}
        selectedEntityData={selectedEntityData}
        calcFieldName={calcFieldName}
        setCalcFieldName={setCalcFieldName}
        calcFieldDesc={calcFieldDesc}
        setCalcFieldDesc={setCalcFieldDesc}
        calcFieldFormula={calcFieldFormula}
        setCalcFieldFormula={setCalcFieldFormula}
        calcCursorPos={calcCursorPos}
        setCalcCursorPos={setCalcCursorPos}
        onCreate={addCalcField}
        onAiGenerate={() => setAiFormulaDialogOpen(true)}
        onResetForm={() => { setCalcFieldName(''); setCalcFieldFormula(''); setCalcFieldDesc(''); }}
        modelId={activeModelId}
      />

      <CalcDimensionDialog
        open={calcDimOpen}
        onOpenChange={setCalcDimOpen}
        selectedEntityData={selectedEntityData}
        onCreate={addCalcDimension}
        modelId={activeModelId}
      />

      <AiWizardDialog
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        wizardStep={wizardStep}
        setWizardStep={setWizardStep}
        wizardTables={wizardTables}
        wizardSelectedTables={wizardSelectedTables}
        setWizardSelectedTables={setWizardSelectedTables}
        wizardTableSearch={wizardTableSearch}
        setWizardTableSearch={setWizardTableSearch}
        wizardTablesLoading={wizardTablesLoading}
        saving={saving}
        aiClassifying={aiClassifying}
        entities={entities}
        relationships={relationships}
        onAddEntities={wizardAddEntities}
        onRunAi={wizardRunAi}
        onFinish={wizardFinish}
      />


      <DerivedMeasureDialog
        open={derivedMeasureOpen}
        onOpenChange={setDerivedMeasureOpen}
        selectedEntityData={selectedEntityData}
        derivedSourceField={derivedSourceField}
        derivedAgg={derivedAgg}
        setDerivedAgg={setDerivedAgg}
        derivedName={derivedName}
        setDerivedName={setDerivedName}
        onCreate={addDerivedMeasure}
        onResetForm={() => { setDerivedSourceField(''); setDerivedName(''); setDerivedDesc(''); setDerivedAgg('avg'); setDerivedFormat(''); }}
      />

      <HierarchyEditorDialog
        open={hierarchyEditorOpen}
        onOpenChange={setHierarchyEditorOpen}
        selectedEntityData={selectedEntityData}
        entities={entities}
        setEntities={setEntities}
        editHierarchyIdx={editHierarchyIdx}
        hierarchyName={hierarchyName}
        setHierarchyName={setHierarchyName}
        hierarchyLevels={hierarchyLevels}
        setHierarchyLevels={setHierarchyLevels}
      />


      <EntityPreviewSheet
        open={!!previewEntity}
        onOpenChange={(v) => { if (!v) setPreviewEntity(null); }}
        entityId={previewEntity?.entity_id}
        entityName={previewEntity?.name}
      />
      <MetadataDiffDialog
        open={!!metadataDiff}
        onOpenChange={(v) => { if (!v) { setMetadataDiff(null); setPendingImportFile(null); } }}
        dryRun={metadataDiff}
        applying={applyingMetadata}
        onApply={async () => {
          if (!pendingImportFile || !activeModelId) return;
          setApplyingMetadata(true);
          try {
            const fd2 = new FormData();
            fd2.append('file', pendingImportFile);
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/semantic/models/${activeModelId}/metadata/import`, {
              method: 'POST', credentials: 'include', body: fd2,
            });
            if (!resp.ok) throw new Error(await resp.text());
            const res = await resp.json();
            toast.success(`Applied ${res.applied} change(s)${res.errors?.length ? `, ${res.errors.length} errors` : ''}`);
            await loadModel(activeModelId);
            // If any formula was touched, auto-recompute measures.
            const formulaChanges = (res.changes || []).filter(c => c.field === 'formula' || c.field === 'expression');
            if (formulaChanges.length > 0 || (res.applied > 0 && res.formula_changes)) {
              try {
                const rc = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/semantic/models/${activeModelId}/recompute-measures`, { method: 'POST', credentials: 'include' });
                if (rc.ok) {
                  const out = await rc.json();
                  if (out.measures_recomputed > 0) toast.success(`Recomputed ${out.measures_recomputed} measure(s) × ${out.rows_updated} row(s)`);
                }
              } catch (_) { /* silent — user can click Recompute manually */ }
            }
            setMetadataDiff(null);
            setPendingImportFile(null);
          } catch (err) {
            toast.error(err.message || 'Metadata import failed');
          } finally {
            setApplyingMetadata(false);
          }
        }}
      /><EditCalcMeasureDialog
        open={!!editCalcMeasure}
        onOpenChange={(v) => { if (!v) setEditCalcMeasure(null); }}
        editCalcMeasure={editCalcMeasure}
        setEditCalcMeasure={setEditCalcMeasure}
        editCalcName={editCalcName}
        setEditCalcName={setEditCalcName}
        editCalcFormula={editCalcFormula}
        setEditCalcFormula={setEditCalcFormula}
        editCalcDesc={editCalcDesc}
        setEditCalcDesc={setEditCalcDesc}
        editCalcEntity={editCalcEntity}
        setEditCalcEntity={setEditCalcEntity}
        editCalcCursorPos={editCalcCursorPos}
        setEditCalcCursorPos={setEditCalcCursorPos}
        entities={entities}
        measures={calcMeasures}
        modelId={activeModelId}
        onAiGenerate={() => setAiFormulaDialogOpen(true)}
        setCalcMeasures={setCalcMeasures}
        activeModelId={activeModelId}
      />

      <EditRelationshipDialog
        open={editRelOpen}
        onOpenChange={setEditRelOpen}
        editRel={editRel}
        setEditRel={setEditRel}
        entities={entities}
        setEntities={setEntities}
        setRelationships={setRelationships}
        model={model}
      />

      <Dialog open={importMeasuresOpen} onOpenChange={(v) => { if (!v) { setImportMeasuresOpen(false); setImportMeasuresPlan(null); setImportMeasuresFile(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="import-measures-dialog">
          <DialogHeader>
            <DialogTitle>Import measures from Excel</DialogTitle>
          </DialogHeader>
          {importMeasuresPlan && (() => {
            const s = importMeasuresPlan.summary || {};
            const plan = importMeasuresPlan.plan || [];
            const actionable = (s.update || 0) + (s.create || 0);
            const badge = (label, n, cls) => (n > 0 ? <span className={'text-[10px] px-2 py-0.5 rounded-full border ' + cls}>{n} {label}</span> : null);
            const rowCls = (a) => a === 'update' ? 'text-sky-300' : a === 'create' ? 'text-emerald-300' : a === 'invalid' ? 'text-rose-300' : a === 'skipped' ? 'text-amber-300' : 'text-muted-foreground';
            return (
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap" data-testid="import-measures-summary">
                  {badge('to update', s.update, 'border-sky-500/40 text-sky-300 bg-sky-500/10')}
                  {badge('to create', s.create, 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10')}
                  {badge('unchanged', s.unchanged, 'border-border/40 text-muted-foreground')}
                  {badge('skipped', s.skipped, 'border-amber-500/40 text-amber-300 bg-amber-500/10')}
                  {badge('invalid', s.invalid, 'border-rose-500/40 text-rose-300 bg-rose-500/10')}
                  {actionable === 0 && <span className="text-[11px] text-muted-foreground">No changes to apply.</span>}
                </div>
                <div className="max-h-80 overflow-auto rounded-lg border border-border/40">
                  <table className="w-full text-[11px]">
                    <thead className="sticky top-0 bg-background">
                      <tr className="text-left text-muted-foreground/70 border-b border-border/40">
                        <th className="px-2 py-1 font-medium">Name</th>
                        <th className="px-2 py-1 font-medium">Action</th>
                        <th className="px-2 py-1 font-medium">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.map((p, i) => (
                        <tr key={p.name + i} className="border-b border-border/10 align-top" data-testid={'import-row-' + p.name}>
                          <td className="px-2 py-1 font-mono text-foreground whitespace-nowrap">{p.name}</td>
                          <td className={'px-2 py-1 capitalize ' + rowCls(p.action)}>{p.action}</td>
                          <td className="px-2 py-1 font-mono text-muted-foreground break-words">
                            {p.action === 'invalid' ? <span className="text-rose-300">{p.error}</span>
                              : p.action === 'skipped' ? <span className="text-amber-300/80">{p.error}</span>
                              : p.action === 'create' ? <span>= {p.new_formula}{p.new_unit ? `  ·  ${p.new_unit}` : ''}</span>
                              : p.action === 'update' ? <span>{p.current_formula !== p.new_formula ? <><span className="line-through text-muted-foreground/70">{p.current_formula || '∅'}</span> → {p.new_formula || '∅'} </> : null}{p.current_unit !== p.new_unit ? <span className="ml-1">unit: {p.current_unit || '∅'} → {p.new_unit || '∅'}</span> : null}</span>
                              : <span className="text-muted-foreground/70">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setImportMeasuresOpen(false); setImportMeasuresPlan(null); setImportMeasuresFile(null); }} data-testid="import-measures-cancel">Cancel</Button>
                  <Button size="sm" onClick={applyMeasuresImport} disabled={importMeasuresBusy || actionable === 0} data-testid="import-measures-confirm">
                    {importMeasuresBusy ? 'Applying…' : `Apply ${actionable} change${actionable === 1 ? '' : 's'}`}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>



      {/* Browse Data Dialog */}
      <SemBrowseDialog
        open={semBrowseOpen}
        onOpenChange={setSemBrowseOpen}
        semBrowseField={semBrowseField}
        semBrowseData={semBrowseData}
        semBrowseLoading={semBrowseLoading}
      />
    </div>
    </TooltipProvider>
    <SmUnsavedDialog />
    <SaveWithVersionDialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen} onSave={handleUpdateModel} saving={saving} title="Save Semantic Model" />

    {/* AI Formula Generator Dialog */}
    <AiFormulaDialog
      open={aiFormulaDialogOpen}
      onOpenChange={setAiFormulaDialogOpen}
      modelId={activeModelId}
      allFields={(entities || []).flatMap(e => (e.attributes || []).map(a => ({ ...a, entityName: e.name })))}
      textFields={(entities || []).flatMap(e => (e.attributes || []).filter(a => ['string', 'text', 'varchar', 'character'].includes((a.data_type || '').toLowerCase())))}
      dateFields={(entities || []).flatMap(e => (e.attributes || []).filter(a => (a.data_type || '').toLowerCase().includes('date')))}
      onApproved={() => { if (activeModelId) loadModel(activeModelId); }}
    />
    <ConfirmDialogElement />
    </>
  );
}
